import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { db, appId } from './firebase-config';
import { 
    collection, 
    onSnapshot, 
    doc, 
    getDoc,
    updateDoc,
    addDoc,
    deleteDoc,
    getDocs,
    where,
    query,
    writeBatch,
    orderBy,
    limit
} from 'firebase/firestore';
import * as Tone from 'tone';
import {
    resolveStockProductsCollectionPath,
    resolveStockProductDocPath,
    resolveStockTasksCollectionPath,
    resolveStockTaskDocPath,
    resolveStockControlsCollectionPath,
    legacyStockProductsCollectionPath,
    legacyStockProductDocPath,
    legacyStockTasksCollectionPath,
    legacyStockTaskDocPath,
    legacyStockProductControlsCollectionPath,
    resolveStockControllersCollectionPath,
    resolveStockControllerDocPath,
    resolveStockSheetsCollectionPath,
    resolveStockSheetDocPath,
    resolveStockSheetLinesCollectionPath,
    resolveStockPurchasesCollectionPath,
    resolveStockPurchaseDocPath,
    resolveStockPurchaseLinesCollectionPath,
    resolveStockOutboundOrdersCollectionPath,
    resolveStockMovementsCollectionPath,
    resolveContactsCollectionPath,
    resolveRouteDocPath
} from './core/company/companyPaths';
import ProductsPanel from './ProductsPanel';
import StatisticsView from './StatisticsView';
import VirtualAssistant from './VirtualAssistant';
import { StockCountModal, HistoryListModal, ProductHistoryModal, ProductObservationHistoryModal } from './Modals';
import useLongPress from './useLongPress';
import StockControllersPanel from './modules/stock/components/StockControllersPanel';
import StockSheetsPanel from './modules/stock/components/StockSheetsPanel';
import StockOperatorSheetView from './modules/stock/components/StockOperatorSheetView';
import StockPurchasesPanel from './modules/stock/components/StockPurchasesPanel';
import StockOutboundOrdersPanel from './modules/stock/components/StockOutboundOrdersPanel';
import {
    uploadCompressedProfilePhoto
} from './core/media/profilePhotoUpload';
import StockMovementsPanel from './modules/stock/components/StockMovementsPanel';
import { buildDefaultStockBuckets, buildPurchaseHeaderSummary, getPurchaseLineProgress } from './modules/stock/stockFlowUtils';
import { getStockNavButtonClass, getStockNavIconWrapClass } from './modules/stock/stockUi';
import { geocodeAddress } from './modules/logistics/components/LogisticsUtils';

// --- Componentes de UI Reutilizables ---
export const Icon = ({ path, className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

const createOutboundOrderLineDraft = () => ({
    id: `outbound-line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    productId: '',
    quantity: '',
    saleUnitPrice: '',
});

const buildDefaultOutboundOrderForm = () => ({
    orderLabel: '',
    selectedContactId: '',
    destinationName: '',
    destinationAddress: '',
    destinationApartment: '',
    destinationLocality: '',
    destinationPartido: '',
    destinationProvince: 'CABA',
    destinationPostalCode: '',
    destinationLatlng: null,
    operationType: 'sale',
    status: 'ready_for_delivery',
    reference: '',
    notes: '',
    currency: 'ARS',
    lines: [createOutboundOrderLineDraft()],
});

const createPurchaseLineDraft = () => ({
    id: null,
    productId: '',
    quantity: '',
    unitCost: '',
    quickProduct: null,
});

const buildDefaultPurchaseForm = () => ({
    supplierName: '',
    selectedSupplierId: '',
    supplierAddress: '',
    supplierApartment: '',
    supplierLocality: '',
    supplierPartido: '',
    supplierProvince: 'CABA',
    supplierPostalCode: '',
    supplierLatlng: null,
    purchaseDate: new Date().toISOString().slice(0, 10),
    sourceType: 'manual',
    sourceReference: '',
    notes: '',
    currency: 'ARS',
    lines: [createPurchaseLineDraft()],
});

const buildEmptyCategoryControlDraft = () => ({
    categoryLabel: '',
    frequency: 'one_time',
    firstDate: new Date().toISOString().slice(0, 10),
    notes: '',
    products: [],
});

const normalizeStockSheetStatus = (sheet = {}) => {
    const explicitStatus = String(sheet?.status || '').trim().toLowerCase();
    if (['waiting_assignment', 'assigned', 'in_progress', 'completed', 'reviewed', 'cancelled'].includes(explicitStatus)) {
        return explicitStatus;
    }

    const hasCompletedAt = Boolean(sheet?.completedAt);
    const hasStartedAt = Boolean(sheet?.startedAt);
    const hasAssignedController = Boolean(
        String(sheet?.assignedControllerId || '').trim()
        || String(sheet?.assignedControllerDisplayNameSnapshot || '').trim()
    );

    if (hasCompletedAt) return 'completed';
    if (hasStartedAt) return 'in_progress';
    if (hasAssignedController) return 'assigned';
    return 'waiting_assignment';
};

const normalizeStockSheetRecord = (sheet = {}) => {
    const normalizedStatus = normalizeStockSheetStatus(sheet);
    const createdAtDate = sheet?.createdAt?.toDate ? sheet.createdAt.toDate() : null;
    return {
        ...sheet,
        status: normalizedStatus,
        sheetDate: String(sheet?.sheetDate || '').trim() || (createdAtDate ? createdAtDate.toISOString().slice(0, 10) : ''),
    };
};

const buildPurchaseBatchLabel = (purchaseNumber = '', lineOrder = 0, fallbackLineId = '') => {
    const normalizedPurchase = String(purchaseNumber || '').trim();
    const normalizedOrder = Number(lineOrder || 0);
    if (normalizedPurchase && normalizedOrder > 0) {
        return `Lote ${normalizedPurchase.slice(0, 6).toUpperCase()} · línea ${normalizedOrder}`;
    }
    if (normalizedPurchase) return `Lote ${normalizedPurchase.slice(0, 6).toUpperCase()}`;
    if (fallbackLineId) return `Lote ${String(fallbackLineId).slice(0, 6).toUpperCase()}`;
    return 'Lote sin referencia visible';
};

const buildProductVariantSnapshot = (product = {}) =>
    [
        String(product?.model || '').trim(),
        String(product?.brand || '').trim(),
        String(product?.featureLabel || '').trim(),
        String(product?.inches || '').trim() ? `${String(product?.inches || '').trim()}"` : '',
    ].filter(Boolean).join(' · ');

const buildSheetLineVariantSnapshot = (line = {}, product = {}) =>
    [
        String(line?.productModelSnapshot || product?.model || '').trim(),
        String(line?.productBrandSnapshot || product?.brand || '').trim(),
        String(line?.productFeatureSnapshot || product?.featureLabel || '').trim(),
        String(product?.inches || '').trim() ? `${String(product?.inches || '').trim()}"` : '',
    ].filter(Boolean).join(' · ');

const buildMovementProductSnapshotFields = ({ product = {}, line = {} } = {}) => {
    const resolvedCategory =
        String(
            line?.productCategorySnapshot ||
            line?.categorySnapshot ||
            product?.category ||
            ''
        ).trim();
    const resolvedModel =
        String(
            line?.productModelSnapshot ||
            product?.model ||
            ''
        ).trim();
    const resolvedBrand =
        String(
            line?.productBrandSnapshot ||
            product?.brand ||
            ''
        ).trim();
    const resolvedFeature =
        String(
            line?.productFeatureSnapshot ||
            product?.featureLabel ||
            ''
        ).trim();
    const resolvedVariant =
        String(
            line?.productVariantSnapshot ||
            buildSheetLineVariantSnapshot(line, product)
        ).trim();
    const resolvedSku =
        String(
            line?.productSkuSnapshot ||
            line?.skuSnapshot ||
            product?.sku ||
            ''
        ).trim();

    return {
        productCategorySnapshot: resolvedCategory,
        categorySnapshot: resolvedCategory,
        productModelSnapshot: resolvedModel,
        productBrandSnapshot: resolvedBrand,
        productFeatureSnapshot: resolvedFeature,
        productVariantSnapshot: resolvedVariant,
        productSkuSnapshot: resolvedSku,
        skuSnapshot: resolvedSku,
    };
};

const slugifyControllerSegment = (value = '') =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'controlador';

const generateControllerPin = () => String(Math.floor(1000 + Math.random() * 9000));
const STOCK_CONTROLLER_PUBLIC_ORIGIN = 'https://app.g-centra.com.ar';
const buildControllerAccessPath = (companyKey = '', controllerKey = '') =>
    `/empresa/${encodeURIComponent(companyKey)}/control/${encodeURIComponent(controllerKey)}`;
const buildControllerAccessUrl = (companyKey = '', controllerKey = '') =>
    `${STOCK_CONTROLLER_PUBLIC_ORIGIN}${buildControllerAccessPath(companyKey, controllerKey)}`;

const buildDefaultControllerForm = () => ({
    firstName: '',
    lastName: '',
    phone: '',
    pin: generateControllerPin(),
    active: true,
    photoURL: '',
    photoStoragePath: '',
});

const normalizeSheetPreviewLine = (line = {}, product = null) => {
    const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity || 0);
    const countedQuantity = line.countedQuantity == null ? null : Number(line.countedQuantity || 0);
    const differenceValue = line.differenceQuantity == null
        ? (countedQuantity == null || expectedQuantity == null ? null : countedQuantity - expectedQuantity)
        : Number(line.differenceQuantity || 0);
    const variantLabel = buildSheetLineVariantSnapshot(line, product || {});
    const categoryLabel = String(line.productCategorySnapshot || product?.category || '').trim();
    return {
        id: line.id,
        productId: line.productId,
        product: product || { id: line.productId, name: line.productNameSnapshot || 'Producto' },
        task: null,
        status: String(line.status || (countedQuantity == null ? 'pending' : differenceValue === 0 ? 'checked_ok' : 'checked_diff')).trim() || 'pending',
        addedDuringControl: String(line.status || '') === 'added_during_control',
        productName: line.productNameSnapshot || product?.name || 'Producto',
        codeLabel: variantLabel || (line.productSkuSnapshot ? `SKU ${line.productSkuSnapshot}` : 'Sin variante visible'),
        categoryLabel,
        expectedQuantity,
        expectedLabel: expectedQuantity == null ? 'Sin base' : `${expectedQuantity}`,
        countedQuantity,
        countedLabel: countedQuantity == null ? '-' : `${countedQuantity}`,
        differenceValue,
        readingLabel: countedQuantity == null ? 'Todavía sin conteo cargado' : differenceValue === 0 ? 'Coincide con el sistema' : 'Hay diferencia para revisar',
        noteLabel: String(line.observation || line.note || '').trim() || 'Sin observación',
        canStartControl: Boolean(line.productId),
    };
};

const CATEGORY_CONTROL_FREQUENCY_OPTIONS = [
    { value: 'one_time', label: 'Solo hoy' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'bi-weekly', label: 'Quincenal' },
    { value: 'monthly', label: 'Mensual' },
];

const getCategoryControlFrequencyLabel = (value = 'one_time') =>
    CATEGORY_CONTROL_FREQUENCY_OPTIONS.find((option) => option.value === value)?.label || 'Solo hoy';

const getCategoryControlFrequencyStep = (value = 'one_time') => {
    switch (value) {
        case 'weekly':
            return 7;
        case 'bi-weekly':
            return 14;
        case 'monthly':
            return 28;
        default:
            return 0;
    }
};

const normalizeCategoryControlKey = (value = '') =>
    String(value || '').trim().toLowerCase();

const buildOutboundOrderFormFromOrder = (order = {}) => ({
    orderLabel: String(order.orderLabel || '').trim(),
    selectedContactId: order.contactId || '',
    destinationName: String(order.destinationNameSnapshot || order.contactNameSnapshot || '').trim(),
    destinationAddress: String(order.destinationSnapshot?.address || '').trim(),
    destinationApartment: String(order.destinationSnapshot?.apartment || '').trim(),
    destinationLocality: String(order.destinationSnapshot?.locality || '').trim(),
    destinationPartido: String(order.destinationSnapshot?.partido || '').trim(),
    destinationProvince: String(order.destinationSnapshot?.province || 'CABA').trim() || 'CABA',
    destinationPostalCode: String(order.destinationSnapshot?.postalCode || '').trim(),
    destinationLatlng: order.destinationGeoSnapshot || null,
    operationType: String(order.operationType || 'sale').trim().toLowerCase() || 'sale',
    status: String(order.status || 'ready_for_delivery').trim() || 'ready_for_delivery',
    reference: String(order.reference || '').trim(),
    notes: String(order.note || '').trim(),
    currency: order.currency || 'ARS',
    lines: Array.isArray(order.lines) && order.lines.length > 0
        ? order.lines
            .filter((line) => line.status !== 'cancelled')
            .map((line) => ({
                id: line.id || createOutboundOrderLineDraft().id,
                productId: line.productId || '',
                quantity: String(line.pendingDeliveryQuantity ?? line.quantity ?? ''),
                saleUnitPrice: line.saleUnitPrice != null ? String(line.saleUnitPrice) : '',
            }))
        : [createOutboundOrderLineDraft()],
});

const getProductReadyStockQty = (product) => {
    const physicalQty = Number(product?.stockBuckets?.physicalQty || 0);
    const pendingOutboundQty = Number(product?.stockBuckets?.pendingOutboundQty || 0);
    return Math.max(0, physicalQty - pendingOutboundQty);
};

const isPurchaseLineEditable = (line = {}) => (
    Number(line.assignedQuantity || 0) <= 0
    && Number(line.pickedUpQuantity || 0) <= 0
    && Number(line.receivedQuantity || 0) <= 0
    && Number(line.cancelledQuantity || 0) <= 0
    && String(line.status || 'pending_pickup').trim().toLowerCase() === 'pending_pickup'
);

// --- Panel de Informe Ejecutivo ---
const ExecutiveSummaryPanel = ({ stats, onShowStats, onShowAssistant }) => {
    const {
        complianceRate,
        topDifferences,
        upcomingTasks,
        neglectedProducts
    } = stats;

    const complianceColor = complianceRate >= 90 ? 'text-green-500' : complianceRate >= 70 ? 'text-yellow-500' : 'text-red-500';

    return (
        <div className="p-4 bg-white rounded-lg shadow-inner border border-stone-200 space-y-6">
            <div className="flex justify-between items-center">
                <h4 className="text-2xl font-bold text-stone-800">Informe</h4>
                <button 
                    onClick={onShowAssistant}
                    className="bg-purple-600 text-white font-bold py-2 px-4 rounded-lg shadow hover:bg-purple-700 flex items-center gap-2 transition-all"
                >
                    <Icon path="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.624l.211.842.211-.842a.875.875 0 011.302-.513l.635.528-.528-.635a.875.875 0 01.513-1.302l.842-.211-.842-.211a.875.875 0 01-.513-1.302l.528-.635-.635.528a.875.875 0 01-1.302-.513l-.211-.842-.211.842a.875.875 0 01-1.302.513l-.635-.528.528.635a.875.875 0 01-.513 1.302l-.842.211.842.211a.875.875 0 01.513 1.302l-.528.635.635.528a.875.875 0 011.302.513z" className="w-5 h-5"/>
                    GIA Stock
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <p className="text-sm font-semibold text-slate-500">Tasa de Cumplimiento (Semana)</p>
                    <p className={`text-5xl font-bold ${complianceColor}`}>{complianceRate.toFixed(0)}%</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg col-span-1 md:col-span-2 lg:col-span-2">
                    <p className="text-sm font-semibold text-slate-500 mb-2">Próximos Controles Programados</p>
                    <ul className="space-y-1">
                        {upcomingTasks.length > 0 ? upcomingTasks.map(task => (
                            <li key={task.id} className="text-sm flex justify-between">
                                <span>{task.productName}</span>
                                <span className="font-semibold">{new Date(task.date + 'T00:00:00').toLocaleDateString('es-AR', {weekday: 'short', day: 'numeric'})}</span>
                            </li>
                        )) : <p className="text-sm text-slate-400">No hay tareas próximas.</p>}
                    </ul>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-red-700 mb-2">Productos Críticos (Mayor Diferencia)</p>
                    <ul className="space-y-1">
                        {topDifferences.length > 0 ? topDifferences.map(item => (
                            <li key={item.name} className="text-sm flex justify-between">
                                <span>{item.name}</span>
                                <span className="font-bold text-red-600">{item.diferencia}</span>
                            </li>
                        )) : <p className="text-sm text-red-400">Sin diferencias significativas.</p>}
                    </ul>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-700 mb-2">Productos a Vigilar (Sin Control Reciente)</p>
                     <ul className="space-y-1">
                        {neglectedProducts.length > 0 ? neglectedProducts.map(product => (
                            <li key={product.id} className="text-sm">
                                {product.name}
                            </li>
                        )) : <p className="text-sm text-yellow-400">Todos los productos controlados.</p>}
                    </ul>
                </div>
            </div>
            
            <div className="text-center">
                <button onClick={onShowStats} className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors">
                    Ver Estadísticas
                </button>
            </div>
        </div>
    );
};


// --- Componente Principal del Módulo de Stock ---
const STOCK_GIA_SECTION_MAP = {
    planner: {
        subsectionLabel: 'Programación',
        focusLabel: 'Agenda de controles',
    },
    products: {
        subsectionLabel: 'Productos',
        focusLabel: 'Base de productos',
    },
    purchases: {
        subsectionLabel: 'Compras',
        focusLabel: 'Ingreso de mercadería',
    },
    'outbound-orders': {
        subsectionLabel: 'Salidas',
        focusLabel: 'Pedidos y preparación',
    },
    movements: {
        subsectionLabel: 'Movimientos',
        focusLabel: 'Historial de movimientos',
    },
    controllers: {
        subsectionLabel: 'Controladores',
        focusLabel: 'Responsables de control',
    },
    sheets: {
        subsectionLabel: 'Hojas de control',
        focusLabel: 'Controles en curso',
    },
    'executive-summary': {
        subsectionLabel: 'Informe ejecutivo',
        focusLabel: 'Resumen del depósito',
    },
    stats: {
        subsectionLabel: 'Estadísticas',
        focusLabel: 'Lectura de actividad',
    },
    assistant: {
        subsectionLabel: 'GIA Stock',
        focusLabel: 'Ayuda del módulo',
    },
};

const STOCK_TUTORIAL_STORAGE_KEY = 'gcentra_tutorial_stock_seen';
const STOCK_TUTORIAL_STEPS = [
    {
        target: '#stock-module-header',
        title: 'Centro de stock',
        content: 'Desde esta cabecera leés el estado general del depósito y entrás al recorrido del módulo.',
        placement: 'bottom',
        disableBeacon: true,
    },
    {
        target: '#stock-nav-panel',
        title: 'Frentes del depósito',
        content: 'Esta navegación separa compras, salidas, movimientos, controles, estadísticas y ayuda para que la operación no se mezcle.',
        placement: 'right',
    },
    {
        target: '#stock-main-panel',
        title: 'Panel activo',
        content: 'Acá cambia el contenido según la sección elegida, sin salir del mismo módulo.',
        placement: 'left',
    },
    {
        target: '#stock-nav-assistant',
        title: 'Ayuda del módulo',
        content: 'Si necesitás reubicarte rápido dentro de Stock, desde acá abrís la ayuda contextual y el recorrido sugerido.',
        placement: 'left',
    },
];

const StockControlApp = ({ userId, showMessage, onBackToMenu, addStockNotification, companyId = 'default', onGiaContextChange = null, onStartTutorial = null, tutorialSeen: tutorialSeenProp = false, tutorialActive = false, tutorialFocusTarget = null }) => {
    const [products, setProducts] = useState([]);
    const [scheduledTasks, setScheduledTasks] = useState([]);
    const [allHistory, setAllHistory] = useState([]);
    const [stockControllers, setStockControllers] = useState([]);
    const [stockSheets, setStockSheets] = useState([]);
    const [stockPurchases, setStockPurchases] = useState([]);
    const [stockPurchaseLines, setStockPurchaseLines] = useState([]);
    const [stockContacts, setStockContacts] = useState([]);
    const [sharedContacts, setSharedContacts] = useState([]);
    const [stockOutboundOrders, setStockOutboundOrders] = useState([]);
    const [stockMovements, setStockMovements] = useState([]);
    const [editingPurchaseId, setEditingPurchaseId] = useState(null);
    const [editingOutboundOrderId, setEditingOutboundOrderId] = useState(null);
    const [newControllerForm, setNewControllerForm] = useState(buildDefaultControllerForm());
    const [editingControllerId, setEditingControllerId] = useState(null);
    const [newSheetForm, setNewSheetForm] = useState({ title: '', sourceType: 'manual', sheetDate: new Date().toISOString().slice(0, 10), notes: '', includeDayTasks: true });
    const [newPurchaseForm, setNewPurchaseForm] = useState(buildDefaultPurchaseForm());
    const [newOutboundOrderForm, setNewOutboundOrderForm] = useState(() => buildDefaultOutboundOrderForm());
    const [showCategoryControlModal, setShowCategoryControlModal] = useState(false);
    const [categoryControlDraft, setCategoryControlDraft] = useState(buildEmptyCategoryControlDraft());
    const [showProgramEditModal, setShowProgramEditModal] = useState(false);
    const [editingProgramDraft, setEditingProgramDraft] = useState({
        sheetId: '',
        title: '',
        categoryLabel: '',
        frequency: 'one_time',
        firstDate: new Date().toISOString().slice(0, 10),
        programStatus: 'scheduled',
        notes: '',
    });
    const [showCategoryHistoryModal, setShowCategoryHistoryModal] = useState(false);
    const [activeCategoryHistory, setActiveCategoryHistory] = useState({
        categoryLabel: '',
        entries: [],
    });
    const [activeSheetPreview, setActiveSheetPreview] = useState(null);
    const [activeSheetPreviewLines, setActiveSheetPreviewLines] = useState([]);
    const [sheetPreviewMode, setSheetPreviewMode] = useState('panel');
    const [sheetPreviewSearch, setSheetPreviewSearch] = useState('');
    const [sheetPreviewFilter, setSheetPreviewFilter] = useState('all');
    const [sheetPreviewSaving, setSheetPreviewSaving] = useState(false);
    const [sheetPreviewMeta, setSheetPreviewMeta] = useState({
        generalObservation: '',
        generalTaggedLineIds: [],
    });
    const [newProductName, setNewProductName] = useState('');
    const [newProductFrequency, setNewProductFrequency] = useState('manual');
    const [newProductDay, setNewProductDay] = useState('1');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
    const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
    const [selectedDayTasks, setSelectedDayTasks] = useState([]);
    const [selectedDayDate, setSelectedDayDate] = useState(null);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [activePanel, setActivePanel] = useState('planner');
    const [tutorialSeen, setTutorialSeen] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem(STOCK_TUTORIAL_STORAGE_KEY) === 'true';
    });
    const [tutorialRun, setTutorialRun] = useState(false);
    const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
    const [draggedProduct, setDraggedProduct] = useState(null);
    const [selectedForMobile, setSelectedForMobile] = useState(null);
    const [dragOverDate, setDragOverDate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showStockCountModal, setShowStockCountModal] = useState(false);
    const [activeControlProduct, setActiveControlProduct] = useState(null);
    const [activeCalendarTask, setActiveCalendarTask] = useState(null);
    const tutorialUtteranceRef = useRef(null);
    const tutorialSpeechTokenRef = useRef(0);
    const [showHistoryListModal, setShowHistoryListModal] = useState(false);
    const [activeHistoryProduct, setActiveHistoryProduct] = useState(null);
    const [showAiSummaryModal, setShowAiSummaryModal] = useState(false);
    const [aiSummaryContent, setAiSummaryContent] = useState({ title: '', content: '' });
    const [controlStreak, setControlStreak] = useState(0);
    const [showMonthlyCalendar, setShowMonthlyCalendar] = useState(false);
    const [mobileVisibleDay, setMobileVisibleDay] = useState(new Date());
    const [weekAnimation, setWeekAnimation] = useState('');
    const [pulsingDate, setPulsingDate] = useState(null);
    const [showSheetAdjustmentModal, setShowSheetAdjustmentModal] = useState(false);
    const [sheetAdjustmentDraft, setSheetAdjustmentDraft] = useState({
        sheetId: '',
        sheetTitle: '',
        categoryLabel: '',
        reason: '',
        note: '',
        selectedLineIds: [],
        lines: [],
    });
    const [showProductHistoryModal, setShowProductHistoryModal] = useState(false);
    const [productForHistory, setProductForHistory] = useState(null);
    const [stockNavExpandedInMovements, setStockNavExpandedInMovements] = useState(false);
    const [movementsHeaderCondensed, setMovementsHeaderCondensed] = useState(false);
    const synths = useRef(null);
    const stalePurchaseReconciliationRef = useRef(new Set());
    const weeklyPlannerRef = useRef(null);
    const stockScrollContainerRef = useRef(null);
    const stockHeaderRef = useRef(null);
    const stockSidebarSlotRef = useRef(null);
    const effectiveTutorialSeen = onStartTutorial ? tutorialSeenProp : tutorialSeen;
    const effectiveTutorialRunning = onStartTutorial ? tutorialActive : tutorialRun;
    const resolvedCompanyId = companyId || 'default';
    const companyProductsPath = useMemo(() => resolveStockProductsCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyTasksPath = useMemo(() => resolveStockTasksCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyControlsPath = useMemo(() => resolveStockControlsCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyControllersPath = useMemo(() => resolveStockControllersCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companySheetsPath = useMemo(() => resolveStockSheetsCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companySheetLinesPath = useMemo(() => resolveStockSheetLinesCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyPurchasesPath = useMemo(() => resolveStockPurchasesCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyPurchaseLinesPath = useMemo(() => resolveStockPurchaseLinesCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyOutboundOrdersPath = useMemo(() => resolveStockOutboundOrdersCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const companyMovementsPath = useMemo(() => resolveStockMovementsCollectionPath(resolvedCompanyId), [resolvedCompanyId]);

    useEffect(() => {
        if (!tutorialActive || !tutorialFocusTarget || typeof window === 'undefined') return undefined;
        const timeoutId = window.setTimeout(() => {
            const targetNode = document.querySelector(tutorialFocusTarget);
            targetNode?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        }, 180);
        return () => window.clearTimeout(timeoutId);
    }, [tutorialActive, tutorialFocusTarget]);
    const companyContactsPath = useMemo(() => resolveContactsCollectionPath(resolvedCompanyId), [resolvedCompanyId]);
    const legacyProductsPath = useMemo(() => legacyStockProductsCollectionPath(appId), []);
    const legacyTasksPath = useMemo(() => legacyStockTasksCollectionPath(appId), []);

    const resolveProductDocPath = useCallback((product = {}) => {
        if (product?._source === 'legacy') {
            return legacyStockProductDocPath(appId, product.id);
        }
        return resolveStockProductDocPath(resolvedCompanyId, product.id);
    }, [resolvedCompanyId]);

    const resolveTaskDocPath = useCallback((task = {}) => {
        if (task?._source === 'legacy') {
            return legacyStockTaskDocPath(appId, task.id);
        }
        return resolveStockTaskDocPath(resolvedCompanyId, task.id);
    }, [resolvedCompanyId]);

    const resolveControlsCollectionPath = useCallback((product = {}) => {
        if (product?._source === 'legacy') {
            return legacyStockProductControlsCollectionPath(appId, product.id);
        }
        return companyControlsPath;
    }, [companyControlsPath]);

    const formatDate = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getTimestampMs = (value) => {
        if (!value) return 0;
        if (typeof value?.toDate === 'function') {
            try {
                return value.toDate().getTime();
            } catch {
                return 0;
            }
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };

    const openPlannerDayDetails = (date) => {
        const nextDate = new Date(date);
        setCurrentDate(new Date(nextDate));
        setSelectedDayTasks(plannerEntries.filter((entry) => entry.date === formatDate(nextDate)));
        setSelectedDayDate(nextDate);
        setShowDayDetailsModal(true);
    };

    const handleDateSelect = useCallback((date, options = {}) => {
        const nextDate = new Date(date);
        const shouldOpenDetails = Boolean(options?.openDetails);
        const newWeekStart = new Date(date);
        newWeekStart.setDate(newWeekStart.getDate() - (newWeekStart.getDay() === 0 ? 6 : newWeekStart.getDay() - 1));
        setCurrentDate(new Date(nextDate));
        setSelectedDayDate(new Date(nextDate));
        
        if (newWeekStart.getTime() === currentWeekStart.getTime()) {
            setPulsingDate(formatDate(date));
            setMobileVisibleDay(nextDate);
            if (shouldOpenDetails) {
                openPlannerDayDetails(nextDate);
            }
            setTimeout(() => setPulsingDate(null), 1500);
            return;
        }

        if (newWeekStart.getTime() > currentWeekStart.getTime()) {
            setWeekAnimation('animate-slide-out-left');
        } else if (newWeekStart.getTime() < currentWeekStart.getTime()) {
            setWeekAnimation('animate-slide-out-right');
        }

        setTimeout(() => {
            setCurrentWeekStart(newWeekStart);
            setMobileVisibleDay(nextDate);
            setWeekAnimation(newWeekStart.getTime() > currentWeekStart.getTime() ? 'animate-slide-in-right' : 'animate-slide-in-left');
            
            setTimeout(() => {
                setWeekAnimation('');
                setPulsingDate(formatDate(date));
                if (shouldOpenDetails) {
                    openPlannerDayDetails(nextDate);
                }
                setTimeout(() => setPulsingDate(null), 1500);
            }, 300);
        }, 300);
    }, [currentWeekStart, formatDate, openPlannerDayDetails]);

    const togglePanel = (panelName) => {
        setActivePanel(prevPanel => prevPanel === panelName ? null : panelName);
        if (panelName === 'movements') {
            setStockNavExpandedInMovements(false);
        }
    };

    useEffect(() => {
        if (activePanel !== 'movements') {
            setStockNavExpandedInMovements(false);
            setMovementsHeaderCondensed(false);
        }
    }, [activePanel]);

    useEffect(() => {
        if (typeof onGiaContextChange !== 'function') return undefined;
        const nextContext = STOCK_GIA_SECTION_MAP[activePanel] || {
            subsectionLabel: 'Stock y movimientos',
            focusLabel: 'Vista general del módulo',
        };
        onGiaContextChange(nextContext);
        return () => {
            onGiaContextChange({
                subsectionLabel: '',
                focusLabel: '',
            });
        };
    }, [activePanel, onGiaContextChange]);

    useEffect(() => {
        if (activePanel !== 'movements') return undefined;

        let frameOne = 0;
        let frameTwo = 0;
        const syncMovementsFocusLayout = () => {
            window.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('scroll'));
            if (stockScrollContainerRef.current?.dispatchEvent) {
                stockScrollContainerRef.current.dispatchEvent(new Event('scroll'));
            }
        };

        frameOne = requestAnimationFrame(() => {
            syncMovementsFocusLayout();
            frameTwo = requestAnimationFrame(syncMovementsFocusLayout);
        });

        return () => {
            if (frameOne) cancelAnimationFrame(frameOne);
            if (frameTwo) cancelAnimationFrame(frameTwo);
        };
    }, [activePanel, stockNavExpandedInMovements]);

    useEffect(() => {
        synths.current = {
            add: new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.1 } }).toDestination(),
            drop: new Tone.Synth({ oscillator: { type: 'sine' }, envelope: { attack: 0.01, decay: 0.1, sustain: 0.2, release: 0.2 } }).toDestination(),
            start: new Tone.Synth({ oscillator: { type: 'square' }, envelope: { attack: 0.01, decay: 0.3, sustain: 0.1, release: 0.1 } }).toDestination(),
            complete: new Tone.PolySynth(Tone.Synth, { oscillator: { type: 'fatsawtooth' }, envelope: { attack: 0.01, decay: 0.4, sustain: 0.2, release: 0.4 } }).toDestination(),
        };
    }, []);

    const playSound = async (type) => {
        if (Tone.context.state !== 'running') await Tone.start();
        if (!synths.current) return;
        switch (type) {
            case 'add': synths.current.add.triggerAttackRelease("E5", "8n"); break;
            case 'drop': synths.current.drop.triggerAttackRelease("G5", "8n"); break;
            case 'start': synths.current.start.triggerAttackRelease("A4", "16n"); break;
            case 'complete': synths.current.complete.triggerAttackRelease(["C5", "E5", "G5"], "8n"); break;
            default: break;
        }
    };
    
    const getWeekDays = useCallback((startOfWeek) => {
        const days = [];
        let currentDay = new Date(startOfWeek);
        currentDay.setDate(currentDay.getDate() - (currentDay.getDay() === 0 ? 6 : currentDay.getDay() - 1));
        for (let i = 0; i < 7; i++) { days.push(new Date(currentDay)); currentDay.setDate(currentDay.getDate() + 1); }
        return days;
    }, []);
    const weekDays = getWeekDays(currentWeekStart);
    
    useEffect(() => {
        if (!userId || !appId || !db) return;

        let companyTasks = [];
        let legacyTasks = [];
        let companyProducts = [];
        let legacyProducts = [];
        let companyTasksReady = false;
        let legacyTasksReady = false;
        let companyProductsReady = false;
        let legacyProductsReady = false;

        const syncTasks = () => {
            if (!companyTasksReady || !legacyTasksReady) return;
            const activeTasks = companyTasks.length > 0 ? companyTasks : legacyTasks;
            setScheduledTasks(activeTasks);
        };

        const syncProducts = () => {
            if (!companyProductsReady || !legacyProductsReady) return;
            const activeProducts = companyProducts.length > 0 ? companyProducts : legacyProducts;
            setProducts(activeProducts);
        };

        const tasksUnsubscribe = onSnapshot(
            collection(db, companyTasksPath),
            (snapshot) => {
                companyTasksReady = true;
                companyTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'company' }));
                syncTasks();
            },
            () => {
                companyTasksReady = true;
                companyTasks = [];
                syncTasks();
            }
        );

        const legacyTasksUnsubscribe = onSnapshot(
            collection(db, legacyTasksPath),
            (snapshot) => {
                legacyTasksReady = true;
                legacyTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'legacy' }));
                syncTasks();
            },
            () => {
                legacyTasksReady = true;
                legacyTasks = [];
                syncTasks();
            }
        );

        const productsUnsubscribe = onSnapshot(
            collection(db, companyProductsPath),
            (snapshot) => {
                companyProductsReady = true;
                companyProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'company' }));
                syncProducts();
            },
            () => {
                companyProductsReady = true;
                companyProducts = [];
                syncProducts();
            }
        );

        const legacyProductsUnsubscribe = onSnapshot(
            collection(db, legacyProductsPath),
            (snapshot) => {
                legacyProductsReady = true;
                legacyProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'legacy' }));
                syncProducts();
            },
            () => {
                legacyProductsReady = true;
                legacyProducts = [];
                syncProducts();
            }
        );

        return () => {
            productsUnsubscribe();
            legacyProductsUnsubscribe();
            tasksUnsubscribe();
            legacyTasksUnsubscribe();
        };
    }, [userId, companyProductsPath, companyTasksPath, legacyProductsPath, legacyTasksPath]);


    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyControllersPath),
            (snapshot) => {
                const nextControllers = snapshot.docs
                    .map((controllerDoc) => {
                        const data = controllerDoc.data() || {};
                        const fallbackAccessSlug = String(data.accessSlug || controllerDoc.id).trim() || controllerDoc.id;
                        return {
                            id: controllerDoc.id,
                            ...data,
                            accessSlug: fallbackAccessSlug,
                            accessPath: data.accessPath || buildControllerAccessPath(resolvedCompanyId, fallbackAccessSlug),
                            accessUrl: data.accessUrl || buildControllerAccessUrl(resolvedCompanyId, fallbackAccessSlug),
                        };
                    })
                    .sort((a, b) => {
                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                        return bTime - aTime;
                    });
                setStockControllers(nextControllers);
            },
            () => setStockControllers([])
        );
        return () => unsubscribe();
    }, [companyControllersPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companySheetsPath),
            (snapshot) => {
                const nextSheets = snapshot.docs
                    .map((sheetDoc) => normalizeStockSheetRecord({ id: sheetDoc.id, ...sheetDoc.data() }))
                    .sort((a, b) => {
                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                        return bTime - aTime;
                    });
                setStockSheets(nextSheets);
            },
            () => setStockSheets([])
        );
        return () => unsubscribe();
    }, [companySheetsPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyContactsPath),
            (snapshot) => {
                const rawContacts = snapshot.docs
                    .map((contactDoc) => ({ id: contactDoc.id, ...contactDoc.data() }))
                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es'));
                const nextContacts = rawContacts.filter((contact) => isSupplierContact(contact));
                setSharedContacts(rawContacts);
                setStockContacts(nextContacts);
            },
            () => {
                setSharedContacts([]);
                setStockContacts([]);
            }
        );
        return () => unsubscribe();
    }, [companyContactsPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyPurchasesPath),
            (snapshot) => {
                const nextPurchases = snapshot.docs
                    .map((purchaseDoc) => ({ id: purchaseDoc.id, ...purchaseDoc.data() }))
                    .sort((a, b) => {
                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                        return bTime - aTime;
                    });
                setStockPurchases(nextPurchases);
            },
            () => setStockPurchases([])
        );
        return () => unsubscribe();
    }, [companyPurchasesPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyPurchaseLinesPath),
            (snapshot) => {
                const nextLines = snapshot.docs
                    .map((lineDoc) => ({ id: lineDoc.id, ...lineDoc.data() }))
                    .sort((a, b) => {
                        const orderA = Number(a.lineOrder || 0);
                        const orderB = Number(b.lineOrder || 0);
                        if (orderA !== orderB) return orderA - orderB;
                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                        return aTime - bTime;
                    });
                setStockPurchaseLines(nextLines);
            },
            () => setStockPurchaseLines([])
        );
        return () => unsubscribe();
    }, [companyPurchaseLinesPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId || stockPurchaseLines.length === 0) return;

        const staleCandidates = stockPurchaseLines.filter((line) => {
            const routeId = String(line?.lastRouteId || '').trim();
            if (!routeId) return false;
            const progress = getPurchaseLineProgress(line);
            return (
                progress.assignedQuantity > 0 &&
                progress.pickedUpQuantity <= 0 &&
                progress.receivedQuantity <= 0
            );
        });

        if (!staleCandidates.length) return;

        let cancelled = false;

        const reconcileStaleAssignments = async () => {
            const routeStatusCache = new Map();
            const linesToRepair = [];

            for (const line of staleCandidates) {
                const lineId = String(line.id || '').trim();
                if (!lineId || stalePurchaseReconciliationRef.current.has(lineId)) continue;

                const routeId = String(line?.lastRouteId || '').trim();
                if (!routeStatusCache.has(routeId)) {
                    try {
                        const routeSnap = await getDoc(doc(db, resolveRouteDocPath(resolvedCompanyId, routeId)));
                        routeStatusCache.set(
                            routeId,
                            routeSnap.exists() ? String(routeSnap.data()?.status || '').trim().toLowerCase() : ''
                        );
                    } catch (error) {
                        console.error('No pude revisar el estado de una ruta para reconciliar una compra pendiente:', error);
                        routeStatusCache.set(routeId, '');
                    }
                }

                const routeStatus = routeStatusCache.get(routeId);
                if (!['completed', 'cancelled'].includes(routeStatus)) continue;

                linesToRepair.push(line);
                stalePurchaseReconciliationRef.current.add(lineId);
            }

            if (!linesToRepair.length || cancelled) return;

            const batch = writeBatch(db);
            const repairedPurchaseIds = new Set();
            const now = new Date();

            linesToRepair.forEach((line) => {
                const lineId = String(line.id || '').trim();
                const progress = getPurchaseLineProgress(line);
                const restoredPendingPickupQuantity = Math.max(
                    0,
                    progress.orderedNet - progress.receivedQuantity - progress.pickedUpQuantity
                );

                batch.update(doc(db, `${companyPurchaseLinesPath}/${lineId}`), {
                    assignedQuantity: 0,
                    pendingPickupQuantity: restoredPendingPickupQuantity,
                    status: restoredPendingPickupQuantity > 0 ? 'pending_pickup' : line.status || 'pending_pickup',
                    lastRouteId: null,
                    lastRouteCodeSnapshot: null,
                    updatedAt: now
                });

                repairedPurchaseIds.add(String(line.purchaseId || ''));
            });

            repairedPurchaseIds.forEach((purchaseId) => {
                const nextLines = stockPurchaseLines
                    .filter((line) => String(line.purchaseId || '') === purchaseId)
                    .map((line) => {
                        const isRepaired = linesToRepair.some((candidate) => String(candidate.id) === String(line.id));
                        if (!isRepaired) return line;
                        const progress = getPurchaseLineProgress(line);
                        return {
                            ...line,
                            assignedQuantity: 0,
                            pendingPickupQuantity: Math.max(
                                0,
                                progress.orderedNet - progress.receivedQuantity - progress.pickedUpQuantity
                            ),
                            status: 'pending_pickup',
                            lastRouteId: null,
                            lastRouteCodeSnapshot: null,
                        };
                    });

                const purchase = stockPurchases.find((item) => String(item.id || '') === purchaseId);
                if (!purchase) return;
                const summary = buildPurchaseHeaderSummary(purchase, nextLines);
                batch.update(doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, purchaseId)), {
                    status: summary.status,
                    pendingLinesCount: summary.pendingLinesCount,
                    completedLinesCount: summary.completedLinesCount,
                    closedAt: summary.closedAt || null,
                    updatedAt: now
                });
            });

            try {
                await batch.commit();
            } catch (error) {
                linesToRepair.forEach((line) => {
                    stalePurchaseReconciliationRef.current.delete(String(line.id || ''));
                });
                console.error('No pude reconciliar compras asignadas a rutas ya cerradas:', error);
            }
        };

        void reconcileStaleAssignments();

        return () => {
            cancelled = true;
        };
    }, [companyPurchaseLinesPath, db, resolvedCompanyId, stockPurchaseLines, stockPurchases]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyOutboundOrdersPath),
            (snapshot) => {
                const nextOrders = snapshot.docs
                    .map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }))
                    .sort((a, b) => {
                        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                        return bTime - aTime;
                    });
                setStockOutboundOrders(nextOrders);
            },
            () => setStockOutboundOrders([])
        );
        return () => unsubscribe();
    }, [companyOutboundOrdersPath, resolvedCompanyId]);

    useEffect(() => {
        if (!db || !resolvedCompanyId) return;
        const unsubscribe = onSnapshot(
            collection(db, companyMovementsPath),
            (snapshot) => {
                const nextMovements = snapshot.docs
                    .map((movementDoc) => ({ id: movementDoc.id, ...movementDoc.data() }))
                    .sort((a, b) => {
                        const aTime =
                            a.appliedAt?.toDate?.()?.getTime?.() ||
                            a.createdAt?.toDate?.()?.getTime?.() ||
                            new Date(a.appliedAt || a.createdAt || 0).getTime() ||
                            0;
                        const bTime =
                            b.appliedAt?.toDate?.()?.getTime?.() ||
                            b.createdAt?.toDate?.()?.getTime?.() ||
                            new Date(b.appliedAt || b.createdAt || 0).getTime() ||
                            0;
                        return bTime - aTime;
                    });
                setStockMovements(nextMovements);
            },
            () => setStockMovements([])
        );
        return () => unsubscribe();
    }, [companyMovementsPath, resolvedCompanyId]);

    useEffect(() => {
        if (products.length === 0) return;
        const fetchAllHistory = async () => {
            const historyPromises = products.map(async (product) => {
                const historyRef = product?._source === 'legacy'
                    ? collection(db, legacyStockProductControlsCollectionPath(appId, product.id))
                    : query(collection(db, companyControlsPath), where('productId', '==', product.id));
                const historySnapshot = await getDocs(historyRef);
                return historySnapshot.docs.map(doc => ({
                    ...doc.data(),
                    id: doc.id,
                    productId: product.id,
                    productName: product.name,
                    _source: product?._source || 'company'
                }));
            });
            const allHistoryArrays = await Promise.all(historyPromises);
            setAllHistory(allHistoryArrays.flat());
        };
        fetchAllHistory();
    }, [products, companyControlsPath]);

    useEffect(() => {
        const completedDates = new Set(
            scheduledTasks
                .filter(t => t.status === 'completed')
                .map(t => t.date)
        );
        let currentStreak = 0;
        let today = new Date();
        today.setHours(0, 0, 0, 0);
        while (completedDates.has(formatDate(today))) {
            currentStreak++;
            today.setDate(today.getDate() - 1);
        }
        setControlStreak(currentStreak);
    }, [scheduledTasks, formatDate]);

    const plannerEntries = useMemo(() => {
        const taskEntries = scheduledTasks.map((task) => ({
            id: `task-${task.id}`,
            entryType: 'task',
            sourceId: task.id,
            date: task.date,
            status: task.status === 'completed' ? 'completed' : 'pending',
            rawStatus: task.status || 'pending',
            title: task.productName || 'Producto',
            summary: 'Control individual programado',
            assignedByName: task.assignedByName || '',
            productId: task.productId || '',
            productName: task.productName || 'Producto',
        }));

        const sheetEntries = stockSheets.flatMap((sheet) => {
            const normalizedSheet = normalizeStockSheetRecord(sheet);
            const sheetDate = String(normalizedSheet.sheetDate || '').trim()
                || (normalizedSheet.createdAt?.toDate ? formatDate(normalizedSheet.createdAt.toDate()) : formatDate(new Date()));
            const isCompleted = ['completed', 'reviewed'].includes(String(normalizedSheet.status || '').trim());
            const categoryLabel = String(sheet.sourceCategorySnapshot || '').trim();
            const frequencyLabel = String(sheet.programFrequencyLabel || '').trim();
            const programFrequency = sheet.programFrequency || 'one_time';
            const programStatus = sheet.programStatus || 'scheduled';

            const baseEntry = {
                id: `sheet-${sheet.id}`,
                entryType: 'sheet',
                sourceId: sheet.id,
                date: normalizedSheet.sheetDate || sheetDate,
                status: isCompleted ? 'completed' : 'pending',
                rawStatus: normalizedSheet.status,
                programStatus,
                title: sheet.title || 'Planilla de control',
                summary: sheet.sourceType === 'category_control'
                    ? `Control por categoría${categoryLabel ? ` · ${categoryLabel}` : ''}${frequencyLabel ? ` · ${frequencyLabel}` : ''}`
                    : 'Planilla general de control',
                assignedByName: sheet.assignedControllerDisplayNameSnapshot || sheet.assignedControllerNameSnapshot || '',
                productId: '',
                productName: categoryLabel ? `Categoría ${categoryLabel}` : 'Control general',
                linesCount: Number(sheet.linesCount || 0),
                differenceLinesCount: Number(sheet.differenceLinesCount || 0),
                sourceCategorySnapshot: categoryLabel,
                sourceType: sheet.sourceType || 'manual',
                programFrequency,
                programFrequencyLabel: frequencyLabel || getCategoryControlFrequencyLabel(programFrequency),
                sheet: normalizedSheet,
            };

            const projectedEntries = [];
            const stepDays = getCategoryControlFrequencyStep(programFrequency);
            if (sheet.sourceType === 'category_control' && stepDays > 0) {
                const startDate = new Date(`${sheetDate}T00:00:00`);
                if (!Number.isNaN(startDate.getTime())) {
                    for (let occurrence = 1; occurrence <= 8; occurrence += 1) {
                        const projectedDate = new Date(startDate);
                        projectedDate.setDate(projectedDate.getDate() + (stepDays * occurrence));
                        projectedEntries.push({
                            ...baseEntry,
                            id: `sheet-program-${sheet.id}-${occurrence}`,
                            entryType: 'sheet_program',
                            date: formatDate(projectedDate),
                            status: programStatus === 'paused' ? 'paused' : 'pending',
                            rawStatus: `programmed_${programFrequency}`,
                            summary: `Próximo control por categoría${categoryLabel ? ` · ${categoryLabel}` : ''}${frequencyLabel ? ` · ${frequencyLabel}` : ''}`,
                            projectedOccurrence: occurrence + 1,
                        });
                    }
                }
            }

            return [baseEntry, ...projectedEntries];
        });

        return [...taskEntries, ...sheetEntries].sort((a, b) => {
            const dateCompare = String(a.date || '').localeCompare(String(b.date || ''));
            if (dateCompare !== 0) return dateCompare;
            const entryOrder = { sheet: 0, sheet_program: 1, task: 2 };
            if (a.entryType !== b.entryType) return (entryOrder[a.entryType] ?? 99) - (entryOrder[b.entryType] ?? 99);
            return String(a.title || '').localeCompare(String(b.title || ''), 'es');
        });
    }, [formatDate, scheduledTasks, stockSheets]);

    const hasCategoryControlScheduledForDay = useCallback((categoryLabel, targetDate, excludedSheetId = '') => {
        const normalizedCategory = normalizeCategoryControlKey(categoryLabel);
        const normalizedDate = String(targetDate || '').trim();
        const excludedId = String(excludedSheetId || '').trim();
        if (!normalizedCategory || !normalizedDate) return false;

        return stockSheets.some((sheet) => {
            if (String(sheet?.sourceType || '').trim() !== 'category_control') return false;
            const normalizedStatus = normalizeStockSheetStatus(sheet);
            if (['cancelled', 'completed', 'reviewed'].includes(normalizedStatus)) return false;
            if (excludedId && String(sheet?.id || '').trim() === excludedId) return false;
            const sheetCategory = normalizeCategoryControlKey(sheet?.sourceCategorySnapshot || '');
            const sheetDate = String(sheet?.sheetDate || '').trim();
            return sheetCategory === normalizedCategory && sheetDate === normalizedDate;
        });
    }, [stockSheets]);

    const getDayStatus = useCallback((day) => {
        const dayString = formatDate(day);
        const controlsOnDay = allHistory.filter(h => h.endTime && formatDate(h.endTime.toDate()) === dayString);
        if (controlsOnDay.length === 0) return 'none';
        
        const hasDifferences = controlsOnDay.some(h => {
            try {
                const details = JSON.parse(h.countDetails);
                // Check if any item in the details array has a non-zero difference
                return details.some(item => item.difference !== 0);
            } catch { return false; }
        });

        return hasDifferences ? 'differences' : 'perfect';
    }, [allHistory, formatDate]);

    const handleStatClick = async (title, data) => {
        const buildSummary = () => {
            if (!Array.isArray(data) || data.length === 0) {
                return 'Todavía no hay datos suficientes para construir una lectura operativa.';
            }

            if (title === 'Resumen de Tareas') {
                const pending = data.find((item) => item.name === 'Pendientes' || item.name === 'Pendientes de control')?.value || 0;
                const completed = data.find((item) => item.name === 'Completadas')?.value || 0;
                const total = pending + completed;
                const compliance = total > 0 ? Math.round((completed / total) * 100) : 0;
                return `Semana actual: ${completed} tareas completadas y ${pending} pendientes. Cumplimiento estimado: ${compliance}%.`;
            }

            if (title === 'Actividad de la Semana') {
                const bestDay = [...data].sort((a, b) => (b.controles || 0) - (a.controles || 0))[0];
                const total = data.reduce((acc, item) => acc + (item.controles || 0), 0);
                return `Se registran ${total} controles completados en la semana. El pico más alto aparece en ${bestDay?.name || 'la semana'} con ${bestDay?.controles || 0} cierres.`;
            }

            if (title === 'Top 5 Productos con Diferencias') {
                const first = data[0];
                const total = data.reduce((acc, item) => acc + (item.diferencia || 0), 0);
                return `Se detectan ${total} diferencias acumuladas en la muestra. El producto con mayor impacto actual es ${first?.name || 'sin dato'} con ${first?.diferencia || 0}.`;
            }

            return 'Lectura operativa lista para revisar en detalle.';
        };

        setAiSummaryContent({ title, content: buildSummary() });
        setShowAiSummaryModal(true);
    };

    const handleBarClick = (productName) => {
        const product = products.find(p => p.name === productName);
        if (product) {
            setProductForHistory(product);
            setShowProductHistoryModal(true);
        }
    };

    const isPastDay = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return new Date(date) < today;
    };

    const handleDragStart = (e, product) => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', JSON.stringify(product)); setDraggedProduct(product); };
    const handleDragEnd = () => { setDraggedProduct(null); setDragOverDate(null); };


    const normalizeContactName = (value = '') => String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const isSupplierContact = (contact = {}) => {
        const type = String(contact?.type || '').trim().toLowerCase();
        return type === 'proveedor' || type === 'supplier' || type === 'cliente-proveedor' || type === 'proveedor-cliente';
    };

    const getPrimaryContactAddress = (contact = {}) => {
        if (Array.isArray(contact?.addresses) && contact.addresses.length > 0) {
            return contact.addresses[0] || null;
        }
        return null;
    };


const buildStockProductPayload = ({
        name,
        sku = '',
        barcode = '',
        qrCode = '',
        category = '',
        model = '',
        brand = '',
        featureLabel = '',
        unitBase = 'unidad',
        defaultPurchaseCost = 0,
        defaultSalePrice = 0,
        controlFrequency = 'manual',
        controlDay = '1',
    }) => ({
        name: String(name || '').trim(),
        sku: String(sku || '').trim(),
        barcode: String(barcode || '').trim(),
        qrCode: String(qrCode || '').trim(),
        category: String(category || '').trim(),
        model: String(model || '').trim(),
        brand: String(brand || '').trim(),
        featureLabel: String(featureLabel || '').trim(),
        unitBase: String(unitBase || 'unidad').trim() || 'unidad',
        defaultPurchaseCost: Number(defaultPurchaseCost || 0),
        defaultSalePrice: Number(defaultSalePrice || 0),
        controlFrequency,
        controlDay,
        controlStatus: { status: 'available' },
        active: true,
        stockBuckets: buildDefaultStockBuckets(),
        stockSummary: {
            availableQty: 0,
            lastMovementAt: null,
            lastMovementType: null,
            lastOperationReason: null,
            lastOperationType: null,
            lastRouteCodeSnapshot: null,
            lastDestinationNameSnapshot: null,
            lastControlAt: null,
            lastControlledById: null,
            lastControlledByName: null,
        },
        companyId: resolvedCompanyId,
        createdAt: new Date(),
        updatedAt: new Date(),
    });

    const handleDrop = async (product, targetDate) => {
        if (isPastDay(targetDate)) { showMessage("No se pueden agregar tareas a días pasados.", "error"); return; }
        if (!product || !userId) return;
        const formattedDate = formatDate(targetDate);
        if (scheduledTasks.find(task => task.productId === product.id && task.date === formattedDate)) { showMessage(`"${product.name}" ya está programado para esta fecha.`, 'error'); return; }
        try {
            await addDoc(collection(db, product?._source === 'legacy' ? legacyTasksPath : companyTasksPath), {
                productId: product.id,
                productName: product.name,
                date: formattedDate,
                status: 'pending',
                assignedTo: userId,
                assignedByName: `Anónimo ${userId.substring(0, 4)}`,
                createdAt: new Date(),
                companyId: resolvedCompanyId
            });
            playSound('drop');
        } catch (error) { showMessage(`Error al agregar tarea: ${error.message}`, 'error'); }
    };

    const handleDayClick = (day) => {
        if (selectedForMobile) {
            if (isPastDay(day)) { showMessage("No se pueden agregar tareas a días pasados.", "error"); setSelectedForMobile(null); return; }
            handleDrop(selectedForMobile, day);
            setSelectedForMobile(null);
        } else {
            openPlannerDayDetails(day);
        }
    };

    const handleOpenProgramEdit = useCallback((sheet = {}) => {
        const nextDate = String(sheet.sheetDate || sheet.programStartDate || new Date().toISOString().slice(0, 10)).trim() || new Date().toISOString().slice(0, 10);
        setEditingProgramDraft({
            sheetId: sheet.id || '',
            title: sheet.title || 'Planilla de control',
            categoryLabel: String(sheet.sourceCategorySnapshot || '').trim(),
            frequency: String(sheet.programFrequency || 'one_time').trim() || 'one_time',
            firstDate: nextDate,
            programStatus: String(sheet.programStatus || 'scheduled').trim() || 'scheduled',
            notes: String(sheet.notes || '').trim(),
        });
        setShowProgramEditModal(true);
    }, []);

    const handleSaveProgramEdit = useCallback(async () => {
        const sheetId = String(editingProgramDraft.sheetId || '').trim();
        if (!sheetId) {
            showMessage('No encontré la planilla para actualizar el programa.', 'error');
            return;
        }

        const nextDate = String(editingProgramDraft.firstDate || '').trim();
        if (!nextDate) {
            showMessage('Elegí una fecha para el programa.', 'warning');
            return;
        }

        if (hasCategoryControlScheduledForDay(editingProgramDraft.categoryLabel, nextDate, sheetId)) {
            showMessage(
                `Ya hay un control de ${editingProgramDraft.categoryLabel || 'esa categoría'} programado para ${new Date(`${nextDate}T00:00:00`).toLocaleDateString('es-AR')}.`,
                'warning'
            );
            return;
        }

        const nextFrequency = String(editingProgramDraft.frequency || 'one_time').trim() || 'one_time';
        const nextProgramStatus = String(editingProgramDraft.programStatus || 'scheduled').trim() || 'scheduled';

        try {
            await updateDoc(doc(db, resolveStockSheetDocPath(resolvedCompanyId, sheetId)), {
                sheetDate: nextDate,
                programStartDate: nextDate,
                programFrequency: nextFrequency,
                programFrequencyLabel: getCategoryControlFrequencyLabel(nextFrequency),
                programStatus: nextProgramStatus,
                notes: String(editingProgramDraft.notes || '').trim(),
                updatedAt: new Date(),
            });
            setShowProgramEditModal(false);
            showMessage('Actualicé la programación del control.', 'success');
        } catch (error) {
            showMessage(`No pude actualizar el programa: ${error.message}`, 'error');
        }
    }, [editingProgramDraft, hasCategoryControlScheduledForDay, resolvedCompanyId, showMessage]);

    const handleToggleProgramStatus = useCallback(async (sheet = {}) => {
        const sheetId = String(sheet.id || '').trim();
        if (!sheetId) {
            showMessage('No encontré la planilla para cambiar el programa.', 'error');
            return;
        }

        const currentProgramStatus = String(sheet.programStatus || 'scheduled').trim() || 'scheduled';
        const nextProgramStatus = currentProgramStatus === 'paused' ? 'scheduled' : 'paused';

        try {
            await updateDoc(doc(db, resolveStockSheetDocPath(resolvedCompanyId, sheetId)), {
                programStatus: nextProgramStatus,
                updatedAt: new Date(),
            });
            showMessage(
                nextProgramStatus === 'paused'
                    ? 'El programa quedó pausado en Programación.'
                    : 'El programa volvió a quedar activo en Programación.',
                'success'
            );
        } catch (error) {
            showMessage(`No pude cambiar el estado del programa: ${error.message}`, 'error');
        }
    }, [resolvedCompanyId, showMessage]);

    const handleOpenCategoryHistory = useCallback((categoryLabel = '') => {
        const normalizedCategory = String(categoryLabel || '').trim();
        if (!normalizedCategory) {
            showMessage('No encontré la categoría para abrir el historial.', 'warning');
            return;
        }

        const entries = stockSheets
            .filter((sheet) =>
                String(sheet?.sourceType || '').trim() === 'category_control'
                && normalizeCategoryControlKey(sheet?.sourceCategorySnapshot || '') === normalizeCategoryControlKey(normalizedCategory)
                && ['completed', 'reviewed'].includes(String(sheet?.status || '').trim())
            )
            .sort((a, b) => {
                const aTime = a.completedAt?.seconds || a.updatedAt?.seconds || a.createdAt?.seconds || 0;
                const bTime = b.completedAt?.seconds || b.updatedAt?.seconds || b.createdAt?.seconds || 0;
                return bTime - aTime;
            });

        setActiveCategoryHistory({
            categoryLabel: normalizedCategory,
            entries,
        });
        setShowDayDetailsModal(false);
        setShowCategoryHistoryModal(true);
    }, [showMessage, stockSheets]);

    const activeSheetAdjustments = useMemo(() => {
        if (!activeSheetPreview?.id) return [];
        return allHistory
            .filter((entry) => String(entry?.type || '').trim() === 'stock_adjustment' && String(entry?.sheetId || '').trim() === String(activeSheetPreview.id || '').trim())
            .sort((a, b) => getTimestampMs(b.endTime || b.createdAt) - getTimestampMs(a.endTime || a.createdAt));
    }, [activeSheetPreview?.id, allHistory]);

    const getSheetAdjustmentsForHistory = useCallback((sheetId = '') => {
        const normalizedSheetId = String(sheetId || '').trim();
        if (!normalizedSheetId) return [];
        return allHistory
            .filter((entry) => String(entry?.type || '').trim() === 'stock_adjustment' && String(entry?.sheetId || '').trim() === normalizedSheetId)
            .sort((a, b) => getTimestampMs(b.endTime || b.createdAt) - getTimestampMs(a.endTime || a.createdAt));
    }, [allHistory]);

    const handleProductTap = (product) => {
        if (selectedForMobile?.id === product.id) setSelectedForMobile(null);
        else { setSelectedForMobile(product); showMessage(`"${product.name}" seleccionado. Toca un día para asignarlo.`); }
    };
    
    const scheduleRecurringTasks = async (product) => {
        const { id, name, controlFrequency, controlDay } = product;
        if (controlFrequency === 'manual' || !controlDay) return;
        const batch = writeBatch(db);
        const tasksRef = collection(
            db,
            product?._source === 'legacy' ? legacyTasksPath : companyTasksPath
        );
        const q = query(tasksRef, where("productId", "==", id), where("assignedTo", "==", "auto"));
        const autoTasksSnapshot = await getDocs(q);
        autoTasksSnapshot.docs.forEach(doc => batch.delete(doc.ref));
        const existingTasksSnapshot = await getDocs(query(tasksRef, where("productId", "==", id)));
        const existingDates = new Set(existingTasksSnapshot.docs.map(doc => doc.data().date));
        const today = new Date();
        today.setHours(0,0,0,0);
        const targetDay = parseInt(controlDay, 10);
        let increment = 0;
        switch(controlFrequency) {
            case 'weekly': increment = 7; break;
            case 'bi-weekly': increment = 14; break;
            case 'monthly': increment = 28; break;
            default: return;
        }
        for (let i = 0; i < 12; i++) {
            let nextDate = new Date(today);
            let dayOfWeek = nextDate.getDay();
            if (dayOfWeek === 0) dayOfWeek = 7;
            let dateOffset = targetDay - dayOfWeek;
            nextDate.setDate(nextDate.getDate() + dateOffset);
            if (i > 0) {
                nextDate.setDate(nextDate.getDate() + (i * increment));
            }
            if (nextDate < today) {
                if (i === 0) {
                    nextDate.setDate(nextDate.getDate() + increment);
                } else {
                    continue;
                }
            }
            const formattedDate = formatDate(nextDate);
            if (!existingDates.has(formattedDate)) {
                const newTaskRef = doc(tasksRef);
                batch.set(newTaskRef, {
                    productId: id, productName: name, date: formattedDate, status: 'pending',
                    assignedTo: 'auto', assignedByName: 'Sistema', createdAt: new Date(), companyId: resolvedCompanyId
                });
            }
        }
        await batch.commit();
    };

    const handleAddProduct = async () => {
        if (newProductName.trim() === '') return;
        try {
            const newProduct = buildStockProductPayload({
                name: newProductName.trim(),
                controlFrequency: newProductFrequency,
                controlDay: newProductDay,
            });
            const docRef = await addDoc(collection(db, companyProductsPath), newProduct);
            await scheduleRecurringTasks({ id: docRef.id, ...newProduct, _source: 'company' });
            setNewProductName('');
            setNewProductFrequency('manual');
            playSound('add');
            addStockNotification(`Nuevo producto agregado: ${newProduct.name}`);
            showMessage(`Producto agregado y programado.`, 'success');
        } catch (error) { showMessage(`Error: ${error.message}`, 'error'); }
    };

    const handleCreateProductFromPurchase = async (lineIndex, quickProductDraft) => {
        const productName = String(quickProductDraft?.name || '').trim();
        if (!productName) {
            showMessage('Definí el nombre del producto para crearlo desde la compra.', 'warning');
            return;
        }
        try {
            const productPayload = buildStockProductPayload({
                name: productName,
                sku: quickProductDraft?.sku,
                barcode: quickProductDraft?.barcode,
                category: quickProductDraft?.category,
                model: quickProductDraft?.model,
                brand: quickProductDraft?.brand,
                featureLabel: quickProductDraft?.featureLabel,
                unitBase: quickProductDraft?.unitBase || 'unidad',
                controlFrequency: quickProductDraft?.controlFrequency || 'manual',
                controlDay: quickProductDraft?.controlDay || '1',
            });
            const docRef = await addDoc(collection(db, companyProductsPath), productPayload);
            await scheduleRecurringTasks({ id: docRef.id, ...productPayload, _source: 'company' });
            setNewPurchaseForm((prev) => ({
                ...prev,
                lines: prev.lines.map((line, currentIndex) => currentIndex === lineIndex ? {
                    ...line,
                    productId: docRef.id,
                    quickProduct: null,
                } : line),
            }));
            playSound('add');
            addStockNotification(`Nuevo producto creado desde compra: ${productName}`);
            showMessage(`Producto creado y vinculado a la línea de compra.`, 'success');
        } catch (error) {
            showMessage(`No pude crear el producto: ${error.message}`, 'error');
        }
    };

    const handleEditProductClick = (product) => { 
        setEditingProduct({
            ...product,
            controlFrequency: product.controlFrequency || 'manual',
            controlDay: product.controlDay || '1',
            defaultPurchaseCost: Number(product.defaultPurchaseCost || 0),
            defaultSalePrice: Number(product.defaultSalePrice || 0),
        });
        setShowEditProductModal(true); 
    };
    
    const handleSaveEditedProduct = async () => {
        if (!editingProduct || editingProduct.name.trim() === '') return;
        try {
            const productRef = doc(db, resolveProductDocPath(editingProduct));
            await updateDoc(productRef, {                 name: editingProduct.name.trim(),
                sku: String(editingProduct.sku || '').trim(),
                barcode: String(editingProduct.barcode || '').trim(),
                category: String(editingProduct.category || '').trim(),
                brand: String(editingProduct.brand || '').trim(),
                featureLabel: String(editingProduct.featureLabel || '').trim(),
                model: String(editingProduct.model || '').trim(),
                inches: String(editingProduct.inches || '').trim(),
                notes: String(editingProduct.notes || '').trim(),
                defaultSupplierName: String(editingProduct.defaultSupplierName || '').trim(),
                defaultPurchaseCost: Number(editingProduct.defaultPurchaseCost || 0),
                defaultSalePrice: Number(editingProduct.defaultSalePrice || 0),
                controlFrequency: editingProduct.controlFrequency,
                controlDay: editingProduct.controlDay,
                updatedAt: new Date(),
            });
            await scheduleRecurringTasks(editingProduct);
            showMessage(`Producto actualizado y reprogramado.`, 'success');
            setShowEditProductModal(false);
        } catch (error) { showMessage(`Error al actualizar: ${error.message}`, 'error'); }
    };

    const handleDeleteProductClick = (product) => {
        setProductToDelete(product);
        setShowConfirmDeleteModal(true);
    };

    const confirmDeleteProduct = async () => {
        if (!productToDelete) return;
        try {
            const batch = writeBatch(db);
            batch.delete(doc(db, resolveProductDocPath(productToDelete)));
            const tasksQuery = query(
                collection(db, productToDelete?._source === 'legacy' ? legacyTasksPath : companyTasksPath),
                where("productId", "==", productToDelete.id)
            );
            const tasksSnapshot = await getDocs(tasksQuery);
            tasksSnapshot.docs.forEach(taskDoc => batch.delete(taskDoc.ref));
            await batch.commit();
            addStockNotification(`Producto eliminado: ${productToDelete.name}`);
            showMessage(`Producto y tareas eliminados.`, 'success');
            setShowConfirmDeleteModal(false);
        } catch (error) { showMessage(`Error al eliminar: ${error.message}`, 'error'); }
    };


    const buildControllerFormFromRecord = useCallback((controller = {}) => ({
        firstName: String(controller.firstName || '').trim(),
        lastName: String(controller.lastName || '').trim(),
        phone: String(controller.phone || '').trim(),
        pin: String(controller.pin || '').trim() || generateControllerPin(),
        active: controller.active !== false,
        photoURL: String(controller.photoURL || '').trim(),
        photoStoragePath: String(controller.photoStoragePath || '').trim(),
    }), []);

    const handleCancelControllerEdition = useCallback(() => {
        setEditingControllerId(null);
        setNewControllerForm(buildDefaultControllerForm());
    }, []);

    const handleEditController = useCallback((controllerId) => {
        const controller = stockControllers.find((item) => item.id === controllerId);
        if (!controller) return;
        setEditingControllerId(controller.id);
        setNewControllerForm(buildControllerFormFromRecord(controller));
        setActivePanel('controllers');
        showMessage('Cargué la ficha del controlador para que la puedas actualizar.', 'success');
    }, [buildControllerFormFromRecord, showMessage, stockControllers]);

    const handleCreateController = async () => {
        const firstName = newControllerForm.firstName.trim();
        const lastName = newControllerForm.lastName.trim();
         if (!firstName || !lastName) {
              showMessage('Completá nombre y apellido para crear el controlador.', 'warning');
              return;
          }
          try {
              const displayName = [firstName, lastName].filter(Boolean).join(' ');
              const pin = String(newControllerForm.pin || '').trim() || generateControllerPin();
              const now = new Date();

              if (editingControllerId) {
                  const currentController = stockControllers.find((item) => item.id === editingControllerId);
                  const existingAccessSlug = String(currentController?.accessSlug || '').trim();
                  const accessSlug = existingAccessSlug || `${slugifyControllerSegment(`${firstName}-${lastName}`)}-${Math.random().toString(36).slice(2, 6)}`;
                  await updateDoc(doc(db, resolveStockControllerDocPath(resolvedCompanyId, editingControllerId)), {
                      firstName,
                      lastName,
                      displayName,
                      phone: newControllerForm.phone.trim(),
                      photoURL: String(newControllerForm.photoURL || '').trim(),
                      photoStoragePath: String(newControllerForm.photoStoragePath || '').trim(),
                      pin,
                      accessSlug,
                      accessPath: buildControllerAccessPath(resolvedCompanyId, accessSlug),
                      accessUrl: buildControllerAccessUrl(resolvedCompanyId, accessSlug),
                      active: newControllerForm.active,
                      updatedAt: now,
                      updatedByUserId: userId,
                  });
                  handleCancelControllerEdition();
                  showMessage('Controlador actualizado.', 'success');
              } else {
                  const accessSlugBase = slugifyControllerSegment(`${firstName}-${lastName}`);
                  const accessSlug = `${accessSlugBase}-${Math.random().toString(36).slice(2, 6)}`;
                  await addDoc(collection(db, companyControllersPath), {
                      companyId: resolvedCompanyId,
                      firstName,
                      lastName,
                      displayName,
                      phone: newControllerForm.phone.trim(),
                      photoURL: String(newControllerForm.photoURL || '').trim(),
                      photoStoragePath: String(newControllerForm.photoStoragePath || '').trim(),
                      pin,
                      accessSlug,
                      accessPath: buildControllerAccessPath(resolvedCompanyId, accessSlug),
                      accessUrl: buildControllerAccessUrl(resolvedCompanyId, accessSlug),
                      active: newControllerForm.active,
                      createdAt: now,
                      createdByUserId: userId,
                  });
                  setNewControllerForm(buildDefaultControllerForm());
                  showMessage('Controlador creado.', 'success');
              }
          } catch (error) {
              showMessage(`No pude guardar el controlador: ${error.message}`, 'error');
          }
      };

    const handleToggleController = async (controller) => {
        try {
            await updateDoc(doc(db, resolveStockControllerDocPath(resolvedCompanyId, controller.id)), {
                active: controller.active === false,
                updatedAt: new Date(),
            });
            showMessage(controller.active === false ? 'Controlador reactivado.' : 'Controlador desactivado.', 'success');
        } catch (error) {
            showMessage(`No pude actualizar el controlador: ${error.message}`, 'error');
        }
    };

    const handleCreateSheet = async () => {
        const title = newSheetForm.title.trim();
        if (!title) {
            showMessage('Definí un título para la planilla.', 'warning');
            return;
        }

        const matchingTasks = scheduledTasks.filter((task) => task.date === newSheetForm.sheetDate);
        if (newSheetForm.includeDayTasks && matchingTasks.length === 0) {
            showMessage('No hay tareas cargadas para esa fecha. Podés crear la planilla vacía desmarcando la importación del día.', 'warning');
            return;
        }

        try {
            const sheetRef = doc(collection(db, companySheetsPath));
            const batch = writeBatch(db);
            const sheetPayload = {
                companyId: resolvedCompanyId,
                title,
                sourceType: newSheetForm.sourceType,
                status: 'waiting_assignment',
                sheetDate: newSheetForm.sheetDate,
                notes: newSheetForm.notes.trim(),
                createdAt: new Date(),
                createdByUserId: userId,
                linesCount: newSheetForm.includeDayTasks ? matchingTasks.length : 0,
                assignedControllerId: null,
                assignedControllerNameSnapshot: null,
                assignedControllerLastNameSnapshot: null,
                assignedControllerDisplayNameSnapshot: null,
                assignedControllerPhotoURLSnapshot: null,
                assignedAt: null,
                startedAt: null,
                completedAt: null,
            };
            batch.set(sheetRef, sheetPayload);

            if (newSheetForm.includeDayTasks) {
                matchingTasks.forEach((task, index) => {
                    const lineRef = doc(collection(db, companySheetLinesPath));
                    batch.set(lineRef, {
                        companyId: resolvedCompanyId,
                        sheetId: sheetRef.id,
                        sheetTitleSnapshot: title,
                        taskId: task.id,
                        sourceTaskStatusSnapshot: task.status,
                        productId: task.productId,
                        productNameSnapshot: task.productName,
                        expectedQuantity: null,
                        countedQuantity: null,
                        differenceQuantity: null,
                        status: 'pending',
                        lineOrder: index + 1,
                        originType: newSheetForm.sourceType,
                        createdAt: new Date(),
                    });

                    batch.update(doc(db, resolveTaskDocPath(task)), {
                        sheetId: sheetRef.id,
                        sheetTitleSnapshot: title,
                        sheetStatus: 'waiting_assignment',
                        updatedAt: new Date(),
                    });
                });
            }

            await batch.commit();
            setNewSheetForm((prev) => ({
                ...prev,
                title: '',
                notes: '',
                sourceType: 'manual',
                includeDayTasks: true,
            }));
            showMessage('Planilla creada y lista para asignación.', 'success');
        } catch (error) {
            showMessage(`No pude crear la planilla: ${error.message}`, 'error');
        }
    };

    const handleSupplierNameChange = useCallback((value) => {
        const rawName = String(value || '');
        const normalizedInput = normalizeContactName(rawName);
        const matchedSupplier = stockContacts.find((contact) => normalizeContactName(contact.name) === normalizedInput);
        const primaryAddress = matchedSupplier ? getPrimaryContactAddress(matchedSupplier) : null;

        setNewPurchaseForm((prev) => ({
            ...prev,
            supplierName: rawName,
            selectedSupplierId: matchedSupplier?.id || '',
            supplierAddress: primaryAddress?.address || (matchedSupplier ? '' : prev.supplierAddress),
            supplierApartment: primaryAddress?.apartment || (matchedSupplier ? '' : prev.supplierApartment),
            supplierLocality: primaryAddress?.locality || (matchedSupplier ? '' : prev.supplierLocality),
            supplierPartido: primaryAddress?.partido || (matchedSupplier ? '' : prev.supplierPartido),
            supplierProvince: primaryAddress?.province || (matchedSupplier ? 'CABA' : prev.supplierProvince || 'CABA'),
            supplierPostalCode: primaryAddress?.postalCode || (matchedSupplier ? '' : prev.supplierPostalCode),
            supplierLatlng: primaryAddress?.latlng || (matchedSupplier ? null : prev.supplierLatlng),
        }));
    }, [stockContacts]);

    const handleSupplierAddressPatch = useCallback((patch) => {
        setNewPurchaseForm((prev) => ({
            ...prev,
            selectedSupplierId: '',
            supplierLatlng: patch.hasOwnProperty('supplierLatlng') ? patch.supplierLatlng : null,
            ...patch,
        }));
    }, []);

    const handleGeocodePurchaseSupplier = useCallback(async () => {
        const parts = [
            newPurchaseForm.supplierAddress,
            newPurchaseForm.supplierApartment,
            newPurchaseForm.supplierLocality,
            newPurchaseForm.supplierPartido,
            newPurchaseForm.supplierProvince,
            newPurchaseForm.supplierPostalCode,
        ].filter(Boolean);
        if (parts.length === 0) {
            showMessage('Completá al menos la dirección del proveedor para validar su ubicación.', 'warning');
            return null;
        }
        const coords = await geocodeAddress(parts.join(', '), showMessage);
        if (coords) {
            setNewPurchaseForm((prev) => ({ ...prev, supplierLatlng: coords }));
            showMessage('Ubicación del proveedor validada.', 'success');
        }
        return coords;
    }, [newPurchaseForm, showMessage]);

    const buildPurchaseFormFromPurchase = useCallback((purchase = {}) => {
        const purchaseLines = stockPurchaseLines
            .filter((line) => line.purchaseId === purchase.id)
            .sort((a, b) => Number(a.lineOrder || 0) - Number(b.lineOrder || 0));

        const linkedSupplier = stockContacts.find((contact) => contact.id === purchase.supplierId) || null;
        const primaryAddress = linkedSupplier ? getPrimaryContactAddress(linkedSupplier) : null;

        return {
            supplierName: String(purchase.supplierNameSnapshot || linkedSupplier?.name || '').trim(),
            selectedSupplierId: linkedSupplier?.id || '',
            supplierAddress: String(primaryAddress?.address || '').trim(),
            supplierApartment: String(primaryAddress?.apartment || '').trim(),
            supplierLocality: String(primaryAddress?.locality || '').trim(),
            supplierPartido: String(primaryAddress?.partido || '').trim(),
            supplierProvince: String(primaryAddress?.province || 'CABA').trim() || 'CABA',
            supplierPostalCode: String(primaryAddress?.postalCode || '').trim(),
            supplierLatlng: primaryAddress?.latlng || purchase.supplierGeoSnapshot || null,
            purchaseDate: String(purchase.purchaseDate || new Date().toISOString().slice(0, 10)).trim(),
            sourceType: String(purchase.sourceType || 'manual').trim() || 'manual',
            sourceReference: String(purchase.sourceReference || '').trim(),
            notes: String(purchase.note || '').trim(),
            currency: purchase.currency || 'ARS',
            lines: purchaseLines.length > 0
                ? purchaseLines.map((line) => ({
                    id: line.id,
                    productId: line.productId || '',
                    quantity: String(line.orderedQuantity ?? ''),
                    unitCost: line.unitCost != null ? String(line.unitCost) : '',
                    quickProduct: null,
                }))
                : [createPurchaseLineDraft()],
        };
    }, [stockContacts, stockPurchaseLines]);

    const handleCancelPurchaseEdition = useCallback(() => {
        setEditingPurchaseId(null);
        setNewPurchaseForm(buildDefaultPurchaseForm());
    }, []);

    const handleEditPurchase = useCallback((purchaseId) => {
        const purchase = stockPurchases.find((item) => item.id === purchaseId);
        if (!purchase) return;
        const purchaseLines = stockPurchaseLines.filter((line) => line.purchaseId === purchaseId);
        const isEditable = purchaseLines.every(isPurchaseLineEditable)
            && ['pending_pickup', 'draft'].includes(String(purchase.status || 'pending_pickup').trim().toLowerCase());

        if (!isEditable) {
            showMessage('Esa compra ya empezó a pasar por logística o recepción. Para cuidarla bien, no la edito desde esta cola.', 'warning');
            return;
        }

        setEditingPurchaseId(purchase.id);
        setNewPurchaseForm(buildPurchaseFormFromPurchase(purchase));
        setActivePanel('purchases');
        showMessage('Cargué la compra al formulario para que la puedas ajustar.', 'success');
    }, [buildPurchaseFormFromPurchase, showMessage, stockPurchaseLines, stockPurchases]);

    const handleCancelPendingPurchase = useCallback(async (purchaseId) => {
        const purchase = stockPurchases.find((item) => item.id === purchaseId);
        if (!purchase) return;

        const purchaseLines = stockPurchaseLines.filter((line) => line.purchaseId === purchaseId);
        const isEditable = purchaseLines.every(isPurchaseLineEditable)
            && ['pending_pickup', 'draft'].includes(String(purchase.status || 'pending_pickup').trim().toLowerCase());

        if (!isEditable) {
            showMessage('Esa compra ya se movió en logística o recepción. Para cuidar la traza, ya no la puedo eliminar desde esta cola.', 'warning');
            return;
        }

        try {
            const now = new Date();
            const batch = writeBatch(db);
            const purchaseRef = doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, purchase.id));

            batch.update(purchaseRef, {
                status: 'cancelled',
                updatedAt: now,
                closedAt: now,
                pendingLinesCount: 0,
            });

            purchaseLines.forEach((line) => {
                const lineRef = doc(db, `${companyPurchaseLinesPath}/${line.id}`);
                const quantity = Math.max(
                    0,
                    Number(line.pendingPickupQuantity ?? line.orderedQuantity ?? 0)
                );
                const product = products.find((item) => item.id === line.productId);

                batch.update(lineRef, {
                    status: 'cancelled',
                    cancelledQuantity: Number(line.orderedQuantity || quantity || 0),
                    pendingPickupQuantity: 0,
                    updatedAt: now,
                    closedAt: now,
                });

                if (product && quantity > 0) {
                    const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                    const productSnapshots = buildMovementProductSnapshotFields({ product, line });
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId: line.productId,
                        productNameSnapshot: line.productNameSnapshot || product.name || 'Producto',
                        ...productSnapshots,
                        unitSnapshot: line.unitSnapshot || product.unitBase || 'unidad',
                        movementType: 'purchase_pending_cancelled',
                        quantity,
                        stockBucketsImpact: {
                            pendingInboundDelta: -quantity,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: 0,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'purchase',
                        sourceId: purchase.id,
                        sourceLineId: line.id,
                        supplierId: purchase.supplierId || '',
                        supplierNameSnapshot: purchase.supplierNameSnapshot || '',
                        note: 'Compra pendiente cancelada antes de entrar a logística.',
                        costUnitSnapshot: line.unitCost ?? null,
                        costTotalSnapshot: line.totalCost ?? null,
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: now,
                        appliedAt: now,
                        cancelledAt: null,
                    });

                    batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, line.productId)), {
                        updatedAt: now,
                        'stockBuckets.pendingInboundQty': Math.max(0, Number(product?.stockBuckets?.pendingInboundQty || 0) - quantity),
                        'stockSummary.lastMovementAt': now,
                        'stockSummary.lastMovementType': 'purchase_pending_cancelled',
                        'stockSummary.lastOperationReason': null,
                        'stockSummary.lastOperationType': 'purchase',
                        'stockSummary.lastRouteCodeSnapshot': null,
                        'stockSummary.lastDestinationNameSnapshot': purchase.supplierNameSnapshot || '',
                    });
                }
            });

            await batch.commit();

            if (editingPurchaseId === purchaseId) {
                setEditingPurchaseId(null);
                setNewPurchaseForm(buildDefaultPurchaseForm());
            }

            showMessage('Compra pendiente cancelada y retirada de la cola operativa.', 'success');
        } catch (error) {
            showMessage(`No pude cancelar la compra pendiente: ${error.message}`, 'error');
        }
    }, [companyPurchaseLinesPath, db, editingPurchaseId, products, resolvedCompanyId, showMessage, stockPurchaseLines, stockPurchases, userId]);

    const handleOutboundDestinationSelect = useCallback((contactId) => {
        const selectedContact = sharedContacts.find((contact) => String(contact.id) === String(contactId)) || null;
        const primaryAddress = selectedContact ? getPrimaryContactAddress(selectedContact) : null;

        setNewOutboundOrderForm((prev) => ({
            ...prev,
            selectedContactId: selectedContact?.id || '',
            destinationName: selectedContact?.name || prev.destinationName,
            destinationAddress: primaryAddress?.address || '',
            destinationApartment: primaryAddress?.apartment || '',
            destinationLocality: primaryAddress?.locality || '',
            destinationPartido: primaryAddress?.partido || '',
            destinationProvince: primaryAddress?.province || 'CABA',
            destinationPostalCode: primaryAddress?.postalCode || '',
            destinationLatlng: primaryAddress?.latlng || null,
        }));
    }, [sharedContacts]);

    const handleOutboundDestinationAddressPatch = useCallback((patch) => {
        setNewOutboundOrderForm((prev) => ({
            ...prev,
            selectedContactId: '',
            destinationLatlng: Object.prototype.hasOwnProperty.call(patch, 'destinationLatlng')
                ? patch.destinationLatlng
                : prev.destinationLatlng,
            ...patch,
        }));
    }, []);

    const handleGeocodeOutboundDestination = useCallback(async () => {
        const parts = [
            newOutboundOrderForm.destinationAddress,
            newOutboundOrderForm.destinationApartment,
            newOutboundOrderForm.destinationLocality,
            newOutboundOrderForm.destinationPartido,
            newOutboundOrderForm.destinationProvince,
            newOutboundOrderForm.destinationPostalCode,
        ].filter(Boolean);
        if (parts.length === 0) {
            showMessage('Completá la dirección del destino para validar la entrega.', 'warning');
            return null;
        }
        const coords = await geocodeAddress(parts.join(', '), showMessage);
        if (coords) {
            setNewOutboundOrderForm((prev) => ({ ...prev, destinationLatlng: coords }));
            showMessage('Destino validado para este pedido de salida.', 'success');
        }
        return coords;
    }, [newOutboundOrderForm, showMessage]);

    const handleCancelOutboundOrderEdition = useCallback(() => {
        setEditingOutboundOrderId(null);
        setNewOutboundOrderForm(buildDefaultOutboundOrderForm());
    }, []);

    const handleEditOutboundOrder = useCallback((orderId) => {
        const order = stockOutboundOrders.find((item) => item.id === orderId);
        if (!order) return;
        if (['assigned_to_route', 'partially_assigned', 'in_transit', 'partially_delivered', 'delivered', 'in_warranty', 'ready_for_pickup'].includes(order.status)) {
            showMessage('Ese pedido ya está vinculado a logística. Para cuidarlo bien, no lo edito desde stock.', 'warning');
            return;
        }
        setEditingOutboundOrderId(order.id);
        setNewOutboundOrderForm(buildOutboundOrderFormFromOrder(order));
        setActivePanel('outbound-orders');
        showMessage('Cargué el pedido al formulario para que lo puedas ajustar.', 'success');
    }, [showMessage, stockOutboundOrders]);

    const handleDeleteOutboundOrder = useCallback(async (orderId) => {
        const order = stockOutboundOrders.find((item) => item.id === orderId);
        if (!order) return;
        if (['assigned_to_route', 'partially_assigned', 'in_transit', 'partially_delivered', 'delivered', 'in_warranty', 'ready_for_pickup'].includes(order.status)) {
            showMessage('Ese pedido ya está en movimiento. No lo puedo eliminar desde acá.', 'warning');
            return;
        }

        try {
            const now = new Date();
            const batch = writeBatch(db);
            const orderDocRef = doc(db, `${companyOutboundOrdersPath}/${order.id}`);

            if (order.status === 'ready_for_delivery') {
                (order.lines || []).forEach((line) => {
                    if (!line?.productId || !line?.quantity) return;
                    const product = products.find((item) => item.id === line.productId);
                    const quantity = Number(line.pendingDeliveryQuantity ?? line.quantity ?? 0);
                    const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                    const productSnapshots = buildMovementProductSnapshotFields({ product, line });
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId: line.productId,
                        productNameSnapshot: line.productNameSnapshot || product?.name || 'Producto',
                        ...productSnapshots,
                        unitSnapshot: line.unitSnapshot || product?.unitBase || 'unidad',
                        movementType: 'stock_outbound_cancelled',
                        quantity,
                        stockBucketsImpact: {
                            pendingInboundDelta: 0,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: -quantity,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'outbound_order',
                        sourceId: order.id,
                        sourceLineId: line.id,
                        customerId: order.contactId || '',
                        customerNameSnapshot: order.contactNameSnapshot || order.destinationNameSnapshot || '',
                        operationType: String(order.operationType || 'sale').trim().toLowerCase() || 'sale',
                        referenceCodeSnapshot: String(order.reference || '').trim(),
                        note: 'Pedido de salida cancelado antes de entrar a logística.',
                        costUnitSnapshot: line.unitCost ?? null,
                        costTotalSnapshot: line.totalCost ?? null,
                        saleUnitSnapshot: line.saleUnitPrice ?? null,
                        saleTotalSnapshot: line.totalSale ?? null,
                        marginSnapshot: line.margin ?? null,
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: now,
                        appliedAt: now,
                        cancelledAt: null,
                    });
                    if (product) {
                        batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, line.productId)), {
                            updatedAt: now,
                            'stockBuckets.pendingOutboundQty': Math.max(0, Number(product?.stockBuckets?.pendingOutboundQty || 0) - quantity),
                            'stockSummary.lastMovementAt': now,
                            'stockSummary.lastMovementType': 'stock_outbound_cancelled',
                            'stockSummary.lastOperationReason': String(order.operationType || 'sale').trim().toLowerCase() || 'sale',
                            'stockSummary.lastOperationType': String(order.operationType || 'sale').trim().toLowerCase() || 'sale',
                            'stockSummary.lastRouteCodeSnapshot': null,
                            'stockSummary.lastDestinationNameSnapshot': order.contactNameSnapshot || order.destinationNameSnapshot || '',
                        });
                    }
                });

                batch.update(orderDocRef, {
                    status: 'cancelled',
                    updatedAt: now,
                    closedAt: now,
                    pendingDeliveryQuantity: 0,
                    assignedQuantity: 0,
                    inTransitQuantity: 0,
                    deliveredQuantity: 0,
                    lines: (order.lines || []).map((line) => ({
                        ...line,
                        status: 'cancelled',
                        cancelledQuantity: Number(line.quantity || 0),
                        pendingDeliveryQuantity: 0,
                        updatedAt: now,
                    })),
                });
            } else {
                batch.delete(orderDocRef);
            }

            await batch.commit();
            if (editingOutboundOrderId === orderId) {
                handleCancelOutboundOrderEdition();
            }
            showMessage(order.status === 'ready_for_delivery' ? 'Pedido cancelado y liberado del stock comprometido.' : 'Pedido eliminado.', 'success');
        } catch (error) {
            showMessage(`No pude eliminar el pedido: ${error.message}`, 'error');
        }
    }, [companyOutboundOrdersPath, editingOutboundOrderId, handleCancelOutboundOrderEdition, products, resolvedCompanyId, showMessage, stockOutboundOrders, userId]);

    const handleCreateOutboundOrder = async () => {
        const destinationName = String(newOutboundOrderForm.destinationName || '').trim();
        const existingOrder = editingOutboundOrderId
            ? stockOutboundOrders.find((item) => item.id === editingOutboundOrderId) || null
            : null;

        const validLines = (newOutboundOrderForm.lines || [])
            .map((line) => {
                const product = products.find((item) => item.id === line.productId);
                const quantity = Math.max(0, Number(line.quantity || 0));
                const availableQty = getProductReadyStockQty(product);
                const latestPurchaseLine = latestPurchaseLineByProductId[String(line.productId || '')] || null;
                const unitCost = latestPurchaseLine
                    ? Number(latestPurchaseLine.unitCost || 0)
                    : Number(product?.defaultPurchaseCost || 0);
                const saleUnitPrice =
                    line.saleUnitPrice !== '' && line.saleUnitPrice != null
                        ? Number(line.saleUnitPrice || 0)
                        : Number(product?.defaultSalePrice || 0);
                return {
                    ...line,
                    product,
                    quantity,
                    availableQty,
                    sourcePurchaseId: String(latestPurchaseLine?.purchaseId || ''),
                    sourcePurchaseLineId: String(latestPurchaseLine?.id || ''),
                    sourcePurchaseNumberSnapshot: String(latestPurchaseLine?.purchaseNumberSnapshot || latestPurchaseLine?.purchaseId || ''),
                    sourceBatchLabelSnapshot: String(latestPurchaseLine?.batchLabelSnapshot || ''),
                    unitCost,
                    saleUnitPrice,
                    totalCost: quantity * unitCost,
                    totalSale: quantity * saleUnitPrice,
                    margin: quantity * saleUnitPrice - quantity * unitCost,
                };
            })
            .filter((line) => line.product && line.quantity > 0);

        if (!destinationName) {
            showMessage('Definí el destino para preparar el pedido de salida.', 'warning');
            return;
        }

        if (validLines.length === 0) {
            showMessage('Sumá al menos una línea válida al pedido de salida.', 'warning');
            return;
        }

        const previousReadyLinesByProductId = (existingOrder?.status === 'ready_for_delivery'
            ? (existingOrder.lines || []).filter((line) => line.status !== 'cancelled')
            : []
        ).reduce((acc, line) => {
            acc[String(line.productId || '')] = (acc[String(line.productId || '')] || 0) + Number(line.pendingDeliveryQuantity ?? line.quantity ?? 0);
            return acc;
        }, {});

        const invalidStockLine = validLines.find((line) => {
            const previouslyReserved = previousReadyLinesByProductId[String(line.product.id || '')] || 0;
            return line.quantity > line.availableQty + previouslyReserved;
        });

        if (invalidStockLine) {
            const previouslyReserved = previousReadyLinesByProductId[String(invalidStockLine.product.id || '')] || 0;
            const availableToEdit = invalidStockLine.availableQty + previouslyReserved;
            showMessage(
                `No podés preparar ${invalidStockLine.quantity} unidad${invalidStockLine.quantity !== 1 ? 'es' : ''} de ${invalidStockLine.product?.name || 'ese producto'}. Disponible para esta salida: ${availableToEdit}.`,
                'warning'
            );
            return;
        }

        try {
            const isEditing = Boolean(existingOrder);
            const orderRef = isEditing ? doc(db, `${companyOutboundOrdersPath}/${existingOrder.id}`) : doc(collection(db, companyOutboundOrdersPath));
            const batch = writeBatch(db);
            const now = new Date();
            const normalizedDestinationName = normalizeContactName(destinationName);
            let destinationContact = sharedContacts.find((contact) => contact.id === newOutboundOrderForm.selectedContactId)
                || sharedContacts.find((contact) => normalizeContactName(contact.name) === normalizedDestinationName)
                || null;
            let contactId = destinationContact?.id || null;
            let destinationAddressSnapshot = '';
            let destinationGeoSnapshot = null;

            if (!destinationContact) {
                if (!String(newOutboundOrderForm.destinationAddress || '').trim()) {
                    showMessage('Completá la dirección del destino para dejar el pedido listo para entregar.', 'warning');
                    return;
                }
                const coords = newOutboundOrderForm.destinationLatlng || await handleGeocodeOutboundDestination();
                if (!coords) {
                    showMessage('No pude validar la ubicación del destino para este pedido.', 'warning');
                    return;
                }
                const contactRef = doc(collection(db, companyContactsPath));
                contactId = contactRef.id;
                destinationAddressSnapshot = [
                    newOutboundOrderForm.destinationAddress,
                    newOutboundOrderForm.destinationApartment,
                    newOutboundOrderForm.destinationLocality,
                    newOutboundOrderForm.destinationPartido,
                    newOutboundOrderForm.destinationProvince,
                    newOutboundOrderForm.destinationPostalCode,
                ].filter(Boolean).join(', ');
                destinationGeoSnapshot = coords;
                batch.set(contactRef, {
                    companyId: resolvedCompanyId,
                    name: destinationName,
                    type: 'cliente',
                    addresses: [{
                        address: String(newOutboundOrderForm.destinationAddress || '').trim(),
                        apartment: String(newOutboundOrderForm.destinationApartment || '').trim(),
                        locality: String(newOutboundOrderForm.destinationLocality || '').trim(),
                        province: String(newOutboundOrderForm.destinationProvince || 'CABA').trim() || 'CABA',
                        partido: String(newOutboundOrderForm.destinationPartido || '').trim(),
                        postalCode: String(newOutboundOrderForm.destinationPostalCode || '').trim(),
                        latlng: coords,
                    }],
                    contacts: [],
                    createdAt: now,
                    updatedAt: now,
                });
            } else {
                const primaryAddress = getPrimaryContactAddress(destinationContact);
                destinationAddressSnapshot = [
                    primaryAddress?.address,
                    primaryAddress?.apartment,
                    primaryAddress?.locality,
                    primaryAddress?.partido,
                    primaryAddress?.province,
                    primaryAddress?.postalCode,
                ].filter(Boolean).join(', ');
                destinationGeoSnapshot = primaryAddress?.latlng || null;
            }

                const lines = validLines.map((line, index) => ({
                id: line.id,
                productId: line.product.id,
                productNameSnapshot: line.product.name,
                ...buildMovementProductSnapshotFields({ product: line.product }),
                barcodeSnapshot: line.product.barcode || '',
                qrCodeSnapshot: line.product.qrCode || '',
                unitSnapshot: line.product.unitBase || 'unidad',
                quantity: line.quantity,
                pendingDeliveryQuantity: line.quantity,
                assignedQuantity: 0,
                inTransitQuantity: 0,
                deliveredQuantity: 0,
                cancelledQuantity: 0,
                unitCost: line.unitCost > 0 ? line.unitCost : null,
                saleUnitPrice: line.saleUnitPrice > 0 ? line.saleUnitPrice : null,
                totalCost: line.totalCost > 0 ? line.totalCost : null,
                totalSale: line.totalSale > 0 ? line.totalSale : null,
                margin: line.margin,
                sourcePurchaseId: String(line.sourcePurchaseId || ''),
                sourcePurchaseLineId: String(line.sourcePurchaseLineId || ''),
                sourcePurchaseNumberSnapshot: String(line.sourcePurchaseNumberSnapshot || ''),
                sourceBatchLabelSnapshot: String(line.sourceBatchLabelSnapshot || ''),
                status: newOutboundOrderForm.status === 'ready_for_delivery' ? 'ready_for_delivery' : 'packing',
                lineOrder: index + 1,
                createdAt: isEditing && existingOrder?.createdAt ? existingOrder.createdAt : now,
                updatedAt: now,
            }));

            const totalCost = lines.reduce((acc, line) => acc + Number(line.totalCost || 0), 0);
            const totalSale = lines.reduce((acc, line) => acc + Number(line.totalSale || 0), 0);
            const totalMargin = totalSale - totalCost;

            if (existingOrder?.status === 'ready_for_delivery') {
                (existingOrder.lines || []).forEach((line) => {
                    const quantity = Number(line.pendingDeliveryQuantity ?? line.quantity ?? 0);
                    const product = products.find((item) => item.id === line.productId);
                    const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                    const productSnapshots = buildMovementProductSnapshotFields({ product, line });
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId: line.productId,
                        productNameSnapshot: line.productNameSnapshot || product?.name || 'Producto',
                        ...productSnapshots,
                        unitSnapshot: line.unitSnapshot || product?.unitBase || 'unidad',
                        movementType: 'stock_outbound_replanned',
                        quantity,
                        stockBucketsImpact: {
                            pendingInboundDelta: 0,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: -quantity,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'outbound_order',
                        sourceId: orderRef.id,
                        sourceLineId: line.id,
                        customerId: existingOrder.contactId || '',
                        customerNameSnapshot: existingOrder.contactNameSnapshot || existingOrder.destinationNameSnapshot || '',
                        operationType: String(existingOrder.operationType || 'sale').trim().toLowerCase() || 'sale',
                        referenceCodeSnapshot: String(existingOrder.reference || '').trim(),
                        note: 'Pedido de salida ajustado antes de entrar a logística.',
                        costUnitSnapshot: line.unitCost ?? null,
                        costTotalSnapshot: line.totalCost ?? null,
                        saleUnitSnapshot: line.saleUnitPrice ?? null,
                        saleTotalSnapshot: line.totalSale ?? null,
                        marginSnapshot: line.margin ?? null,
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: now,
                        appliedAt: now,
                        cancelledAt: null,
                    });
                    if (product) {
                        batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, line.productId)), {
                            updatedAt: now,
                            'stockBuckets.pendingOutboundQty': Math.max(0, Number(product?.stockBuckets?.pendingOutboundQty || 0) - quantity),
                            'stockSummary.lastMovementAt': now,
                            'stockSummary.lastMovementType': 'stock_outbound_replanned',
                            'stockSummary.lastOperationReason': String(existingOrder.operationType || 'sale').trim().toLowerCase() || 'sale',
                            'stockSummary.lastOperationType': String(existingOrder.operationType || 'sale').trim().toLowerCase() || 'sale',
                            'stockSummary.lastRouteCodeSnapshot': null,
                            'stockSummary.lastDestinationNameSnapshot': existingOrder.contactNameSnapshot || existingOrder.destinationNameSnapshot || '',
                        });
                    }
                });
            }

            batch.set(orderRef, {
                companyId: resolvedCompanyId,
                orderNumber: orderRef.id,
                orderLabel: String(newOutboundOrderForm.orderLabel || '').trim(),
                operationType: String(newOutboundOrderForm.operationType || 'sale').trim().toLowerCase() || 'sale',
                status: newOutboundOrderForm.status,
                reference: String(newOutboundOrderForm.reference || '').trim(),
                note: String(newOutboundOrderForm.notes || '').trim(),
                currency: newOutboundOrderForm.currency || 'ARS',
                contactId,
                contactNameSnapshot: destinationName,
                destinationNameSnapshot: destinationName,
                destinationAddressSnapshot,
                destinationGeoSnapshot,
                destinationSnapshot: {
                    address: String(newOutboundOrderForm.destinationAddress || '').trim(),
                    apartment: String(newOutboundOrderForm.destinationApartment || '').trim(),
                    locality: String(newOutboundOrderForm.destinationLocality || '').trim(),
                    partido: String(newOutboundOrderForm.destinationPartido || '').trim(),
                    province: String(newOutboundOrderForm.destinationProvince || 'CABA').trim() || 'CABA',
                    postalCode: String(newOutboundOrderForm.destinationPostalCode || '').trim(),
                },
                lines,
                totalQuantity: lines.reduce((acc, line) => acc + Number(line.quantity || 0), 0),
                totalCost,
                totalSale,
                totalMargin,
                pendingDeliveryQuantity: lines.reduce((acc, line) => acc + Number(line.pendingDeliveryQuantity || 0), 0),
                assignedQuantity: 0,
                inTransitQuantity: 0,
                deliveredQuantity: 0,
                createdByUserId: existingOrder?.createdByUserId || userId,
                createdByUserName: existingOrder?.createdByUserName || userId || 'Usuario',
                routeIds: existingOrder?.routeIds || [],
                createdAt: existingOrder?.createdAt || now,
                updatedAt: now,
                closedAt: null,
            }, { merge: true });

            if (newOutboundOrderForm.status === 'ready_for_delivery') {
                lines.forEach((line) => {
                    const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                    const product = products.find((item) => item.id === line.productId);
                    const productSnapshots = buildMovementProductSnapshotFields({ product, line });
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId: line.productId,
                        productNameSnapshot: line.productNameSnapshot,
                        ...productSnapshots,
                        unitSnapshot: line.unitSnapshot,
                        movementType: 'stock_outbound_prepared',
                        quantity: line.quantity,
                        stockBucketsImpact: {
                            pendingInboundDelta: 0,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: line.quantity,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'outbound_order',
                        sourceId: orderRef.id,
                        sourceLineId: line.id,
                        purchaseId: String(line.sourcePurchaseId || ''),
                        purchaseLineId: String(line.sourcePurchaseLineId || ''),
                        purchaseNumberSnapshot: String(line.sourcePurchaseNumberSnapshot || ''),
                        batchLabelSnapshot: String(line.sourceBatchLabelSnapshot || ''),
                        customerId: contactId || '',
                        customerNameSnapshot: destinationName,
                        operationType: String(newOutboundOrderForm.operationType || 'sale').trim().toLowerCase() || 'sale',
                        referenceCodeSnapshot: String(newOutboundOrderForm.reference || '').trim(),
                        note: 'Pedido de salida preparado y listo para que logística lo tome como pendiente de entrega.',
                        costUnitSnapshot: line.unitCost,
                        costTotalSnapshot: line.totalCost,
                        saleUnitSnapshot: line.saleUnitPrice,
                        saleTotalSnapshot: line.totalSale,
                        marginSnapshot: line.margin,
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: now,
                        appliedAt: now,
                        cancelledAt: null,
                    });
                    batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, line.productId)), {
                        updatedAt: now,
                        'stockBuckets.pendingOutboundQty': Number(product?.stockBuckets?.pendingOutboundQty || 0) + line.quantity,
                        'stockSummary.lastMovementAt': now,
                        'stockSummary.lastMovementType': 'stock_outbound_prepared',
                        'stockSummary.lastOperationReason': String(newOutboundOrderForm.operationType || 'sale').trim().toLowerCase() || 'sale',
                        'stockSummary.lastOperationType': String(newOutboundOrderForm.operationType || 'sale').trim().toLowerCase() || 'sale',
                        'stockSummary.lastRouteCodeSnapshot': null,
                        'stockSummary.lastDestinationNameSnapshot': destinationName,
                    });
                });
            }

            await batch.commit();
            addStockNotification(`${existingOrder ? 'Pedido de salida actualizado' : 'Pedido de salida creado'} para ${destinationName}.`);
            showMessage(
                newOutboundOrderForm.status === 'ready_for_delivery'
                    ? existingOrder
                        ? 'Pedido de salida actualizado. Sigue listo para logística.'
                        : 'Pedido de salida listo. Ya aparece como pendiente de entrega para logística.'
                    : existingOrder
                        ? 'Pedido de salida actualizado en preparación.'
                        : 'Pedido de salida guardado en preparación.',
                'success'
            );
            setEditingOutboundOrderId(null);
            setNewOutboundOrderForm(buildDefaultOutboundOrderForm());
        } catch (error) {
            showMessage(`No pude guardar el pedido de salida: ${error.message}`, 'error');
        }
    };

    const handleApproveWarrantyPickup = useCallback(async (orderId, outcome) => {
        const order = stockOutboundOrders.find((item) => item.id === orderId);
        if (!order) return;

        const normalizedOperationType = String(order.operationType || '').trim().toLowerCase();
        if (normalizedOperationType !== 'warranty') {
            showMessage('Este paso solo aplica a pedidos de garantía.', 'warning');
            return;
        }

        if (String(order.status || '').trim() !== 'in_warranty') {
            showMessage('El retiro solo se puede aprobar cuando el pedido ya está en garantía.', 'warning');
            return;
        }

        const outcomeLabel = {
            fixed: 'arreglada',
            not_repaired: 'no reparada',
            replaced_new: 'cambio por nuevo',
        }[outcome] || 'actualizada';

        try {
            const now = new Date();
            const batch = writeBatch(db);
            const orderRef = doc(db, `${companyOutboundOrdersPath}/${order.id}`);
            const nextLines = (order.lines || []).map((line) => ({
                ...line,
                status: 'ready_for_pickup',
                updatedAt: now,
            }));

            batch.update(orderRef, {
                lines: nextLines,
                status: 'ready_for_pickup',
                warrantyStatus: 'ready_for_pickup',
                warrantyOutcome: outcome,
                warrantyPickupApprovedAt: now,
                updatedAt: now,
                closedAt: null,
            });

            (order.lines || []).forEach((line) => {
                const quantity = Math.max(0, Number(line.quantity || 0));
                if (!line.productId || !quantity) return;

                const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                const product = products.find((item) => item.id === line.productId) || {};
                const productSnapshots = buildMovementProductSnapshotFields({ product, line });
                batch.set(movementRef, {
                    companyId: resolvedCompanyId,
                    productId: line.productId,
                    productNameSnapshot: line.productNameSnapshot || 'Producto',
                    ...productSnapshots,
                    unitSnapshot: line.unitSnapshot || 'unidad',
                    movementType: 'stock_warranty_pickup_ready',
                    quantity,
                    stockBucketsImpact: {
                        pendingInboundDelta: 0,
                        inboundTransitDelta: 0,
                        physicalDelta: 0,
                        pendingOutboundDelta: 0,
                        outboundTransitDelta: 0,
                        repairDelta: 0,
                        returnedDelta: 0,
                        damagedDelta: 0,
                    },
                    status: 'applied',
                    sourceType: 'outbound_order',
                    sourceId: order.id,
                    sourceLineId: line.id,
                    customerId: order.contactId || '',
                    customerNameSnapshot: order.contactNameSnapshot || order.destinationNameSnapshot || '',
                    operationType: normalizedOperationType,
                    operationReason: normalizedOperationType,
                    referenceCodeSnapshot: String(order.reference || '').trim(),
                    note: `Garantía lista para retiro. Resultado informado: ${outcomeLabel}.`,
                    costUnitSnapshot: line.unitCost ?? null,
                    costTotalSnapshot: line.totalCost ?? null,
                    saleUnitSnapshot: line.saleUnitPrice ?? null,
                    saleTotalSnapshot: line.totalSale ?? null,
                    marginSnapshot: line.margin ?? null,
                    createdByUserId: userId,
                    createdByUserName: userId || 'Usuario',
                    createdAt: now,
                    appliedAt: now,
                    cancelledAt: null,
                });

                batch.update(doc(db, resolveStockProductDocPath(resolvedCompanyId, line.productId)), {
                    updatedAt: now,
                    'stockSummary.lastMovementAt': now,
                    'stockSummary.lastMovementType': 'stock_warranty_pickup_ready',
                    'stockSummary.lastMovementStatus': 'applied',
                    'stockSummary.lastOperationReason': normalizedOperationType,
                    'stockSummary.lastOperationType': normalizedOperationType,
                    'stockSummary.lastRouteCodeSnapshot': null,
                    'stockSummary.lastDestinationNameSnapshot': order.contactNameSnapshot || order.destinationNameSnapshot || '',
                    'stockSummary.specialState': 'ready_for_pickup',
                    'stockSummary.specialStateQty': quantity,
                    'stockSummary.warrantyEnteredAt': order.warrantyEnteredAt || now,
                    'stockSummary.warrantyPickupApprovedAt': now,
                    'stockSummary.warrantyOutcome': outcome,
                });
            });

            await batch.commit();
            showMessage(`Garantía marcada como lista para retiro (${outcomeLabel}).`, 'success');
        } catch (error) {
            showMessage(`No pude aprobar el retiro de garantía: ${error.message}`, 'error');
        }
    }, [companyOutboundOrdersPath, resolvedCompanyId, showMessage, stockOutboundOrders, userId]);

    const handleCreatePurchase = async () => {
        const supplierName = newPurchaseForm.supplierName.trim();
        const existingPurchase = editingPurchaseId
            ? stockPurchases.find((item) => item.id === editingPurchaseId) || null
            : null;
        const pendingQuickCreateLines = (newPurchaseForm.lines || [])
            .map((line, index) => ({
                index,
                hasLinkedProduct: Boolean(String(line?.productId || '').trim()),
                quickProduct: line?.quickProduct || null,
            }))
            .filter(({ hasLinkedProduct, quickProduct }) => {
                if (hasLinkedProduct || !quickProduct) return false;
                return [
                    quickProduct.name,
                    quickProduct.category,
                    quickProduct.model,
                    quickProduct.brand,
                    quickProduct.featureLabel,
                    quickProduct.sku,
                    quickProduct.barcode,
                ].some((value) => String(value || '').trim());
            });
        const validLines = newPurchaseForm.lines
            .map((line) => ({
                ...line,
                product: products.find((product) => product.id === line.productId),
                quantityNumber: Number(line.quantity || 0),
                unitCostNumber: Number(line.unitCost || 0),
            }))
            .filter((line) => line.product && line.quantityNumber > 0);

        if (!supplierName) {
            showMessage('Definí el proveedor para registrar la compra.', 'warning');
            return;
        }
        if (pendingQuickCreateLines.length > 0) {
            const lineLabels = pendingQuickCreateLines
                .map(({ index, quickProduct }) => `línea ${index + 1}${quickProduct?.name ? ` (${quickProduct.name})` : ''}`)
                .join(', ');
            showMessage(
                `Antes de guardar la compra, terminá de crear y vincular el producto en ${lineLabels}. Tocá "Crear producto y usarlo en esta línea".`,
                'warning'
            );
            return;
        }
        if (validLines.length === 0) {
            showMessage('Sumá al menos una línea válida para registrar la compra pendiente.', 'warning');
            return;
        }

        try {
            const purchaseRef = existingPurchase
                ? doc(db, resolveStockPurchaseDocPath(resolvedCompanyId, existingPurchase.id))
                : doc(collection(db, companyPurchasesPath));
            const batch = writeBatch(db);
            const totalEstimatedCost = validLines.reduce((acc, line) => acc + (line.unitCostNumber > 0 ? line.unitCostNumber * line.quantityNumber : 0), 0);
            const normalizedSupplierName = normalizeContactName(supplierName);
            let supplierContact = stockContacts.find((contact) => contact.id === newPurchaseForm.selectedSupplierId)
                || stockContacts.find((contact) => normalizeContactName(contact.name) === normalizedSupplierName)
                || null;
            let supplierId = supplierContact?.id || null;
            let supplierAddressSnapshot = '';
            let supplierGeoSnapshot = null;

            if (!supplierContact) {
                if (!String(newPurchaseForm.supplierAddress || '').trim()) {
                    showMessage('Si el proveedor no existe todavía, cargá al menos la dirección para geolocalizarlo.', 'warning');
                    return;
                }
                const coords = newPurchaseForm.supplierLatlng || await handleGeocodePurchaseSupplier();
                if (!coords) {
                    showMessage('No pude validar la ubicación del proveedor nuevo.', 'warning');
                    return;
                }
                const supplierRef = doc(collection(db, companyContactsPath));
                supplierId = supplierRef.id;
                supplierAddressSnapshot = [
                    newPurchaseForm.supplierAddress,
                    newPurchaseForm.supplierApartment,
                    newPurchaseForm.supplierLocality,
                    newPurchaseForm.supplierPartido,
                    newPurchaseForm.supplierProvince,
                    newPurchaseForm.supplierPostalCode,
                ].filter(Boolean).join(', ');
                supplierGeoSnapshot = coords;
                batch.set(supplierRef, {
                    companyId: resolvedCompanyId,
                    name: supplierName,
                    type: 'proveedor',
                    addresses: [{
                        address: String(newPurchaseForm.supplierAddress || '').trim(),
                        apartment: String(newPurchaseForm.supplierApartment || '').trim(),
                        locality: String(newPurchaseForm.supplierLocality || '').trim(),
                        province: String(newPurchaseForm.supplierProvince || 'CABA').trim() || 'CABA',
                        partido: String(newPurchaseForm.supplierPartido || '').trim(),
                        postalCode: String(newPurchaseForm.supplierPostalCode || '').trim(),
                        latlng: coords,
                    }],
                    contacts: [],
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
            } else {
                const primaryAddress = getPrimaryContactAddress(supplierContact);
                supplierAddressSnapshot = [
                    primaryAddress?.address,
                    primaryAddress?.apartment,
                    primaryAddress?.locality,
                    primaryAddress?.partido,
                    primaryAddress?.province,
                    primaryAddress?.postalCode,
                ].filter(Boolean).join(', ');
                supplierGeoSnapshot = primaryAddress?.latlng || null;
            }

            const purchasePayload = {
                companyId: resolvedCompanyId,
                purchaseNumber: purchaseRef.id,
                sourceType: newPurchaseForm.sourceType,
                sourceReference: newPurchaseForm.sourceReference.trim(),
                supplierId,
                supplierNameSnapshot: supplierName,
                supplierAddressSnapshot,
                supplierGeoSnapshot,
                status: 'pending_pickup',
                purchaseDate: newPurchaseForm.purchaseDate,
                currency: newPurchaseForm.currency || 'ARS',
                totalEstimatedCost,
                note: newPurchaseForm.notes.trim(),
                createdByUserId: existingPurchase?.createdByUserId || userId,
                createdByUserName: existingPurchase?.createdByUserName || userId || 'Usuario',
                assignedRouteIds: [],
                pendingLinesCount: validLines.length,
                completedLinesCount: 0,
                createdAt: existingPurchase?.createdAt || new Date(),
                updatedAt: new Date(),
                closedAt: null,
            };

            if (existingPurchase) {
                batch.update(purchaseRef, purchasePayload);
            } else {
                batch.set(purchaseRef, purchasePayload);
            }

            const existingLines = existingPurchase
                ? stockPurchaseLines.filter((line) => line.purchaseId === existingPurchase.id)
                : [];
            const existingLinesById = new Map(existingLines.map((line) => [line.id, line]));
            const nextProductIds = new Set();

            validLines.forEach((line, index) => {
                const lineRef = line.id
                    ? doc(db, `${companyPurchaseLinesPath}/${line.id}`)
                    : doc(collection(db, companyPurchaseLinesPath));
                const previousLine = line.id ? existingLinesById.get(line.id) : null;
                const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                const totalCost = line.unitCostNumber > 0 ? line.unitCostNumber * line.quantityNumber : 0;
                const productSnapshots = buildMovementProductSnapshotFields({ product: line.product });
                const linePayload = {
                    companyId: resolvedCompanyId,
                    purchaseId: purchaseRef.id,
                    purchaseNumberSnapshot: purchaseRef.id,
                    productId: line.product.id,
                    productNameSnapshot: line.product.name,
                    ...productSnapshots,
                    barcodeSnapshot: line.product.barcode || '',
                    qrCodeSnapshot: line.product.qrCode || '',
                    unitSnapshot: line.product.unitBase || 'unidad',
                    orderedQuantity: line.quantityNumber,
                    assignedQuantity: 0,
                    pickedUpQuantity: 0,
                    receivedQuantity: 0,
                    cancelledQuantity: 0,
                    pendingPickupQuantity: line.quantityNumber,
                    unitCost: line.unitCostNumber > 0 ? line.unitCostNumber : null,
                    totalCost: totalCost > 0 ? totalCost : null,
                    status: 'pending_pickup',
                    supplierId,
                    supplierNameSnapshot: supplierName,
                    batchLabelSnapshot: buildPurchaseBatchLabel(purchaseRef.id, index + 1, lineRef.id),
                    lastRouteId: null,
                    lastRouteCodeSnapshot: null,
                    lastMovementId: previousLine?.lastMovementId || (!existingPurchase ? movementRef.id : null),
                    lastMovementStatus: 'applied',
                    note: '',
                    lineOrder: index + 1,
                    createdAt: previousLine?.createdAt || new Date(),
                    updatedAt: new Date(),
                    closedAt: null,
                };

                if (previousLine) {
                    batch.update(lineRef, linePayload);
                } else if (!existingPurchase) {
                    batch.set(lineRef, linePayload);
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId: line.product.id,
                        productNameSnapshot: line.product.name,
                        ...productSnapshots,
                        unitSnapshot: line.product.unitBase || 'unidad',
                        movementType: 'purchase_pending_created',
                        quantity: line.quantityNumber,
                        stockBucketsImpact: {
                            pendingInboundDelta: line.quantityNumber,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: 0,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'purchase',
                        sourceId: purchaseRef.id,
                        sourceLineId: lineRef.id,
                        purchaseId: purchaseRef.id,
                        purchaseLineId: lineRef.id,
                        purchaseNumberSnapshot: purchaseRef.id,
                        batchLabelSnapshot: buildPurchaseBatchLabel(purchaseRef.id, index + 1, lineRef.id),
                        supplierId,
                        supplierNameSnapshot: supplierName,
                        note: 'Nacimiento de compra pendiente de retiro.',
                        costUnitSnapshot: line.unitCostNumber > 0 ? line.unitCostNumber : null,
                        costTotalSnapshot: totalCost > 0 ? totalCost : null,
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: new Date(),
                        appliedAt: new Date(),
                        cancelledAt: null,
                    });
                } else {
                    batch.set(lineRef, linePayload);
                }
                nextProductIds.add(line.product.id);
                if (line.id) existingLinesById.delete(line.id);
            });

            if (existingPurchase) {
                const pendingInboundDiffByProduct = new Map();
                existingLines.forEach((line) => {
                    const productId = String(line.productId || '');
                    if (!productId) return;
                    pendingInboundDiffByProduct.set(productId, (pendingInboundDiffByProduct.get(productId) || 0) - Number(line.orderedQuantity || 0));
                });
                validLines.forEach((line) => {
                    const productId = String(line.product?.id || '');
                    if (!productId) return;
                    pendingInboundDiffByProduct.set(productId, (pendingInboundDiffByProduct.get(productId) || 0) + line.quantityNumber);
                });

                existingLinesById.forEach((line, lineId) => {
                    batch.delete(doc(db, `${companyPurchaseLinesPath}/${lineId}`));
                });

                pendingInboundDiffByProduct.forEach((delta, productId) => {
                    if (!delta) return;
                    const product = products.find((item) => item.id === productId);
                    if (!product) return;
                    const movementRef = doc(collection(db, resolveStockMovementsCollectionPath(resolvedCompanyId)));
                    const productSnapshots = buildMovementProductSnapshotFields({ product });
                    batch.update(doc(db, resolveProductDocPath(product)), {
                        updatedAt: new Date(),
                        'stockBuckets.pendingInboundQty': Math.max(0, Number(product?.stockBuckets?.pendingInboundQty || 0) + delta),
                        'stockSummary.lastMovementAt': new Date(),
                        'stockSummary.lastMovementType': 'purchase_pending_adjusted',
                        'stockSummary.lastOperationReason': null,
                        'stockSummary.lastOperationType': 'purchase',
                        'stockSummary.lastRouteCodeSnapshot': null,
                        'stockSummary.lastDestinationNameSnapshot': supplierName,
                    });
                    batch.set(movementRef, {
                        companyId: resolvedCompanyId,
                        productId,
                        productNameSnapshot: product.name || 'Producto',
                        ...productSnapshots,
                        unitSnapshot: product.unitBase || 'unidad',
                        movementType: 'purchase_pending_adjusted',
                        quantity: Math.abs(delta),
                        stockBucketsImpact: {
                            pendingInboundDelta: delta,
                            inboundTransitDelta: 0,
                            physicalDelta: 0,
                            pendingOutboundDelta: 0,
                            outboundTransitDelta: 0,
                            repairDelta: 0,
                            returnedDelta: 0,
                            damagedDelta: 0,
                        },
                        status: 'applied',
                        sourceType: 'purchase',
                        sourceId: purchaseRef.id,
                        purchaseId: purchaseRef.id,
                        purchaseNumberSnapshot: purchaseRef.id,
                        supplierId,
                        supplierNameSnapshot: supplierName,
                        note: 'Ajuste manual de compra pendiente antes de entrar a logística.',
                        createdByUserId: userId,
                        createdByUserName: userId || 'Usuario',
                        createdAt: new Date(),
                        appliedAt: new Date(),
                        cancelledAt: null,
                    });
                });
            } else {
                validLines.forEach((line) => {
                    batch.update(doc(db, resolveProductDocPath(line.product)), {
                        updatedAt: new Date(),
                        'stockBuckets.pendingInboundQty': (Number(line.product?.stockBuckets?.pendingInboundQty || 0) + line.quantityNumber),
                        'stockSummary.lastMovementAt': new Date(),
                        'stockSummary.lastMovementType': 'purchase_pending_created',
                        'stockSummary.lastOperationReason': null,
                        'stockSummary.lastOperationType': 'purchase',
                        'stockSummary.lastRouteCodeSnapshot': null,
                        'stockSummary.lastDestinationNameSnapshot': supplierName,
                    });
                });
            }

            await batch.commit();
            setEditingPurchaseId(null);
            setNewPurchaseForm(buildDefaultPurchaseForm());
            showMessage(existingPurchase ? 'Compra pendiente actualizada.' : 'Compra pendiente registrada.', 'success');
        } catch (error) {
            showMessage(`No pude ${existingPurchase ? 'actualizar' : 'registrar'} la compra: ${error.message}`, 'error');
        }
    };

    const handleAssignSheet = async (sheet, controllerId) => {
        if (!controllerId) {
            showMessage('Elegí un controlador antes de asignar la planilla.', 'warning');
            return;
        }
        const controller = stockControllers.find((item) => item.id === controllerId);
        if (!controller) {
            showMessage('No encontré el controlador seleccionado.', 'error');
            return;
        }
        try {
            const linesQuery = query(collection(db, companySheetLinesPath), where('sheetId', '==', sheet.id));
            const linesSnapshot = await getDocs(linesQuery);
            const batch = writeBatch(db);
            const displayName = [controller.firstName, controller.lastName].filter(Boolean).join(' ');
            batch.update(doc(db, resolveStockSheetDocPath(resolvedCompanyId, sheet.id)), {
                status: 'assigned',
                assignedControllerId: controller.id,
                assignedControllerNameSnapshot: controller.firstName || '',
                assignedControllerLastNameSnapshot: controller.lastName || '',
                assignedControllerDisplayNameSnapshot: displayName,
                assignedControllerPhotoURLSnapshot: String(controller.photoURL || '').trim(),
                assignedAt: new Date(),
                updatedAt: new Date(),
            });
            linesSnapshot.docs.forEach((lineDoc) => {
                const data = lineDoc.data();
                if (data.taskId) {
                    batch.update(doc(db, companyTasksPath, data.taskId), {
                        assignedControllerId: controller.id,
                        assignedControllerNameSnapshot: controller.firstName || '',
                        assignedControllerLastNameSnapshot: controller.lastName || '',
                        assignedControllerDisplayNameSnapshot: displayName,
                        assignedControllerPhotoURLSnapshot: String(controller.photoURL || '').trim(),
                        sheetId: sheet.id,
                        sheetTitleSnapshot: sheet.title,
                        sheetStatus: 'assigned',
                        updatedAt: new Date(),
                    });
                }
            });
            await batch.commit();
            showMessage(`Planilla asignada a ${displayName}.`, 'success');
        } catch (error) {
            showMessage(`No pude asignar la planilla: ${error.message}`, 'error');
        }
    };

    const handleReleaseSheet = async (sheet) => {
        try {
            const linesQuery = query(collection(db, companySheetLinesPath), where('sheetId', '==', sheet.id));
            const linesSnapshot = await getDocs(linesQuery);
            const batch = writeBatch(db);
            batch.update(doc(db, resolveStockSheetDocPath(resolvedCompanyId, sheet.id)), {
                status: 'waiting_assignment',
                assignedControllerId: null,
                assignedControllerNameSnapshot: null,
                assignedControllerLastNameSnapshot: null,
                assignedControllerDisplayNameSnapshot: null,
                assignedControllerPhotoURLSnapshot: null,
                assignedAt: null,
                updatedAt: new Date(),
            });
            linesSnapshot.docs.forEach((lineDoc) => {
                const data = lineDoc.data();
                if (data.taskId) {
                    batch.update(doc(db, companyTasksPath, data.taskId), {
                        assignedControllerId: null,
                        assignedControllerNameSnapshot: null,
                        assignedControllerLastNameSnapshot: null,
                        assignedControllerDisplayNameSnapshot: null,
                        assignedControllerPhotoURLSnapshot: null,
                        sheetId: sheet.id,
                        sheetTitleSnapshot: sheet.title,
                        sheetStatus: 'waiting_assignment',
                        updatedAt: new Date(),
                    });
                }
            });
            await batch.commit();
            showMessage('Planilla liberada y vuelta a espera de asignación.', 'success');
        } catch (error) {
            showMessage(`No pude liberar la planilla: ${error.message}`, 'error');
        }
    };

    const handleAdvanceSheetStatus = async (sheet, nextStatus) => {
        if (nextStatus === 'completed') {
            showMessage('Para completar una planilla, abrila y finalizala desde adentro con los conteos cargados.', 'warning');
            return;
        }
        if (nextStatus === 'reviewed' && !['completed', 'reviewed'].includes(String(sheet?.status || '').trim())) {
            showMessage('Primero completá la planilla antes de marcarla como revisada.', 'warning');
            return;
        }
        try {
            await updateDoc(doc(db, resolveStockSheetDocPath(resolvedCompanyId, sheet.id)), {
                status: nextStatus,
                updatedAt: new Date(),
                ...(nextStatus === 'in_progress' ? { startedAt: new Date() } : {}),
                ...(nextStatus === 'completed' ? { completedAt: new Date() } : {}),
            });
            showMessage('Estado de planilla actualizado.', 'success');
        } catch (error) {
            showMessage(`No pude cambiar el estado: ${error.message}`, 'error');
        }
    };

    const handleOpenSheetPreview = useCallback(async (sheet, options = {}) => {
        if (!sheet?.id) return;
        const nextMode = options?.mode === 'readonly-modal' ? 'readonly-modal' : 'panel';
        try {
            const linesQuery = query(collection(db, companySheetLinesPath), where('sheetId', '==', sheet.id));
            const linesSnapshot = await getDocs(linesQuery);
            const normalizedLines = linesSnapshot.docs
                .map((lineDoc) => ({ id: lineDoc.id, ...lineDoc.data() }))
                .sort((a, b) => Number(a.lineOrder || 0) - Number(b.lineOrder || 0))
                .map((line) => {
                    const product = products.find((item) => item.id === line.productId) || null;
                    return normalizeSheetPreviewLine(line, product);
                });

            setActiveSheetPreview(sheet);
            setSheetPreviewMode(nextMode);
            setActiveSheetPreviewLines(normalizedLines);
            setSheetPreviewMeta({
                generalObservation: String(sheet.generalObservation || '').trim(),
                generalTaggedLineIds: Array.isArray(sheet.generalTaggedLineIds) ? sheet.generalTaggedLineIds : [],
            });
            setSheetPreviewSearch('');
            setSheetPreviewFilter('all');
            setShowCategoryHistoryModal(false);
            setShowProgramEditModal(false);
            if (nextMode === 'panel') {
                setShowDayDetailsModal(false);
                setActivePanel('sheets');
            } else {
                setShowDayDetailsModal(false);
            }
        } catch (error) {
            showMessage(`No pude abrir la planilla: ${error.message}`, 'error');
        }
    }, [companySheetLinesPath, db, products, setActivePanel, showMessage]);

    const handlePreviewLineChange = useCallback((lineId, updates = {}) => {
        setActiveSheetPreviewLines((prev) => prev.map((line) => {
            if (line.id !== lineId) return line;
            const countedQuantity = Object.prototype.hasOwnProperty.call(updates, 'countedQuantity')
                ? (updates.countedQuantity === '' ? null : updates.countedQuantity)
                : line.countedQuantity;
            const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity);
            const differenceValue = Object.prototype.hasOwnProperty.call(updates, 'differenceValue')
                ? updates.differenceValue
                : (countedQuantity == null || expectedQuantity == null ? null : Number(countedQuantity) - expectedQuantity);
            const nextStatus = updates.status || (countedQuantity == null ? 'pending' : differenceValue === 0 ? 'checked_ok' : 'checked_diff');
            const nextCountedLabel = Object.prototype.hasOwnProperty.call(updates, 'countedLabel')
                ? updates.countedLabel
                : (countedQuantity == null ? '-' : `${countedQuantity}`);
            const nextReadingLabel = Object.prototype.hasOwnProperty.call(updates, 'readingLabel')
                ? updates.readingLabel
                : (
                    countedQuantity == null
                        ? 'Todavía sin conteo cargado'
                        : differenceValue === 0
                            ? 'Coincide con el sistema'
                            : 'Hay diferencia para revisar'
                );
            return {
                ...line,
                ...updates,
                countedQuantity,
                differenceValue,
                countedLabel: nextCountedLabel,
                readingLabel: nextReadingLabel,
                status: nextStatus,
            };
        }));
    }, []);

    const handleSheetPreviewMetaChange = useCallback((updates = {}) => {
        setSheetPreviewMeta((prev) => ({
            ...prev,
            ...updates,
        }));
    }, []);

    const persistActiveSheetPreview = useCallback(async ({ finalize = false } = {}) => {
        if (!activeSheetPreview?.id) return false;
        if (finalize && activeSheetPreview.status === 'completed') {
            showMessage('Esta planilla ya estaba finalizada.', 'info');
            return false;
        }
        setSheetPreviewSaving(true);
        try {
            const batch = writeBatch(db);
            const now = new Date();
            const normalizedForWrite = activeSheetPreviewLines.map((line) => {
                const countedQuantity = line.countedQuantity == null || line.countedQuantity === '' ? null : Number(line.countedQuantity);
                const expectedQuantity = line.expectedQuantity == null ? null : Number(line.expectedQuantity);
                const differenceQuantity = countedQuantity == null || expectedQuantity == null ? null : countedQuantity - expectedQuantity;
                const status = countedQuantity == null ? 'pending' : differenceQuantity === 0 ? 'checked_ok' : 'checked_diff';
                return {
                    ...line,
                    countedQuantity,
                    differenceQuantity,
                    status,
                    noteLabel: String(line.noteLabel || '').trim() || 'Sin observación',
                };
            });

            if (finalize) {
                const missingReasonLines = normalizedForWrite.filter((line) => (
                    line.status === 'checked_diff' &&
                    (!String(line.noteLabel || '').trim() || String(line.noteLabel || '').trim() === 'Sin observación')
                ));
                if (missingReasonLines.length > 0) {
                    showMessage(`Antes de finalizar, completá el motivo de la diferencia en ${missingReasonLines.length} fila${missingReasonLines.length === 1 ? '' : 's'}.`, 'warning');
                    return false;
                }
            }

            normalizedForWrite.forEach((line) => {
                batch.update(doc(db, companySheetLinesPath, line.id), {
                    countedQuantity: line.countedQuantity,
                    differenceQuantity: line.differenceQuantity,
                    observation: line.noteLabel === 'Sin observación' ? '' : line.noteLabel,
                    status: line.status,
                    reviewedAt: line.countedQuantity == null ? null : now,
                    updatedAt: now,
                });
            });

            const completedLines = normalizedForWrite.filter((line) => line.status === 'checked_ok' || line.status === 'checked_diff').length;
            const diffLines = normalizedForWrite.filter((line) => line.status === 'checked_diff').length;
            if (finalize && completedLines === 0) {
                showMessage('No podés finalizar una planilla sin registrar al menos un conteo real.', 'warning');
                return false;
            }
            batch.update(doc(db, resolveStockSheetDocPath(resolvedCompanyId, activeSheetPreview.id)), {
                status: finalize ? 'completed' : completedLines > 0 ? 'in_progress' : (activeSheetPreview.status || 'assigned'),
                reviewedLinesCount: completedLines,
                differenceLinesCount: diffLines,
                generalObservation: String(sheetPreviewMeta.generalObservation || '').trim(),
                generalTaggedLineIds: Array.isArray(sheetPreviewMeta.generalTaggedLineIds) ? sheetPreviewMeta.generalTaggedLineIds : [],
                completedAt: finalize ? now : activeSheetPreview.completedAt || null,
                startedAt: completedLines > 0 ? (activeSheetPreview.startedAt || now) : activeSheetPreview.startedAt || null,
                updatedAt: now,
            });

            if (finalize) {
                normalizedForWrite
                    .filter((line) => line.productId && line.countedQuantity != null)
                    .forEach((line) => {
                        const controlRef = doc(collection(db, companyControlsPath));
                        batch.set(controlRef, {
                            startTime: activeSheetPreview.createdAt || activeSheetPreview.startedAt || now,
                            endTime: now,
                            countDetails: JSON.stringify([{
                                id: line.id,
                                name: `${line.productName}${line.codeLabel && line.codeLabel !== 'Sin variante visible' ? ` · ${line.codeLabel.replace(/^SKU\s+/i, '')}` : ''}`,
                                stockSystem: line.expectedQuantity ?? 0,
                                stockPhysical: line.countedQuantity,
                                difference: line.differenceQuantity ?? 0,
                                observation: line.noteLabel === 'Sin observación' ? '' : line.noteLabel,
                            }]),
                            type: 'sheet_line',
                            productId: line.productId,
                            productName: line.productName,
                            companyId: resolvedCompanyId,
                            controllerId: activeSheetPreview.assignedControllerId || null,
                            controllerNameSnapshot: activeSheetPreview.assignedControllerNameSnapshot || null,
                            controllerLastNameSnapshot: activeSheetPreview.assignedControllerLastNameSnapshot || null,
                            controllerDisplayNameSnapshot: activeSheetPreview.assignedControllerDisplayNameSnapshot || null,
                            controllerPhotoURLSnapshot: activeSheetPreview.assignedControllerPhotoURLSnapshot || null,
                            sheetId: activeSheetPreview.id,
                            sheetTitleSnapshot: activeSheetPreview.title || 'Planilla',
                            sheetGeneralObservationSnapshot: String(sheetPreviewMeta.generalObservation || '').trim(),
                            sheetTaggedLineIdsSnapshot: Array.isArray(sheetPreviewMeta.generalTaggedLineIds) ? sheetPreviewMeta.generalTaggedLineIds : [],
                            completedByUserId: activeSheetPreview.assignedControllerId || userId || null,
                            sourceType: activeSheetPreview.sourceType || 'category_control',
                            sourceCategorySnapshot: activeSheetPreview.sourceCategorySnapshot || '',
                            createdAt: now,
                        });
                    });
            }

            await batch.commit();
            setActiveSheetPreviewLines(normalizedForWrite.map((line) => ({
                ...line,
                countedLabel: line.countedQuantity == null ? '-' : `${line.countedQuantity}`,
                differenceValue: line.differenceQuantity,
                readingLabel: line.countedQuantity == null ? 'Todavía sin conteo cargado' : line.differenceQuantity === 0 ? 'Coincide con el sistema' : 'Hay diferencia para revisar',
            })));
            setActiveSheetPreview((prev) => prev ? ({
                ...prev,
                status: finalize ? 'completed' : completedLines > 0 ? 'in_progress' : prev.status,
                completedAt: finalize ? now : prev.completedAt,
                startedAt: completedLines > 0 ? (prev.startedAt || now) : prev.startedAt,
                generalObservation: String(sheetPreviewMeta.generalObservation || '').trim(),
                generalTaggedLineIds: Array.isArray(sheetPreviewMeta.generalTaggedLineIds) ? sheetPreviewMeta.generalTaggedLineIds : [],
            }) : prev);
            showMessage(finalize ? 'Planilla finalizada y guardada en el historial.' : 'Planilla guardada.', 'success');
            return true;
        } catch (error) {
            showMessage(`No pude guardar la planilla: ${error.message}`, 'error');
            return false;
        } finally {
            setSheetPreviewSaving(false);
        }
    }, [activeSheetPreview, activeSheetPreviewLines, companyControlsPath, companySheetLinesPath, db, resolvedCompanyId, sheetPreviewMeta.generalObservation, sheetPreviewMeta.generalTaggedLineIds, showMessage, userId]);

    const handleSaveSheetPreview = useCallback(async () => {
        await persistActiveSheetPreview({ finalize: false });
    }, [persistActiveSheetPreview]);

    const handleFinalizeSheetPreview = useCallback(async () => {
        await persistActiveSheetPreview({ finalize: true });
    }, [persistActiveSheetPreview]);

    const handleCloseSheetPreview = useCallback(() => {
        setActiveSheetPreview(null);
        setActiveSheetPreviewLines([]);
        setSheetPreviewMode('panel');
        setSheetPreviewMeta({
            generalObservation: '',
            generalTaggedLineIds: [],
        });
        setSheetPreviewSearch('');
        setSheetPreviewFilter('all');
        setSheetPreviewSaving(false);
    }, []);

    const handleOpenSheetAdjustment = useCallback(() => {
        if (!activeSheetPreview?.id) {
            showMessage('Primero abrí una planilla cerrada para registrar el ajuste.', 'warning');
            return;
        }
        const differenceLines = activeSheetPreviewLines.filter((line) => line.productId && Number(line.differenceValue || 0) !== 0);
        if (differenceLines.length === 0) {
            showMessage('Esta planilla no tiene diferencias pendientes para ajustar.', 'info');
            return;
        }
        const taggedIds = Array.isArray(sheetPreviewMeta.generalTaggedLineIds) ? sheetPreviewMeta.generalTaggedLineIds : [];
        const selectedLineIds = taggedIds.filter((lineId) => differenceLines.some((line) => line.id === lineId));
        setSheetAdjustmentDraft({
            sheetId: activeSheetPreview.id,
            sheetTitle: activeSheetPreview.title || 'Planilla',
            categoryLabel: activeSheetPreview.sourceCategorySnapshot || '',
            reason: '',
            note: '',
            selectedLineIds: selectedLineIds.length > 0 ? selectedLineIds : differenceLines.map((line) => line.id),
            lines: differenceLines,
        });
        setShowSheetAdjustmentModal(true);
    }, [activeSheetPreview, activeSheetPreviewLines, sheetPreviewMeta.generalTaggedLineIds, showMessage]);

    const handleToggleAdjustmentLine = useCallback((lineId) => {
        setSheetAdjustmentDraft((prev) => {
            const nextSelected = prev.selectedLineIds.includes(lineId)
                ? prev.selectedLineIds.filter((id) => id !== lineId)
                : [...prev.selectedLineIds, lineId];
            return {
                ...prev,
                selectedLineIds: nextSelected,
            };
        });
    }, []);

    const handleApplySheetAdjustment = useCallback(async () => {
        const reason = String(sheetAdjustmentDraft.reason || '').trim();
        const note = String(sheetAdjustmentDraft.note || '').trim();
        const selectedLineIds = Array.isArray(sheetAdjustmentDraft.selectedLineIds) ? sheetAdjustmentDraft.selectedLineIds : [];
        if (!activeSheetPreview?.id) {
            showMessage('No encontré la planilla cerrada para asentar el ajuste.', 'error');
            return;
        }
        if (!reason) {
            showMessage('Antes de registrar el ajuste, dejá claro por qué se ajusta.', 'warning');
            return;
        }
        const selectedLines = (sheetAdjustmentDraft.lines || []).filter((line) => selectedLineIds.includes(line.id));
        if (selectedLines.length === 0) {
            showMessage('Elegí al menos una fila con diferencia para registrar el ajuste.', 'warning');
            return;
        }

        try {
            const batch = writeBatch(db);
            const now = new Date();
            const adjustmentEvents = [];
            const updatedProductMap = new Map();

            selectedLines.forEach((line) => {
                const differenceValue = Number(line.differenceValue || 0);
                if (!differenceValue || !line.product?.id) return;
                const product = line.product;
                const currentBuckets = product.stockBuckets || {};
                const currentSummary = product.stockSummary || {};
                const previousPhysicalQty = Math.max(0, Number(currentBuckets.physicalQty || 0));
                const nextPhysicalQty = Math.max(0, previousPhysicalQty + differenceValue);
                const pendingOutboundQty = Math.max(0, Number(currentBuckets.pendingOutboundQty || 0));
                const specialQty = Math.max(
                    0,
                    Number(currentBuckets.repairQty || 0),
                    Number(currentBuckets.damagedQty || 0),
                    Number(currentBuckets.returnedQty || 0)
                );
                const nextAvailableQty = Math.max(0, nextPhysicalQty - pendingOutboundQty - specialQty);
                const productRef = doc(db, resolveProductDocPath(product));

                batch.update(productRef, {
                    'stockBuckets.physicalQty': nextPhysicalQty,
                    stockSummary: {
                        ...currentSummary,
                        availableQty: nextAvailableQty,
                        lastMovementAt: now,
                        lastMovementType: 'stock_adjustment',
                        lastOperationType: 'stock_adjustment',
                        lastOperationReason: 'control_adjustment',
                        lastControlAt: now,
                        lastControlledById: userId || activeSheetPreview.assignedControllerId || null,
                        lastControlledByName: activeSheetPreview.assignedControllerDisplayNameSnapshot || activeSheetPreview.assignedControllerNameSnapshot || 'Ajuste manual',
                    },
                    updatedAt: now,
                });

                updatedProductMap.set(product.id, {
                    ...product,
                    stockBuckets: {
                        ...currentBuckets,
                        physicalQty: nextPhysicalQty,
                    },
                    stockSummary: {
                        ...currentSummary,
                        availableQty: nextAvailableQty,
                        lastMovementAt: now,
                        lastMovementType: 'stock_adjustment',
                        lastOperationType: 'stock_adjustment',
                        lastOperationReason: 'control_adjustment',
                        lastControlAt: now,
                        lastControlledById: userId || activeSheetPreview.assignedControllerId || null,
                        lastControlledByName: activeSheetPreview.assignedControllerDisplayNameSnapshot || activeSheetPreview.assignedControllerNameSnapshot || 'Ajuste manual',
                    },
                    updatedAt: now,
                });

                const lineDocRef = doc(db, companySheetLinesPath, line.id);
                batch.update(lineDocRef, {
                    adjustmentStatus: 'applied',
                    adjustmentDelta: differenceValue,
                    adjustmentReason: reason,
                    adjustmentNote: note,
                    adjustedAt: now,
                    adjustedByUserId: userId || null,
                });

                const movementRef = doc(collection(db, companyMovementsPath));
                const productSnapshots = buildMovementProductSnapshotFields({
                    product,
                    line: {
                        productNameSnapshot: line.productName,
                        productCategorySnapshot: line.categoryLabel,
                        productVariantSnapshot: line.codeLabel || '',
                    },
                });
                batch.set(movementRef, {
                    companyId: resolvedCompanyId,
                    movementType: 'stock_adjustment',
                    quantity: Math.abs(differenceValue),
                    deltaQuantity: differenceValue,
                    unitSnapshot: product.unitBase || 'unidad',
                    productId: product.id,
                    productNameSnapshot: line.productName,
                    ...productSnapshots,
                    operationReason: 'control_adjustment',
                    adjustmentReasonSnapshot: reason,
                    adjustmentNoteSnapshot: note,
                    previousPhysicalQty,
                    adjustedPhysicalQty: nextPhysicalQty,
                    sheetId: activeSheetPreview.id,
                    sheetTitleSnapshot: activeSheetPreview.title || 'Planilla',
                    sourceType: 'category_control_adjustment',
                    sourceCategorySnapshot: activeSheetPreview.sourceCategorySnapshot || '',
                    controllerId: activeSheetPreview.assignedControllerId || null,
                    controllerDisplayNameSnapshot: activeSheetPreview.assignedControllerDisplayNameSnapshot || activeSheetPreview.assignedControllerNameSnapshot || null,
                    controllerPhotoURLSnapshot: activeSheetPreview.assignedControllerPhotoURLSnapshot || null,
                    appliedAt: now,
                    createdAt: now,
                });

                const controlRef = doc(collection(db, companyControlsPath));
                const controlEvent = {
                    id: controlRef.id,
                    startTime: now,
                    endTime: now,
                    countDetails: JSON.stringify([{
                        id: line.id,
                        name: `${line.productName}${line.codeLabel && line.codeLabel !== 'Sin variante visible' ? ` · ${line.codeLabel.replace(/^SKU\s+/i, '')}` : ''}`,
                        stockSystem: line.expectedQuantity ?? 0,
                        stockPhysical: line.countedQuantity ?? 0,
                        difference: differenceValue,
                        observation: note || reason,
                    }]),
                    type: 'stock_adjustment',
                    productId: line.productId,
                    productName: line.productName,
                    companyId: resolvedCompanyId,
                    controllerId: activeSheetPreview.assignedControllerId || null,
                    controllerNameSnapshot: activeSheetPreview.assignedControllerNameSnapshot || null,
                    controllerLastNameSnapshot: activeSheetPreview.assignedControllerLastNameSnapshot || null,
                    controllerDisplayNameSnapshot: activeSheetPreview.assignedControllerDisplayNameSnapshot || null,
                    controllerPhotoURLSnapshot: activeSheetPreview.assignedControllerPhotoURLSnapshot || null,
                    sheetId: activeSheetPreview.id,
                    sheetTitleSnapshot: activeSheetPreview.title || 'Planilla',
                    sourceType: 'category_control_adjustment',
                    sourceCategorySnapshot: activeSheetPreview.sourceCategorySnapshot || '',
                    adjustmentReasonSnapshot: reason,
                    adjustmentNoteSnapshot: note,
                    adjustmentDelta: differenceValue,
                    adjustedByUserId: userId || null,
                    adjustedByName: activeSheetPreview.assignedControllerDisplayNameSnapshot || activeSheetPreview.assignedControllerNameSnapshot || 'Ajuste manual',
                    createdAt: now,
                };
                batch.set(controlRef, controlEvent);
                adjustmentEvents.push(controlEvent);
            });

            if (adjustmentEvents.length === 0) {
                showMessage('No encontré filas válidas para ajustar en esta planilla.', 'warning');
                return;
            }

            batch.update(doc(db, resolveStockSheetDocPath(resolvedCompanyId, activeSheetPreview.id)), {
                adjustmentsCount: Number(activeSheetPreview.adjustmentsCount || 0) + adjustmentEvents.length,
                lastAdjustmentAt: now,
                lastAdjustmentByUserId: userId || null,
                lastAdjustmentReason: reason,
                updatedAt: now,
            });

            await batch.commit();

            setProducts((prev) => prev.map((product) => updatedProductMap.get(product.id) || product));
            setAllHistory((prev) => [...adjustmentEvents, ...prev]);
            setActiveSheetPreview((prev) => prev ? ({
                ...prev,
                adjustmentsCount: Number(prev.adjustmentsCount || 0) + adjustmentEvents.length,
                lastAdjustmentAt: now,
                lastAdjustmentReason: reason,
            }) : prev);
            setActiveSheetPreviewLines((prev) => prev.map((line) => (
                selectedLineIds.includes(line.id)
                    ? {
                        ...line,
                        product: updatedProductMap.get(line.product?.id) || line.product,
                        adjustmentStatus: 'applied',
                        adjustmentDelta: Number(line.differenceValue || 0),
                        adjustmentReason: reason,
                        adjustmentNote: note,
                        adjustedAt: now,
                    }
                    : line
            )));
            setShowSheetAdjustmentModal(false);
            showMessage(`Asenté ${adjustmentEvents.length} ajuste${adjustmentEvents.length === 1 ? '' : 's'} y quedó registrado para el próximo ciclo.`, 'success');
        } catch (error) {
            showMessage(`No pude registrar el ajuste: ${error.message}`, 'error');
        }
    }, [activeSheetPreview, companyControlsPath, companyMovementsPath, companySheetLinesPath, db, resolvedCompanyId, resolveProductDocPath, sheetAdjustmentDraft, showMessage, userId]);

    const filteredSheetPreviewLines = useMemo(() => {
        const search = String(sheetPreviewSearch || '').trim().toLowerCase();
        return activeSheetPreviewLines.filter((line) => {
            if (sheetPreviewFilter !== 'all' && line.status !== sheetPreviewFilter) return false;
            if (!search) return true;
            const haystack = [
                line.productName,
                line.codeLabel,
                line.categoryLabel,
            ].join(' ').toLowerCase();
            return haystack.includes(search);
        });
    }, [activeSheetPreviewLines, sheetPreviewFilter, sheetPreviewSearch]);

    const getCategoryControlAvailableQty = useCallback((product = {}) => {
        const physicalQty = Math.max(0, Number(product?.stockBuckets?.physicalQty || 0));
        const pendingOutboundQty = Math.max(0, Number(product?.stockBuckets?.pendingOutboundQty || 0));
        const specialQty = Math.max(
            0,
            Number(product?.stockSummary?.specialStateQty || 0),
            Number(product?.stockBuckets?.repairQty || 0),
            Number(product?._traceWarrantyQty || 0)
        );
        return Math.max(0, physicalQty - pendingOutboundQty - specialQty);
    }, []);

    const handleStartCategoryControl = useCallback((categoryLabel, categoryProducts = []) => {
        const normalizedCategory = String(categoryLabel || '').trim();
        const validProducts = (categoryProducts || [])
            .map((product) => ({
                ...product,
                availableForControl: getCategoryControlAvailableQty(product),
            }))
            .filter((product) => product.availableForControl > 0);

        if (!normalizedCategory) {
            showMessage('Primero elegí una categoría para armar la planilla.', 'warning');
            return;
        }

        if (validProducts.length === 0) {
            showMessage(`No encontré productos con stock listo para controlar en ${normalizedCategory}.`, 'warning');
            return;
        }

        setCategoryControlDraft({
            ...buildEmptyCategoryControlDraft(),
            categoryLabel: normalizedCategory,
            products: validProducts,
        });
        setShowCategoryControlModal(true);
    }, [getCategoryControlAvailableQty, showMessage]);

    const handleConfirmCategoryControl = useCallback(async () => {
        const normalizedCategory = String(categoryControlDraft?.categoryLabel || '').trim();
        const validProducts = Array.isArray(categoryControlDraft?.products)
            ? categoryControlDraft.products.filter((product) => Number(product?.availableForControl || 0) > 0)
            : [];
        const selectedDate = String(categoryControlDraft?.firstDate || '').trim() || new Date().toISOString().slice(0, 10);
        const selectedFrequency = String(categoryControlDraft?.frequency || 'one_time').trim() || 'one_time';
        const draftNotes = String(categoryControlDraft?.notes || '').trim();

        if (!normalizedCategory) {
            showMessage('Primero elegí una categoría para armar la planilla.', 'warning');
            return;
        }

        if (validProducts.length === 0) {
            showMessage(`No encontré productos con stock listo para controlar en ${normalizedCategory}.`, 'warning');
            return;
        }

        if (hasCategoryControlScheduledForDay(normalizedCategory, selectedDate)) {
            showMessage(`Ya hay un control de ${normalizedCategory} programado para ${new Date(`${selectedDate}T00:00:00`).toLocaleDateString('es-AR')}.`, 'warning');
            return;
        }

        try {
            const now = new Date();
            const sheetRef = doc(collection(db, companySheetsPath));
            const batch = writeBatch(db);
            const title = `Control ${normalizedCategory} · ${new Date(`${selectedDate}T00:00:00`).toLocaleDateString('es-AR')}`;
            const frequencyLabel = getCategoryControlFrequencyLabel(selectedFrequency);
            const notes = draftNotes || `Planilla generada desde Productos para la categoría ${normalizedCategory}.`;
            const createdSheetRecord = {
                id: sheetRef.id,
                companyId: resolvedCompanyId,
                title,
                sourceType: 'category_control',
                sourceCategorySnapshot: normalizedCategory,
                sourceSummarySnapshot: `Control por categoría: ${normalizedCategory}`,
                status: 'waiting_assignment',
                sheetDate: selectedDate,
                programFrequency: selectedFrequency,
                programFrequencyLabel: frequencyLabel,
                programStartDate: selectedDate,
                programStatus: 'scheduled',
                programSourcePanel: 'products',
                notes,
                createdAt: now,
                createdByUserId: userId,
                linesCount: validProducts.length,
                assignedControllerId: null,
                assignedControllerNameSnapshot: null,
                assignedControllerLastNameSnapshot: null,
                assignedControllerDisplayNameSnapshot: null,
                assignedControllerPhotoURLSnapshot: null,
                assignedAt: null,
                startedAt: null,
                completedAt: null,
            };

            batch.set(sheetRef, createdSheetRecord);

            validProducts.forEach((product, index) => {
                const lineRef = doc(collection(db, companySheetLinesPath));
                batch.set(lineRef, {
                    companyId: resolvedCompanyId,
                    sheetId: sheetRef.id,
                    sheetTitleSnapshot: title,
                    taskId: null,
                    sourceTaskStatusSnapshot: null,
                    productId: product.id,
                    productNameSnapshot: product.name || 'Producto',
                    productCategorySnapshot: product.category || '',
                    productModelSnapshot: product.model || '',
                    productBrandSnapshot: product.brand || '',
                    productFeatureSnapshot: product.featureLabel || '',
                    productVariantSnapshot: buildProductVariantSnapshot(product),
                    productSkuSnapshot: product.sku || '',
                    expectedQuantity: product.availableForControl,
                    countedQuantity: null,
                    differenceQuantity: null,
                    status: 'pending',
                    lineOrder: index + 1,
                    originType: 'category_control',
                    createdAt: now,
                });
            });

            await batch.commit();
            setStockSheets((prev) => {
                const nextSheets = [
                    normalizeStockSheetRecord(createdSheetRecord),
                    ...prev.filter((sheet) => sheet.id !== createdSheetRecord.id).map((sheet) => normalizeStockSheetRecord(sheet)),
                ];
                return nextSheets.sort((a, b) => {
                    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
                    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
                    return bTime - aTime;
                });
            });
            setShowCategoryControlModal(false);
            setCategoryControlDraft(buildEmptyCategoryControlDraft());
            setActivePanel('sheets');
            const focusDate = new Date(`${selectedDate}T12:00:00`);
            handleDateSelect(focusDate, { openDetails: false });
            showMessage(
                `Armé el control de ${normalizedCategory} para ${new Date(`${selectedDate}T00:00:00`).toLocaleDateString('es-AR')}. Ya quedó en Control físico y también agendado en Programación.`,
                'success'
            );
        } catch (error) {
            showMessage(`No pude armar el control por categoría: ${error.message}`, 'error');
        }
    }, [categoryControlDraft, companySheetLinesPath, companySheetsPath, db, handleDateSelect, hasCategoryControlScheduledForDay, resolvedCompanyId, setActivePanel, showMessage, userId]);

    const handleStartControl = async (productId, task = null) => {
        const productToStart = products.find(p => p.id === productId);
        if (!productToStart) {
            showMessage("Producto no encontrado.", "error");
            return;
        }
        if (productToStart.controlStatus?.status === 'in_control') {
            showMessage(`"${productToStart.name}" ya está siendo controlado por otro usuario.`, "warning");
            return;
        }
        playSound('start');
        try {
            await updateDoc(doc(db, resolveProductDocPath(productToStart)), { controlStatus: { status: 'in_control', by: userId } });
            setActiveControlProduct(productToStart);
            setActiveCalendarTask(task);
            setShowStockCountModal(true);
            setShowDayDetailsModal(false);
        } catch (error) {
            showMessage("Error al intentar bloquear el producto.", "error");
        }
    };
    
    const handleShowHistory = (product) => {
        setActiveSheetPreview(null);
        setActiveSheetPreviewLines([]);
        setSheetPreviewMode('panel');
        setSheetPreviewMeta({
            generalObservation: '',
            generalTaggedLineIds: [],
        });
        setSheetPreviewSearch('');
        setSheetPreviewFilter('all');
        setSheetPreviewSaving(false);
        setShowCategoryHistoryModal(false);
        setShowProgramEditModal(false);
        setActiveHistoryProduct(product);
        setShowHistoryListModal(true);
        setShowDayDetailsModal(false);
    };

    const handleOpenProductCycle = (product) => {
        setProductForHistory(product);
        setShowProductHistoryModal(true);
        setShowDayDetailsModal(false);
    };

    const handleCountCompleted = async (productId) => {
        playSound('complete');
        try {
            const productToUnlock = products.find(p => p.id === productId);
            if (productToUnlock) {
                await updateDoc(doc(db, resolveProductDocPath(productToUnlock)), { controlStatus: { status: 'available' } });
            }
            addStockNotification(`Control de ${products.find(p=>p.id === productId)?.name} finalizado.`);
        } catch (error) {
            console.error("Error al desbloquear el producto:", error);
        }
        setShowStockCountModal(false);
        setActiveControlProduct(null);
        setActiveCalendarTask(null);
    };
    
    const handleCloseCountModal = async () => {
        if (activeControlProduct) {
            try {
                await updateDoc(doc(db, resolveProductDocPath(activeControlProduct)), { controlStatus: { status: 'available' } });
            } catch (error) {
                console.error("Error al desbloquear el producto al cancelar:", error);
            }
        }
        setShowStockCountModal(false);
        setActiveControlProduct(null);
        setActiveCalendarTask(null);
    };

    const handleRemoveTask = async (taskId) => {
        const taskToRemove = scheduledTasks.find(task => task.id === taskId);
        if (!taskToRemove) return;
        try {
            await deleteDoc(doc(db, resolveTaskDocPath(taskToRemove)));
            setSelectedDayTasks(prev => prev.filter(t => t.id !== taskId));
            showMessage("Tarea eliminada.", "success");
        } catch (error) { showMessage(`Error: ${error.message}`, 'error'); }
    };

    const handleMobileNav = (offset) => {
        const newDate = new Date(mobileVisibleDay);
        newDate.setDate(newDate.getDate() + offset);
        setMobileVisibleDay(newDate);
    };
    
    const DayCard = ({ day, isMobile, getDayStatus, selectedDayDate, ...props }) => {
        const dayStr = formatDate(day);
        const isToday = dayStr === formatDate(new Date());
        const isPast = !isToday && isPastDay(day);
        const isPulsing = props.pulsingDate === dayStr;
        const isSelected = selectedDayDate ? dayStr === formatDate(selectedDayDate) : false;
        const dayStatus = getDayStatus(day);
        const visiblePlannerEntries = plannerEntries.filter((entry) => entry.date === dayStr).slice(0, isMobile ? 6 : 4);
    
        let dayClass = '';
        if (isToday) dayClass = 'bg-blue-500 text-white font-bold relative ring-4 ring-blue-300 ring-offset-2 animate-glow';
        else if (isPast) dayClass = 'bg-stone-200 text-stone-400 cursor-not-allowed opacity-70';
        else dayClass = 'bg-white hover:bg-blue-50';
        if (isSelected && !isToday) dayClass += ' ring-2 ring-cyan-400 ring-offset-2 shadow-[0_0_0_3px_rgba(34,211,238,0.15)] bg-cyan-50';
    
        if (props.dragOverDate === dayStr) dayClass = 'border-purple-400 bg-purple-100 ring-2 ring-purple-300';
    
        const longPressEvents = useLongPress(() => handleDayClick(day), () => handleDayClick(day));
    
        return (
            <div
                key={dayStr}
                onDragEnter={() => !isPast && props.setDragOverDate(dayStr)}
                onDragLeave={() => props.setDragOverDate(null)}
                onDragOver={(e) => !isPast && e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleDrop(JSON.parse(e.dataTransfer.getData('text/plain')), day); props.setDragOverDate(null); }}
                {...longPressEvents}
                className={`relative flex flex-col p-2 rounded-xl border border-stone-200 transition-all duration-300 shadow-lg hover:shadow-xl active:bg-slate-200 ${dayClass} ${isMobile ? 'h-full' : ''} ${isPulsing ? 'animate-pulse-bright' : ''}`}
            >
                {dayStatus === 'perfect' && (
                    <div className="absolute top-1 right-1 text-green-500">
                        <Icon path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="w-5 h-5" />
                    </div>
                )}
                {dayStatus === 'differences' && (
                     <div className="absolute top-1 right-1 text-yellow-500">
                        <Icon path="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" className="w-5 h-5" />
                    </div>
                )}
                <div className={`text-center font-bold mb-2 text-sm md:text-base ${isPast ? 'text-stone-400' : 'text-stone-700'} ${isToday ? 'text-white' : ''}`}>
                    {day.toLocaleDateString('es-AR', { weekday: 'short' })} <span className={isToday ? 'text-blue-200' : 'text-stone-400'}>{day.getDate()}</span>
                </div>
                <div className="flex-grow space-y-1 p-1">
                    {visiblePlannerEntries.map((entry) => {
                        if (entry.entryType === 'sheet' || entry.entryType === 'sheet_program') {
                            const isPaused = entry.programStatus === 'paused';
                            const isProjectedProgram = entry.entryType === 'sheet_program';
                            const badgeTone = isPaused
                                ? 'bg-amber-100 text-amber-800'
                                : isProjectedProgram
                                    ? 'bg-cyan-100 text-cyan-800'
                                    : entry.status === 'completed'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-violet-100 text-violet-800';
                            return (
                                <div key={entry.id} className={`p-2 rounded-md text-xs truncate font-semibold cursor-pointer shadow-inner ${badgeTone}`}>
                                    {isProjectedProgram
                                        ? `${entry.sourceCategorySnapshot || entry.title} · recurrente`
                                        : `${entry.sourceCategorySnapshot || entry.title}${entry.assignedByName ? ` · ${entry.assignedByName}` : ''}`}
                                </div>
                            );
                        }

                        const product = products.find(p => p.id === entry.productId);
                        const inControl = product?.controlStatus?.status === 'in_control';
                        return (
                            <div key={entry.id} className={`p-2 rounded-md text-xs truncate font-semibold cursor-pointer shadow-inner ${inControl ? 'bg-yellow-200 text-yellow-800' : entry.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                {inControl ? 'EN CONTROL' : entry.productName}
                            </div>
                        );
                    })}
                    {plannerEntries.filter((entry) => entry.date === dayStr).length > visiblePlannerEntries.length ? (
                        <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                            +{plannerEntries.filter((entry) => entry.date === dayStr).length - visiblePlannerEntries.length} más
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };
    
    const executiveStats = useMemo(() => {
        // 1. Compliance Rate
        const startOfWeek = weekDays[0];
        const endOfWeek = weekDays[6];
        const weeklyScheduled = scheduledTasks.filter(t => {
            const taskDate = new Date(t.date + 'T00:00:00');
            return taskDate >= startOfWeek && taskDate <= endOfWeek;
        });
        const weeklyCompleted = weeklyScheduled.filter(t => t.status === 'completed');
        const complianceRate = weeklyScheduled.length > 0 ? (weeklyCompleted.length / weeklyScheduled.length) * 100 : 100;

        // 2. Top Differences
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const differences = allHistory
            .filter(h => h.endTime && h.endTime.toDate() > thirtyDaysAgo)
            .flatMap(h => {
                try {
                    const details = JSON.parse(h.countDetails);
                    return details.map(item => ({ ...item, productName: h.productName }));
                } catch { return []; }
            })
            .filter(item => typeof item.difference === 'number' && item.difference !== 0);

        const productDifferences = differences.reduce((acc, item) => {
            acc[item.productName] = (acc[item.productName] || 0) + Math.abs(item.difference);
            return acc;
        }, {});

        const topDifferences = Object.entries(productDifferences)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([name, value]) => ({ name, diferencia: value }));

        // 3. Upcoming Tasks
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcomingTasks = scheduledTasks
            .filter(t => t.status === 'pending' && new Date(t.date + 'T00:00:00') >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(0, 3);

        // 4. Neglected Products
        const lastControlDates = allHistory.reduce((acc, h) => {
            if (h.endTime) {
                acc[h.productId] = acc[h.productId] ? Math.max(acc[h.productId], h.endTime.toDate().getTime()) : h.endTime.toDate().getTime();
            }
            return acc;
        }, {});

        const neglectedProducts = products
            .filter(p => {
                const lastControl = lastControlDates[p.id];
                if (!lastControl) return true; // Never controlled
                return (today.getTime() - lastControl) / (1000 * 3600 * 24) > 30; // Not controlled in 30 days
            })
            .slice(0, 3);

        return { complianceRate, topDifferences, upcomingTasks, neglectedProducts };
    }, [products, scheduledTasks, allHistory, weekDays]);


    const stockHeroStats = useMemo(() => ({
        products: products.length,
        pendingTasks: scheduledTasks.filter((task) => task.status === 'pending').length,
        controllers: stockControllers.filter((item) => item.active !== false).length,
        waitingSheets: stockSheets.filter((item) => normalizeStockSheetStatus(item) === 'waiting_assignment').length,
        assignedSheets: stockSheets.filter((item) => normalizeStockSheetStatus(item) === 'assigned').length,
        pendingPurchases: stockPurchases.filter((item) => item.status === 'pending_pickup' || item.status === 'partially_assigned' || item.status === 'assigned_to_route').length,
        pendingOutboundOrders: stockOutboundOrders.filter((item) => item.status === 'ready_for_delivery' || item.status === 'partially_assigned' || item.status === 'assigned_to_route').length,
    }), [products, scheduledTasks, stockControllers, stockSheets, stockPurchases, stockOutboundOrders]);

    const isMovementsFocus = activePanel === 'movements';
    const isStockNavCollapsed = isMovementsFocus && !stockNavExpandedInMovements;
    const stockNavDesktopWidthClass = isStockNavCollapsed ? 'lg:max-w-[108px]' : 'lg:max-w-sm';
    const stockNavDesktopLabelClass = isStockNavCollapsed ? 'lg:hidden' : '';
    const stockNavDesktopArrowClass = isStockNavCollapsed ? 'lg:hidden' : '';
    const stockNavDesktopButtonClass = isStockNavCollapsed ? 'lg:justify-center lg:px-0' : '';
    const stockNavDesktopRowClass = isStockNavCollapsed ? 'lg:justify-center' : '';
    const [movementsStickyTop, setMovementsStickyTop] = useState(0);
    const [stockSidebarPinnedLayout, setStockSidebarPinnedLayout] = useState({
        active: false,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    });

    useEffect(() => {
        if (!isMovementsFocus) {
            setMovementsStickyTop(0);
            setStockSidebarPinnedLayout((prev) => (prev.active ? { active: false, top: 0, left: 0, width: 0, height: 0 } : prev));
            return undefined;
        }

        const scrollEl = stockScrollContainerRef.current;

        const updatePinnedLayout = () => {
            const companyHeaderRect = document.getElementById('company-header')?.getBoundingClientRect();
            const sidebarRect = stockSidebarSlotRef.current?.getBoundingClientRect();
            const companyHeaderBottom = companyHeaderRect?.bottom || 0;
            const stickyTop = companyHeaderBottom + 4;
            const scrollTop = Math.max(Number(scrollEl?.scrollTop || 0), Number(window.scrollY || 0));
            const shouldPin = movementsHeaderCondensed || scrollTop > 28;

            setMovementsStickyTop(stickyTop);

            if (!sidebarRect || window.innerWidth < 1024 || !shouldPin) {
                setStockSidebarPinnedLayout((prev) => (prev.active ? { active: false, top: 0, left: 0, width: 0, height: 0 } : prev));
                return;
            }

            setStockSidebarPinnedLayout({
                active: true,
                top: stickyTop,
                left: sidebarRect.left,
                width: sidebarRect.width,
                height: sidebarRect.height,
            });
        };

        updatePinnedLayout();
        if (scrollEl?.addEventListener) {
            scrollEl.addEventListener('scroll', updatePinnedLayout, { passive: true });
        }
        window.addEventListener('scroll', updatePinnedLayout, { passive: true });
        window.addEventListener('resize', updatePinnedLayout);

        return () => {
            if (scrollEl?.removeEventListener) {
                scrollEl.removeEventListener('scroll', updatePinnedLayout);
            }
            window.removeEventListener('scroll', updatePinnedLayout);
            window.removeEventListener('resize', updatePinnedLayout);
        };
    }, [isMovementsFocus, stockNavExpandedInMovements, activePanel, movementsHeaderCondensed]);

    const latestPurchaseLineByProductId = useMemo(() => {
        const grouped = {};
        (stockPurchaseLines || []).forEach((line) => {
            const productId = String(line?.productId || '');
            if (!productId) return;
            const currentTime = Math.max(
                Number(line?.updatedAt?.seconds || 0) * 1000,
                Number(line?.createdAt?.seconds || 0) * 1000,
                line?.updatedAt instanceof Date ? line.updatedAt.getTime() : 0,
                line?.createdAt instanceof Date ? line.createdAt.getTime() : 0,
                Number.isNaN(new Date(line?.updatedAt).getTime()) ? 0 : new Date(line?.updatedAt).getTime(),
                Number.isNaN(new Date(line?.createdAt).getTime()) ? 0 : new Date(line?.createdAt).getTime()
            );
            const previous = grouped[productId];
            const previousTime = previous?._sortTime || 0;
            if (!previous || currentTime >= previousTime) {
                grouped[productId] = {
                    ...line,
                    _sortTime: currentTime,
                };
            }
        });
        return grouped;
    }, [stockPurchaseLines]);

    const stockBalanceStats = useMemo(() => {
        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const productCatalogById = (products || []).reduce((acc, product) => {
            const productId = String(product?.id || '').trim();
            if (productId) acc[productId] = product;
            return acc;
        }, {});

        const buildProductCommercialLabel = (productId = '', fallback = {}) => {
            const product = productCatalogById[String(productId || '').trim()] || {};
            const category = String(
                product?.category ||
                fallback?.categorySnapshot ||
                fallback?.category ||
                fallback?.productName ||
                'Producto'
            ).trim();
            const parts = [
                product?.model,
                product?.brand,
                product?.featureLabel,
                product?.inches ? `${product.inches} pulgadas` : '',
                product?.sku,
                fallback?.sku,
            ]
                .map((value) => String(value || '').trim())
                .filter(Boolean);
            const meta = [...new Set(parts)].join(' · ');
            return meta ? `${category} · ${meta}` : category;
        };

        const getMovementTime = (movement = {}) => {
            const sources = [movement.appliedAt, movement.createdAt, movement.updatedAt];
            for (const source of sources) {
                if (!source) continue;
                if (typeof source?.toDate === 'function') {
                    try {
                        return source.toDate().getTime();
                    } catch {
                        continue;
                    }
                }
                const parsed = new Date(source);
                if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
            }
            return 0;
        };

        const latestPurchaseCostByProductId = {};
        const latestSalePriceByProductId = {};
        const purchaseLineById = {};

        (stockPurchaseLines || []).forEach((line) => {
            const productId = String(line?.productId || '').trim();
            const lineId = String(line?.id || '').trim();
            if (lineId) {
                purchaseLineById[lineId] = line;
            }
            if (!productId) return;
            const quantity = Number(line?.orderedQuantity || 0);
            const unitCost = Number(line?.unitCost || 0);
            if (quantity <= 0 || unitCost <= 0) return;

            const currentTime = Math.max(
                Number(line?.updatedAt?.seconds || 0) * 1000,
                Number(line?.createdAt?.seconds || 0) * 1000,
                line?.updatedAt instanceof Date ? line.updatedAt.getTime() : 0,
                line?.createdAt instanceof Date ? line.createdAt.getTime() : 0,
                Number.isNaN(new Date(line?.updatedAt).getTime()) ? 0 : new Date(line?.updatedAt).getTime(),
                Number.isNaN(new Date(line?.createdAt).getTime()) ? 0 : new Date(line?.createdAt).getTime()
            );

            const previous = latestPurchaseCostByProductId[productId];
            if (!previous || currentTime >= previous.timestamp) {
                latestPurchaseCostByProductId[productId] = {
                    unitCost,
                    timestamp: currentTime,
                };
            }
        });

        (stockMovements || []).forEach((movement) => {
            const productId = String(movement?.productId || '').trim();
            if (!productId) return;
            const timestamp = getMovementTime(movement);

            if (movement.movementType === 'purchase_received') {
                const unitCost = Number(movement.costUnitSnapshot || movement.unitCostSnapshot || movement.unitCost || 0);
                if (unitCost > 0) {
                    const previous = latestPurchaseCostByProductId[productId];
                    if (!previous || timestamp >= previous.timestamp) {
                        latestPurchaseCostByProductId[productId] = { unitCost, timestamp };
                    }
                }
            }

            if (['stock_outbound_prepared', 'stock_outbound_assigned_to_route', 'stock_outbound_delivered'].includes(String(movement.movementType || ''))) {
                const saleUnit = Number(movement.saleUnitSnapshot || 0);
                if (saleUnit > 0) {
                    const previous = latestSalePriceByProductId[productId];
                    if (!previous || timestamp >= previous.timestamp) {
                        latestSalePriceByProductId[productId] = { saleUnit, timestamp };
                    }
                }
            }
        });

        const totals = products.reduce((acc, product) => {
            const productId = String(product?.id || '').trim();
            const buckets = product?.stockBuckets || {};
            const physicalQty = Math.max(0, Number(buckets.physicalQty || 0));
            const pendingInboundQty = Math.max(0, Number(buckets.pendingInboundQty || 0));
            const inboundTransitQty = Math.max(0, Number(buckets.inboundTransitQty || 0));
            const pendingOutboundQty = Math.max(0, Number(buckets.pendingOutboundQty || 0));
            const outboundTransitQty = Math.max(0, Number(buckets.outboundTransitQty || 0));
            const repairQty = Math.max(0, Number(buckets.repairQty || 0));
            const returnedQty = Math.max(0, Number(buckets.returnedQty || 0));
            const damagedQty = Math.max(0, Number(buckets.damagedQty || 0));
            const specialQty = repairQty + returnedQty + damagedQty;

            const resolvedCost =
                Number(latestPurchaseCostByProductId[productId]?.unitCost || 0) ||
                Number(product?.defaultPurchaseCost || 0);
            const resolvedSale =
                Number(product?.defaultSalePrice || 0) ||
                Number(latestSalePriceByProductId[productId]?.saleUnit || 0);

            acc.totalStoredUnits += physicalQty;
            acc.totalPendingInboundUnits += pendingInboundQty + inboundTransitQty;
            acc.totalPendingOutboundUnits += pendingOutboundQty + outboundTransitQty;
            acc.totalSpecialUnits += specialQty;

            if (physicalQty > 0 && resolvedCost <= 0) {
                acc.productsWithMissingCost += 1;
            } else {
                acc.currentCapitalValue += physicalQty * resolvedCost;
            }

            if (physicalQty > 0 && resolvedSale <= 0) {
                acc.productsWithMissingSalePrice += 1;
            } else {
                acc.currentPotentialSaleValue += physicalQty * resolvedSale;
            }

            if (physicalQty > 0 && resolvedCost > 0 && resolvedSale > 0) {
                acc.currentPotentialMargin += physicalQty * (resolvedSale - resolvedCost);
            }

            return acc;
        }, {
            totalStoredUnits: 0,
            totalPendingInboundUnits: 0,
            totalPendingOutboundUnits: 0,
            totalSpecialUnits: 0,
            currentCapitalValue: 0,
            currentPotentialSaleValue: 0,
            currentPotentialMargin: 0,
            productsWithMissingCost: 0,
            productsWithMissingSalePrice: 0,
        });

        const periodMovements = (stockMovements || []).filter((movement) => {
            const timestamp = getMovementTime(movement);
            return timestamp >= thirtyDaysAgo.getTime();
        });

        const realized = periodMovements.reduce((acc, movement) => {
            const quantity = Number(movement.quantity || 0);
            const sourcePurchaseLine = purchaseLineById[String(movement?.sourceLineId || movement?.purchaseLineId || '')] || null;
            const fallbackPurchaseUnitCost = Number(sourcePurchaseLine?.unitCost || 0);
            const fallbackPurchaseCostTotal = fallbackPurchaseUnitCost > 0 ? fallbackPurchaseUnitCost * quantity : 0;
            const costUnitSnapshot = Number(movement.costUnitSnapshot || movement.unitCostSnapshot || movement.unitCost || 0);
            const saleUnit = Number(movement.saleUnitSnapshot || 0);
            const resolvedCostUnit = costUnitSnapshot > 0 ? costUnitSnapshot : fallbackPurchaseUnitCost;
            const rawCostTotal = Number(movement.costTotalSnapshot || 0);
            const costTotal = rawCostTotal > 0 ? rawCostTotal : resolvedCostUnit * quantity;
            const rawSaleTotal = Number(movement.saleTotalSnapshot || 0);
            const saleTotal = rawSaleTotal > 0 ? rawSaleTotal : saleUnit * quantity;
            const rawMargin = Number(movement.marginSnapshot || 0);
            const margin = rawMargin !== 0 ? rawMargin : (saleTotal - costTotal);
            const productId = String(movement?.productId || '').trim();
            const productLabel = buildProductCommercialLabel(productId, movement);
            const supplierLabel = String(
                movement?.supplierNameSnapshot ||
                movement?.supplierName ||
                sourcePurchaseLine?.supplierName ||
                sourcePurchaseLine?.supplier ||
                'Sin proveedor'
            ).trim();

            if (String(movement?.movementType || '') === 'purchase_received') {
                const spendValue = costTotal > 0 ? costTotal : fallbackPurchaseCostTotal;
                acc.purchaseUnits += quantity;
                acc.purchaseSpend += spendValue;
                if (!acc.purchaseBySupplier[supplierLabel]) {
                    acc.purchaseBySupplier[supplierLabel] = { supplier: supplierLabel, spend: 0, units: 0, operations: 0 };
                }
                acc.purchaseBySupplier[supplierLabel].spend += spendValue;
                acc.purchaseBySupplier[supplierLabel].units += quantity;
                acc.purchaseBySupplier[supplierLabel].operations += 1;
            }

            if (String(movement?.movementType || '') === 'stock_outbound_delivered') {
                acc.realizedUnits += quantity;
                acc.realizedCost += costTotal;
                acc.realizedSales += saleTotal;
                acc.realizedMargin += margin;
                if (!acc.salesByProduct[productLabel]) {
                    acc.salesByProduct[productLabel] = { product: productLabel, units: 0, sales: 0, margin: 0, deliveries: 0 };
                }
                acc.salesByProduct[productLabel].units += quantity;
                acc.salesByProduct[productLabel].sales += saleTotal;
                acc.salesByProduct[productLabel].margin += margin;
                acc.salesByProduct[productLabel].deliveries += 1;
            }

            return acc;
        }, {
            purchaseUnits: 0,
            purchaseSpend: 0,
            realizedUnits: 0,
            realizedCost: 0,
            realizedSales: 0,
            realizedMargin: 0,
            purchaseBySupplier: {},
            salesByProduct: {},
        });

        const salesRows = Object.values(realized.salesByProduct || {});
        const supplierRows = Object.values(realized.purchaseBySupplier || {}).map((item) => ({
            ...item,
            avgUnitCost: item.units > 0 ? item.spend / item.units : 0,
        }));

        const topSellingProducts = [...salesRows]
            .sort((a, b) => b.units - a.units || b.sales - a.sales)
            .slice(0, 5);
        const lowSellingProducts = [...salesRows]
            .sort((a, b) => a.units - b.units || a.sales - b.sales)
            .slice(0, 5);
        const topMarginProducts = [...salesRows]
            .sort((a, b) => b.margin - a.margin || b.sales - a.sales)
            .slice(0, 5);
        const lowMarginProducts = [...salesRows]
            .sort((a, b) => a.margin - b.margin || a.sales - b.sales)
            .slice(0, 5);
        const topSuppliers = [...supplierRows]
            .sort((a, b) => b.spend - a.spend || b.units - a.units)
            .slice(0, 5);
        const bestPriceSuppliers = [...supplierRows]
            .filter((item) => item.units > 0)
            .sort((a, b) => a.avgUnitCost - b.avgUnitCost || b.units - a.units)
            .slice(0, 5);
        const worstPriceSuppliers = [...supplierRows]
            .filter((item) => item.units > 0)
            .sort((a, b) => b.avgUnitCost - a.avgUnitCost || b.units - a.units)
            .slice(0, 5);

        return {
            ...totals,
            ...realized,
            periodMovements,
            topSellingProducts,
            lowSellingProducts,
            topMarginProducts,
            lowMarginProducts,
            topSuppliers,
            bestPriceSuppliers,
            worstPriceSuppliers,
            periodLabel: 'Últimos 30 días',
        };
    }, [products, stockMovements, stockPurchaseLines]);

    const renderPlannerContent = () => {
        const weeklyCompleted = weekDays.reduce((acc, day) => {
            const dayString = formatDate(day);
            return acc + plannerEntries.filter((entry) => entry.date === dayString && entry.status === 'completed').length;
        }, 0);
        const weeklyWithDiffs = weekDays.reduce((acc, day) => {
            const dayStatus = getDayStatus(day);
            return acc + (dayStatus === 'differences' ? 1 : 0);
        }, 0);

        return (
            <div className="flex-grow bg-white rounded-2xl shadow-2xl p-4 md:p-6 flex flex-col border bg-gradient-to-br from-slate-50 via-gray-50 to-blue-100">
                <div className="mb-4 flex flex-col gap-3 rounded-[24px] border border-cyan-200 bg-[linear-gradient(135deg,rgba(255,255,255,0.98)_0%,rgba(236,254,255,0.95)_100%)] px-4 py-4 shadow-sm md:flex-row md:items-center md:justify-between">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600">Programación de control</p>
                        <h2 className="mt-1 text-2xl font-bold text-slate-900">Agenda del depósito</h2>
                        <p className="mt-1 text-sm text-slate-600">Desde acá ves qué controles están programados, qué quedó pendiente y qué ya se realizó.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:w-[320px]">
                        <div className="rounded-[18px] border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Completados</p>
                            <p className="mt-1 text-lg font-bold leading-none text-slate-900">{weeklyCompleted}</p>
                        </div>
                        <div className="rounded-[18px] border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Con diferencias</p>
                            <p className="mt-1 text-lg font-bold leading-none text-slate-900">{weeklyWithDiffs}</p>
                        </div>
                    </div>
                </div>

                <div ref={weeklyPlannerRef} className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2 scroll-mt-4">
                    <h3 className="text-xl font-bold text-stone-900 mb-2 md:mb-0">Programación semanal</h3>
                    <div className="flex items-center gap-2">
                        <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); handleDateSelect(d); }} className="p-2 rounded-md hover:bg-stone-100 transition-colors"><Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="w-5 h-5" /></button>
                        <span className="text-lg font-semibold text-stone-600 w-48 text-center">{weekDays[0].toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</span>
                        <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); handleDateSelect(d); }} className="p-2 rounded-md hover:bg-stone-100 transition-colors"><Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" className="w-5 h-5" /></button>
                    </div>
                </div>
                <div className="text-center text-sm text-stone-500 mb-4">
                    <span>Esta semana: <b>{weeklyCompleted}</b> controles completados, <b>{weeklyWithDiffs}</b> con diferencias.</span>
                </div>
                
                <div className={`hidden md:grid grid-cols-7 gap-2 flex-grow ${weekAnimation}`}>
                    {weekDays.map((day) => <DayCard key={formatDate(day)} day={day} pulsingDate={pulsingDate} dragOverDate={dragOverDate} setDragOverDate={setDragOverDate} getDayStatus={getDayStatus} selectedDayDate={selectedDayDate} />)}
                </div>

                <div className="block md:hidden flex-grow flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <button onClick={() => handleMobileNav(-1)} className="p-2 rounded-full hover:bg-stone-200 transition-colors">
                            <Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="w-5 h-5"/>
                        </button>
                        <span className="font-semibold text-stone-700">{mobileVisibleDay.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                        <button onClick={() => handleMobileNav(1)} className="p-2 rounded-full hover:bg-stone-200 transition-colors">
                            <Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" className="w-5 h-5"/>
                        </button>
                    </div>
                    <div className="flex-grow">
                        <DayCard day={mobileVisibleDay} isMobile={true} pulsingDate={pulsingDate} dragOverDate={dragOverDate} setDragOverDate={setDragOverDate} getDayStatus={getDayStatus} selectedDayDate={selectedDayDate} />
                    </div>
                </div>

                <div className="mt-4 text-center">
                    <button 
                        onClick={() => setShowMonthlyCalendar(!showMonthlyCalendar)}
                        className="bg-slate-200 text-slate-700 font-semibold py-2 px-4 rounded-lg hover:bg-slate-300 transition-all flex items-center gap-2 mx-auto"
                    >
                        <Icon path="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0h18" className="w-5 h-5"/>
                        {showMonthlyCalendar ? 'Ocultar' : 'Ver'} Calendario Mensual
                    </button>
                </div>
                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showMonthlyCalendar ? 'max-h-screen mt-4' : 'max-h-0'}`}>
                    <MonthlyCalendar 
                        currentDate={currentDate} 
                        setCurrentDate={setCurrentDate} 
                        onDateSelect={handleDateSelect}
                        weekDays={weekDays}
                        plannerEntries={plannerEntries}
                        pulsingDate={pulsingDate}
                        getDayStatus={getDayStatus}
                        selectedDayDate={selectedDayDate}
                    />
                </div>
            </div>
        );
    };

    const renderMainContent = () => {
        switch (activePanel) {
            case 'planner':
                return renderPlannerContent();
            case 'products':
                return (
                    <ProductsPanel 
                          products={products}
                          purchaseLines={stockPurchaseLines}
                          stockOutboundOrders={stockOutboundOrders}
                          searchTerm={searchTerm}
                          setSearchTerm={setSearchTerm}
                          onGoToPurchases={() => setActivePanel('purchases')}
                        handleStartControl={handleStartControl}
                        handleStartCategoryControl={handleStartCategoryControl}
                        handleOpenProductCycle={handleOpenProductCycle}
                        handleEditProductClick={handleEditProductClick}
                        handleDeleteProductClick={handleDeleteProductClick}
                     />
                );
            case 'stats':
                return (
                    <StatisticsView
                        tasks={plannerEntries}
                        allHistory={allHistory}
                        onStatClick={handleStatClick}
                        onBarClick={handleBarClick}
                        weekDays={weekDays}
                        stockBalance={stockBalanceStats}
                        initialSection="stats"
                    />
                );
            case 'balance':
                return (
                    <StatisticsView
                        tasks={plannerEntries}
                        allHistory={allHistory}
                        onStatClick={handleStatClick}
                        onBarClick={handleBarClick}
                        weekDays={weekDays}
                        stockBalance={stockBalanceStats}
                        initialSection="balance"
                    />
                );
            case 'assistant':
                return (
                    <VirtualAssistant
                        showMessage={showMessage}
                        tasks={scheduledTasks}
                        allHistory={allHistory}
                        products={products}
                        purchases={stockPurchases}
                        outboundOrders={stockOutboundOrders}
                        movements={stockMovements}
                        onStartTask={(productId) => handleStartControl(productId)}
                        onGoToPanel={(panelName) => setActivePanel(panelName)}
                    />
                );
            case 'controllers':
                return (
                     <StockControllersPanel
                        controllers={stockControllers}
                        form={newControllerForm}
                        setForm={setNewControllerForm}
                        editingControllerId={editingControllerId}
                        companyId={resolvedCompanyId}
                        uploadPhoto={uploadCompressedProfilePhoto}
                        showMessage={showMessage}
                        onCreate={handleCreateController}
                        onEdit={handleEditController}
                        onCancelEdit={handleCancelControllerEdition}
                        onToggle={handleToggleController}
                     />
                );
            case 'purchases':
                return (
                    <StockPurchasesPanel
                        purchases={stockPurchases}
                        purchaseLines={stockPurchaseLines}
                        products={products}
                        suppliers={stockContacts}
                        form={newPurchaseForm}
                        setForm={setNewPurchaseForm}
                        editingPurchaseId={editingPurchaseId}
                        onSupplierNameChange={handleSupplierNameChange}
                        onSupplierAddressPatch={handleSupplierAddressPatch}
                        onGeocodeSupplier={handleGeocodePurchaseSupplier}
                        onCreate={handleCreatePurchase}
                        onEditPurchase={handleEditPurchase}
                        onDeletePurchase={handleCancelPendingPurchase}
                        onCancelEdit={handleCancelPurchaseEdition}
                        onQuickCreateProduct={handleCreateProductFromPurchase}
                    />
                );
            case 'outbound-orders':
                return (
                    <StockOutboundOrdersPanel
                        orders={stockOutboundOrders}
                        products={products}
                        purchaseLines={stockPurchaseLines}
                        contacts={sharedContacts}
                        form={newOutboundOrderForm}
                        setForm={setNewOutboundOrderForm}
                    editingOrderId={editingOutboundOrderId}
                    onCancelEdit={handleCancelOutboundOrderEdition}
                    onEditOrder={handleEditOutboundOrder}
                    onDeleteOrder={handleDeleteOutboundOrder}
                    onApproveWarrantyPickup={handleApproveWarrantyPickup}
                    onDestinationSelect={handleOutboundDestinationSelect}
                    onDestinationAddressPatch={handleOutboundDestinationAddressPatch}
                    onGeocodeDestination={handleGeocodeOutboundDestination}
                        onCreate={handleCreateOutboundOrder}
                    />
                );
            case 'movements':
                return (
                    <StockMovementsPanel
                        movements={stockMovements}
                        products={products}
                    />
                );
            case 'sheets':
                if (activeSheetPreview && sheetPreviewMode === 'panel') {
                    const previewMeta = {
                        id: activeSheetPreview.id,
                        title: activeSheetPreview.title || 'Planilla',
                        locationLabel: activeSheetPreview.sourceType === 'category_control'
                            ? `Categoría ${activeSheetPreview.sourceCategorySnapshot || ''}`.trim()
                            : 'Control físico',
                        dateLabel: activeSheetPreview.sheetDate || 'Sin fecha',
                        totalLines: activeSheetPreviewLines.length,
                        pendingLines: activeSheetPreviewLines.filter((line) => line.status === 'pending').length,
                        completedLines: activeSheetPreviewLines.filter((line) => ['checked_ok', 'checked_diff'].includes(line.status)).length,
                        diffLines: activeSheetPreviewLines.filter((line) => line.status === 'checked_diff').length,
                    };
                    return (
                        <StockOperatorSheetView
                            sheet={{
                                ...previewMeta,
                                generalObservation: sheetPreviewMeta.generalObservation,
                                generalTaggedLineIds: sheetPreviewMeta.generalTaggedLineIds,
                            }}
                            lines={filteredSheetPreviewLines}
                            searchTerm={sheetPreviewSearch}
                            setSearchTerm={setSheetPreviewSearch}
                            lineFilter={sheetPreviewFilter}
                            setLineFilter={setSheetPreviewFilter}
                            onBack={handleCloseSheetPreview}
                            onStartTask={handleStartControl}
                            onShowHistory={handleShowHistory}
                            onInlineChange={handlePreviewLineChange}
                            onOpenAddProduct={() => showMessage('Agregar producto durante control queda para la siguiente pasada.', 'info')}
                            onSaveSheet={handleSaveSheetPreview}
                            onFinalizeSheet={handleFinalizeSheetPreview}
                            saving={sheetPreviewSaving}
                            onCloseSheet={handleCloseSheetPreview}
                            onSheetMetaChange={handleSheetPreviewMetaChange}
                            readOnly={['completed', 'reviewed'].includes(String(activeSheetPreview.status || '').trim())}
                            Icon={Icon}
                        />
                    );
                }
                return (
                    <StockSheetsPanel
                        sheets={stockSheets}
                        controllers={stockControllers}
                        products={products}
                        form={newSheetForm}
                        setForm={setNewSheetForm}
                        onCreate={handleCreateSheet}
                        onAssign={handleAssignSheet}
                        onRelease={handleReleaseSheet}
                        onAdvanceStatus={handleAdvanceSheetStatus}
                        onStartControl={handleStartControl}
                        onOpenSheet={handleOpenSheetPreview}
                    />
                );
            case 'executive-summary':
                return (
                    <ExecutiveSummaryPanel
                        stats={executiveStats}
                        onShowStats={() => togglePanel('stats')}
                        onShowAssistant={() => togglePanel('assistant')}
                    />
                );
            default:
                return renderPlannerContent();
        }
    };

    const stopTutorialSpeech = useCallback(() => {
        tutorialSpeechTokenRef.current += 1;
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        tutorialUtteranceRef.current = null;
    }, []);

    const speakTutorialStep = useCallback((step) => {
        const text = String(step?.narration || step?.content || '').trim();
        if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;

        tutorialSpeechTokenRef.current += 1;
        const token = tutorialSpeechTokenRef.current;
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'es-AR';
        utterance.rate = 0.97;
        utterance.pitch = 1.04;
        utterance.volume = 1;

        const voices = window.speechSynthesis.getVoices?.() || [];
        const preferredVoice =
            voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang) && /female|helena|laura|sofia|paulina|monica|maria|sabina/i.test(voice.name))
            || voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang))
            || null;

        if (preferredVoice) utterance.voice = preferredVoice;
        tutorialUtteranceRef.current = utterance;

        window.setTimeout(() => {
            if (tutorialSpeechTokenRef.current !== token) return;
            window.speechSynthesis.speak(utterance);
        }, 120);
    }, []);

    const finishTutorialRun = useCallback(() => {
        stopTutorialSpeech();
        setTutorialRun(false);
        setTutorialStepIndex(0);
    }, [stopTutorialSpeech]);

    const startTutorial = useCallback(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem(STOCK_TUTORIAL_STORAGE_KEY, 'true');
        }
        setTutorialSeen(true);
        setTutorialRun(true);
        setTutorialStepIndex(0);
        speakTutorialStep(STOCK_TUTORIAL_STEPS[0]);
    }, [speakTutorialStep]);

    const handleTutorialCallback = useCallback((data) => {
        const { action, index, status, type } = data;
        const isLastStep = index >= STOCK_TUTORIAL_STEPS.length - 1;

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
            finishTutorialRun();
            return;
        }

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            if (action !== ACTIONS.PREV && isLastStep) {
                finishTutorialRun();
                return;
            }

            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            if (nextIndex >= 0 && nextIndex < STOCK_TUTORIAL_STEPS.length) {
                setTutorialStepIndex(nextIndex);
                speakTutorialStep(STOCK_TUTORIAL_STEPS[nextIndex]);
            }
        }
    }, [finishTutorialRun, speakTutorialStep]);

    return (
        <>
        <Joyride
            run={tutorialRun}
            stepIndex={tutorialStepIndex}
            steps={STOCK_TUTORIAL_STEPS}
            callback={handleTutorialCallback}
            continuous
            showProgress
            showSkipButton
            scrollToFirstStep
            disableScrolling={false}
            disableOverlayClose
            spotlightClicks={false}
            locale={{ back: 'Atrás', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', skip: 'Omitir' }}
            styles={{
                options: {
                    arrowColor: '#020617',
                    backgroundColor: '#020617',
                    overlayColor: 'rgba(2,6,23,0.34)',
                    primaryColor: '#22d3ee',
                    textColor: '#e2e8f0',
                    width: 420,
                    zIndex: 2100,
                },
            }}
        />
        <div ref={stockScrollContainerRef} className="flex h-full min-h-0 flex-col overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_transparent_40%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)]">
             <div id="stock-module-header" ref={stockHeaderRef} className={`border-b border-slate-200/80 bg-white/95 backdrop-blur ${isMovementsFocus ? 'sticky top-0 z-30' : ''}`}>
                <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3 px-4 py-4 md:px-6">
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                onClick={onBackToMenu}
                                className="inline-flex self-start items-center gap-2 rounded-full border border-slate-900 bg-slate-950 px-3.5 py-2 text-[13px] font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-900 hover:shadow-[0_16px_30px_rgba(15,23,42,0.22)]"
                            >
                                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-4 h-4" />
                                <span>Volver a inicio</span>
                            </button>
                            <button
                                type="button"
                                onClick={onStartTutorial || (tutorialRun ? finishTutorialRun : startTutorial)}
                                className={`inline-flex items-center gap-2 rounded-full border border-cyan-300 bg-cyan-50 px-3.5 py-2 text-[13px] font-semibold text-cyan-800 shadow-[0_12px_24px_rgba(34,211,238,0.12)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-cyan-100 ${effectiveTutorialSeen ? '' : 'animate-pulse shadow-[0_0_0_6px_rgba(34,211,238,0.14),0_0_24px_rgba(34,211,238,0.16)]'} ${effectiveTutorialRunning ? 'ring-2 ring-cyan-200' : ''}`}
                            >
                                <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="w-4 h-4" />
                                <span>{effectiveTutorialRunning ? 'Tutorial activo' : 'Tutorial guiado'}</span>
                            </button>
                        </div>
                        {controlStreak > 0 && (
                            <div className="inline-flex items-center gap-2 self-start rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700 lg:self-auto">
                                <span>🔥</span>
                                <span>{controlStreak} días de racha</span>
                            </div>
                        )}
                    </div>
                    <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.75fr)]">
                        <div className="rounded-[24px] border border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-900 px-4 py-3 text-white shadow-[0_18px_34px_rgba(15,23,42,0.16)]">
                            <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">Inventario operativo</p>
                                    <h1 className="mt-1 text-xl font-bold leading-none md:text-[1.7rem]">Control de Stock</h1>
                                </div>
                                <p className="max-w-md text-[11px] font-medium text-slate-200/90 md:text-xs">
                                    Productos, compras y control físico en una sola vista.
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                            <div className="rounded-[16px] border border-slate-200 bg-white/90 px-3 py-2 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Prod.</p><p className="mt-1 text-lg font-bold leading-none text-slate-900">{stockHeroStats.products}</p></div>
                            <div className="rounded-[16px] border border-slate-200 bg-white/90 px-3 py-2 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Pend.</p><p className="mt-1 text-lg font-bold leading-none text-slate-900">{stockHeroStats.pendingTasks}</p></div>
                            <div className="rounded-[16px] border border-slate-200 bg-white/90 px-3 py-2 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Ctrl.</p><p className="mt-1 text-lg font-bold leading-none text-slate-900">{stockHeroStats.controllers}</p></div>
                            <div className="rounded-[16px] border border-slate-200 bg-white/90 px-3 py-2 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Compras</p><p className="mt-1 text-lg font-bold leading-none text-slate-900">{stockHeroStats.pendingPurchases}</p></div>
                            <div className="rounded-[16px] border border-slate-200 bg-white/90 px-3 py-2 shadow-sm"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Salidas</p><p className="mt-1 text-lg font-bold leading-none text-slate-900">{stockHeroStats.pendingOutboundOrders}</p></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mx-auto flex w-full max-w-[1600px] flex-grow flex-col gap-6 p-2 md:p-6 lg:flex-row lg:items-start">
                <div
                    ref={stockSidebarSlotRef}
                    className={`flex w-full flex-col ${stockNavDesktopWidthClass} transition-all duration-300 ${isMovementsFocus ? 'lg:self-start' : ''}`}
                    style={stockSidebarPinnedLayout.active ? { height: `${stockSidebarPinnedLayout.height}px` } : undefined}
                >
                    <div
                        id="stock-nav-panel"
                        className="flex w-full flex-col rounded-[30px] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(248,250,252,0.98)_42%,rgba(241,245,249,1)_100%)] p-4 shadow-[0_20px_50px_rgba(15,23,42,0.08)] md:p-5"
                        style={stockSidebarPinnedLayout.active ? {
                            position: 'fixed',
                            top: `${stockSidebarPinnedLayout.top}px`,
                            left: `${stockSidebarPinnedLayout.left}px`,
                            width: `${stockSidebarPinnedLayout.width}px`,
                            maxHeight: `calc(100vh - ${stockSidebarPinnedLayout.top + 18}px)`,
                            overflowY: 'auto',
                            zIndex: 34,
                        } : undefined}
                    >
                    {isStockNavCollapsed ? (
                        <button
                            type="button"
                            onClick={() => setStockNavExpandedInMovements(true)}
                            className="mb-3 hidden items-center justify-center gap-2 rounded-[22px] border border-slate-200 bg-slate-950 px-3 py-3 text-white shadow-sm transition hover:bg-slate-900 lg:flex"
                        >
                            <Icon path="M3.75 5.25h16.5M3.75 9.75h16.5M3.75 14.25h16.5m-16.5 4.5h10.5" className="w-5 h-5" />
                            <span className="text-[11px] font-black uppercase tracking-[0.18em]">Menú</span>
                        </button>
                    ) : null}

                    {!isStockNavCollapsed ? (
                    <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#ecfeff_100%)] px-4 py-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                            <div className={stockNavDesktopLabelClass}>
                                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-700">Navegación V2</p>
                                <h2 className="mt-2 text-xl font-black text-slate-950">Control de Stock</h2>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Compras, salidas, movimientos y control físico ordenados por frente.
                                </p>
                            </div>
                            <div className={`rounded-2xl border border-white/80 bg-white/90 px-3 py-2 text-right shadow-sm ${isStockNavCollapsed ? 'lg:hidden' : ''}`}>
                                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Activo</p>
                                <p className="mt-1 text-sm font-black text-slate-900">
                                    {activePanel === 'planner' ? 'Programación'
                                        : activePanel === 'products' ? 'Productos'
                                        : activePanel === 'purchases' ? 'Compras'
                                        : activePanel === 'outbound-orders' ? 'Pedidos'
                                        : activePanel === 'movements' ? 'Movimientos'
                                        : activePanel === 'controllers' ? 'Controladores'
                                        : activePanel === 'sheets' ? 'Controles'
                                        : activePanel === 'stats' ? 'Estadísticas'
                                        : activePanel === 'balance' ? 'Balance'
                                        : activePanel === 'executive-summary' ? 'Informe'
                                        : activePanel === 'assistant' ? 'GIA Stock'
                                        : 'Módulo'}
                                </p>
                            </div>
                        </div>
                    </div>
                    ) : null}

                    <div className={`mt-4 ${stockNavDesktopLabelClass}`}>
                        <p className="px-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Operación</p>
                    </div>
                    <div className="mt-2 border-b border-stone-200/80 pb-1">
                        <button onClick={() => setActivePanel('planner')} className={`${getStockNavButtonClass(activePanel === 'planner', 'planner')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'planner', 'text-sky-700', 'planner')}>
                                    <Icon path="M8.25 6.75h7.5M8.25 12h7.5m-7.5 5.25h4.5M6 3.75h12A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6A2.25 2.25 0 016 3.75z" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Programación</h2>
                                    <p className="text-xs text-stone-500">Agenda para ver, reprogramar y seguir controles físicos</p>
                                </div>
                            </div>
                            <Icon path="M4.5 12h15" className={`w-5 h-5 ${activePanel === 'planner' ? 'text-sky-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => setActivePanel('products')} className={`${getStockNavButtonClass(activePanel === 'products', 'products')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'products', 'text-emerald-700', 'products')}>
                                    <Icon path="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Prod.</h2>
                                    <p className="text-xs text-stone-500">Lista general con estado, proveedor, costo e historial</p>
                                </div>
                            </div>
                            <Icon path="M4.5 12h15" className={`w-5 h-5 ${activePanel === 'products' ? 'text-emerald-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('purchases')} className={`${getStockNavButtonClass(activePanel === 'purchases', 'purchases')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'purchases', 'text-amber-700', 'purchases')}>
                                    <Icon path="M3 7.5h18M6.75 3v3m10.5-3v3M6 12h12m-12 4.5h7.5m4.5 0H18" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Compras / pendientes</h2>
                                    <p className="text-xs text-stone-500">Acá cargás lo que entra antes de pasarlo a Logística</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'purchases' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'purchases' ? 'text-amber-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('outbound-orders')} className={`${getStockNavButtonClass(activePanel === 'outbound-orders', 'outbound')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'outbound-orders', 'text-fuchsia-700', 'outbound')}>
                                    <Icon path="M3.75 6.75h16.5m-16.5 5.25h16.5m-16.5 5.25h9.75m1.5-12.75l4.5 4.5-4.5 4.5" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Pedidos de salida</h2>
                                    <p className="text-xs text-stone-500">Lo que ya está preparado para entrega antes de pasar a logística</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'outbound-orders' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'outbound-orders' ? 'text-fuchsia-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('movements')} className={`${getStockNavButtonClass(activePanel === 'movements', 'movements')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'movements', 'text-cyan-800', 'movements')}>
                                    <Icon path="M3.75 5.25h16.5M3.75 9.75h16.5M3.75 14.25h16.5m-16.5 4.5h10.5" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Movimientos</h2>
                                    <p className="text-xs text-stone-500">Ingresos, salidas y lectura general de todo el recorrido</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'movements' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'movements' ? 'text-cyan-800' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className={`mt-4 ${stockNavDesktopLabelClass}`}>
                        <p className="px-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Gestión y lectura</p>
                    </div>

                    <div className="mt-2 border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('controllers')} className={`${getStockNavButtonClass(activePanel === 'controllers', 'controllers')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'controllers', 'text-teal-700', 'controllers')}>
                                    <Icon path="M18 18.75a7.5 7.5 0 00-12 0m12 0a7.5 7.5 0 10-12 0m12 0H6" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Ctrl.</h2>
                                    <p className="text-xs text-stone-500">Fichas, accesos y responsables</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'controllers' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'controllers' ? 'text-teal-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('sheets')} className={`${getStockNavButtonClass(activePanel === 'sheets', 'sheets')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'sheets', 'text-violet-700', 'sheets')}>
                                    <Icon path="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A3.375 3.375 0 0011.25 11.625v8.25m0 0H5.625A2.625 2.625 0 013 17.25V6.75a2.625 2.625 0 012.625-2.625h7.5A2.625 2.625 0 0115.75 6.75v3m3.75 4.5l-3.75 3.75-1.5-1.5" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Controles físicos</h2>
                                    <p className="text-xs text-stone-500">Planillas vivas para cotejar sistema vs físico</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'sheets' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'sheets' ? 'text-violet-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('executive-summary')} className={`${getStockNavButtonClass(activePanel === 'executive-summary', 'reports')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'executive-summary', 'text-orange-700', 'reports')}>
                                    <Icon path="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M12 3V2.25A2.25 2.25 0 009.75 0h-3.5A2.25 2.25 0 004 2.25V3" className="w-6 h-6"/>
                                </div>
                                <h2 className={`text-lg font-bold text-stone-800 ${stockNavDesktopLabelClass}`}>Informe Ejecutivo</h2>
                            </div>
                            <Icon path={activePanel === 'executive-summary' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'executive-summary' ? 'text-orange-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('stats')} className={`${getStockNavButtonClass(activePanel === 'stats', 'stats')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'stats', 'text-lime-700', 'stats')}>
                                    <Icon path="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Estadísticas</h2>
                                    <p className="text-xs text-stone-500">Cumplimiento, diferencias y lectura del control</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'stats' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'stats' ? 'text-lime-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                    <div className="border-b border-stone-200/80 py-1">
                        <button onClick={() => togglePanel('balance')} className={`${getStockNavButtonClass(activePanel === 'balance', 'reports')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'balance', 'text-emerald-700', 'reports')}>
                                    <Icon path="M12 6v12m6-6H6m14.25 8.25H3.75a.75.75 0 01-.75-.75V4.5a.75.75 0 01.75-.75h16.5a.75.75 0 01.75.75v15a.75.75 0 01-.75.75z" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">Balance</h2>
                                    <p className="text-xs text-stone-500">Stock, compras, ventas, ganancia y alertas</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'balance' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'balance' ? 'text-emerald-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>

                     <div className="border-b border-stone-200/80 py-1">
                        <button id="stock-nav-assistant" onClick={() => togglePanel('assistant')} className={`${getStockNavButtonClass(activePanel === 'assistant', 'assistant')} ${stockNavDesktopButtonClass}`}>
                            <div className={`flex items-center gap-3 ${stockNavDesktopRowClass}`}>
                                <div className={getStockNavIconWrapClass(activePanel === 'assistant', 'text-indigo-700', 'assistant')}>
                                    <Icon path="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V8.25a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 8.25v7.5a2.25 2.25 0 002.25 2.25z" className="w-6 h-6"/>
                                </div>
                                <div className={`text-left ${stockNavDesktopLabelClass}`}>
                                    <h2 className="text-lg font-bold text-stone-800">GIA Stock</h2>
                                    <p className="text-xs text-stone-500">Lectura guiada del módulo y recorrido sugerido</p>
                                </div>
                            </div>
                            <Icon path={activePanel === 'assistant' ? "M19.5 8.25l-7.5 7.5-7.5-7.5" : "M4.5 15.75l7.5-7.5 7.5 7.5"} className={`w-5 h-5 ${activePanel === 'assistant' ? 'text-indigo-700' : 'text-stone-500'} ${stockNavDesktopArrowClass}`} />
                        </button>
                    </div>
                    </div>
                </div>
                
                <div id="stock-main-panel" className={`flex-grow ${isMovementsFocus ? 'min-w-0' : ''}`}>
                    {activePanel === 'movements' ? (
                        <div className="min-w-0">
                            <StockMovementsPanel
                                movements={stockMovements}
                                products={products}
                                focusMode
                                scrollHostRef={stockScrollContainerRef}
                                stickyTop={movementsStickyTop}
                                onCondensedChange={setMovementsHeaderCondensed}
                            />
                        </div>
                    ) : (
                        renderMainContent()
                    )}
                </div>
            </div>
            
            {/* --- MODALES --- */}
            {showAiSummaryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b">
                            <h3 className="text-xl font-bold text-stone-900">Resumen operativo: {aiSummaryContent.title}</h3>
                            <button onClick={() => setShowAiSummaryModal(false)} className="p-2 rounded-full hover:bg-stone-200"><Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/></button>
                        </div>
                        <div className="text-stone-700 whitespace-pre-wrap max-h-[60vh] overflow-y-auto">{aiSummaryContent.content}</div>
                    </div>
                </div>
            )}
            {showCategoryControlModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-2xl rounded-[28px] bg-white shadow-2xl">
                        <div className="border-b border-slate-200 px-6 py-5">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600">Control por categoría</p>
                            <h3 className="mt-1 text-2xl font-bold text-slate-900">{categoryControlDraft.categoryLabel || 'Nueva planilla'}</h3>
                            <p className="mt-1 text-sm text-slate-600">
                                Desde acá definimos cómo nace el programa. Después queda visible en <b>Programación</b> y pendiente en <b>Control físico</b>.
                            </p>
                        </div>
                        <div className="grid gap-6 px-6 py-5 md:grid-cols-[1.2fr_0.8fr]">
                            <div className="space-y-4">
                                <div className="rounded-[22px] border border-violet-200 bg-violet-50/70 px-4 py-4">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-700">Qué entra en esta planilla</p>
                                    <p className="mt-1 text-sm text-slate-700">
                                        {Array.isArray(categoryControlDraft.products) ? categoryControlDraft.products.length : 0} producto{Array.isArray(categoryControlDraft.products) && categoryControlDraft.products.length === 1 ? '' : 's'} con stock listo para controlar.
                                    </p>
                                    <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
                                        {(categoryControlDraft.products || []).map((product) => (
                                            <div key={product.id} className="flex items-start justify-between gap-3 rounded-2xl border border-white/80 bg-white/80 px-3 py-2 shadow-sm">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-900">{product.name || 'Producto'}</p>
                                                    <p className="text-xs text-slate-500">
                                                        {[product.category, product.model, product.brand, product.featureLabel].filter(Boolean).join(' · ') || 'Sin variante detallada'}
                                                    </p>
                                                </div>
                                                <span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-bold text-violet-700">
                                                    {Number(product.availableForControl || 0)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-700">Frecuencia</label>
                                    <select
                                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-violet-400 focus:bg-white"
                                        value={categoryControlDraft.frequency}
                                        onChange={(e) => setCategoryControlDraft((prev) => ({ ...prev, frequency: e.target.value }))}
                                    >
                                        {CATEGORY_CONTROL_FREQUENCY_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-700">
                                        {categoryControlDraft.frequency === 'one_time' ? 'Fecha del control' : 'Primera fecha'}
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-violet-400 focus:bg-white"
                                        value={categoryControlDraft.firstDate}
                                        onChange={(e) => setCategoryControlDraft((prev) => ({ ...prev, firstDate: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-slate-700">Observación interna</label>
                                    <textarea
                                        className="min-h-[118px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-violet-400 focus:bg-white"
                                        placeholder="Opcional: criterio, depósito, alcance del control..."
                                        value={categoryControlDraft.notes}
                                        onChange={(e) => setCategoryControlDraft((prev) => ({ ...prev, notes: e.target.value }))}
                                    />
                                </div>
                                <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                    <p><b>Nace en Productos</b>: definimos qué categoría controlar.</p>
                                    <p className="mt-1"><b>Vive en Control físico</b>: la planilla queda pendiente y viva hasta cerrarse.</p>
                                    <p className="mt-1"><b>Programación</b> la toma como agenda y después permite ajustar fechas.</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCategoryControlModal(false);
                                    setCategoryControlDraft(buildEmptyCategoryControlDraft());
                                }}
                                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmCategoryControl}
                                className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                                Crear programa de control
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showProgramEditModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-xl rounded-[28px] bg-white shadow-2xl">
                        <div className="border-b border-slate-200 px-6 py-5">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600">Programación</p>
                            <h3 className="mt-1 text-2xl font-bold text-slate-900">{editingProgramDraft.title || 'Editar programa'}</h3>
                            <p className="mt-1 text-sm text-slate-600">
                                Desde acá cambiamos el calendario del control. No abre ni ejecuta la planilla.
                            </p>
                        </div>
                        <div className="grid gap-4 px-6 py-5 md:grid-cols-2">
                            <div className="md:col-span-2 rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                                <p><b>Categoría:</b> {editingProgramDraft.categoryLabel || 'Sin categoría visible'}</p>
                                <p className="mt-1"><b>Controlador asignado:</b> {stockSheets.find((sheet) => sheet.id === editingProgramDraft.sheetId)?.assignedControllerDisplayNameSnapshot || 'Todavía sin asignar'}</p>
                                <p className="mt-1 text-xs text-slate-500">La asignación concreta se cambia desde Control físico.</p>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Frecuencia</label>
                                <select
                                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                                    value={editingProgramDraft.frequency}
                                    onChange={(e) => setEditingProgramDraft((prev) => ({ ...prev, frequency: e.target.value }))}
                                >
                                    {CATEGORY_CONTROL_FREQUENCY_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Fecha programada</label>
                                <input
                                    type="date"
                                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                                    value={editingProgramDraft.firstDate}
                                    onChange={(e) => setEditingProgramDraft((prev) => ({ ...prev, firstDate: e.target.value }))}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Estado del programa</label>
                                <select
                                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                                    value={editingProgramDraft.programStatus}
                                    onChange={(e) => setEditingProgramDraft((prev) => ({ ...prev, programStatus: e.target.value }))}
                                >
                                    <option value="scheduled">Activo</option>
                                    <option value="paused">Pausado</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-sm font-semibold text-slate-700">Observación interna</label>
                                <textarea
                                    className="min-h-[110px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                                    value={editingProgramDraft.notes}
                                    onChange={(e) => setEditingProgramDraft((prev) => ({ ...prev, notes: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
                            <button
                                type="button"
                                onClick={() => setShowProgramEditModal(false)}
                                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveProgramEdit}
                                className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                                Guardar programación
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showCategoryHistoryModal && (
                <div className="fixed inset-0 z-[72] flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-3xl rounded-[28px] bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600">Historial global por categoría</p>
                                <h3 className="mt-1 text-2xl font-bold text-slate-900">{activeCategoryHistory.categoryLabel || 'Categoría'}</h3>
                            </div>
                            <button onClick={() => setShowCategoryHistoryModal(false)} className="p-2 rounded-full hover:bg-slate-100">
                                <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                            {activeCategoryHistory.entries.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                    Todavía no hay controles cerrados para esta categoría.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {activeCategoryHistory.entries.map((sheet) => (
                                        <div key={sheet.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                                            {(() => {
                                                const adjustmentCount = getSheetAdjustmentsForHistory(sheet.id).length;
                                                return (
                                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                                <div>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-base font-semibold text-slate-900">{sheet.title}</p>
                                                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                                            {String(sheet.status || 'completed').replaceAll('_', ' ')}
                                                        </span>
                                                        {adjustmentCount > 0 ? (
                                                            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                                                                {adjustmentCount} ajuste{adjustmentCount === 1 ? '' : 's'}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <p className="mt-1 text-sm text-slate-500">
                                                        {sheet.sheetDate || 'Sin fecha'} · {sheet.linesCount || 0} líneas · {sheet.differenceLinesCount || 0} con diferencia
                                                    </p>
                                                    <p className="mt-1 text-xs font-medium text-slate-600">
                                                        {sheet.assignedControllerDisplayNameSnapshot || 'Sin controlador visible'}
                                                    </p>
                                                    {sheet.notes ? <p className="mt-2 text-sm text-slate-600">{sheet.notes}</p> : null}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => handleOpenSheetPreview(sheet, { mode: 'readonly-modal' })}
                                                        className="bg-cyan-600 text-white py-2 px-4 rounded-md text-sm"
                                                    >
                                                        Ver planilla
                                                    </button>
                                                </div>
                                            </div>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {activeSheetPreview && sheetPreviewMode === 'readonly-modal' ? (
                <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/55 p-4">
                    <div className="h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] bg-white shadow-2xl">
                        <StockOperatorSheetView
                            sheet={{
                                id: activeSheetPreview.id,
                                title: activeSheetPreview.title || 'Planilla',
                                locationLabel: activeSheetPreview.sourceType === 'category_control'
                                    ? `Categoría ${activeSheetPreview.sourceCategorySnapshot || ''}`.trim()
                                    : 'Control físico',
                                dateLabel: activeSheetPreview.sheetDate || 'Sin fecha',
                                totalLines: activeSheetPreviewLines.length,
                                pendingLines: activeSheetPreviewLines.filter((line) => line.status === 'pending').length,
                                completedLines: activeSheetPreviewLines.filter((line) => ['checked_ok', 'checked_diff'].includes(line.status)).length,
                                diffLines: activeSheetPreviewLines.filter((line) => line.status === 'checked_diff').length,
                                generalObservation: sheetPreviewMeta.generalObservation,
                                generalTaggedLineIds: sheetPreviewMeta.generalTaggedLineIds,
                            }}
                            adjustments={activeSheetAdjustments}
                            lines={filteredSheetPreviewLines}
                            searchTerm={sheetPreviewSearch}
                            setSearchTerm={setSheetPreviewSearch}
                            lineFilter={sheetPreviewFilter}
                            setLineFilter={setSheetPreviewFilter}
                            onBack={handleCloseSheetPreview}
                            onShowHistory={handleShowHistory}
                            onInlineChange={null}
                            onOpenAddProduct={null}
                            onSaveSheet={() => {}}
                            onFinalizeSheet={() => {}}
                            saving={false}
                            onCloseSheet={handleCloseSheetPreview}
                            onSheetMetaChange={() => {}}
                            onOpenAdjustment={handleOpenSheetAdjustment}
                            readOnly
                            hideAddProduct
                            hideScan
                            backLabel="Volver al historial"
                            Icon={Icon}
                        />
                    </div>
                </div>
            ) : null}
            {showSheetAdjustmentModal ? (
                <div className="fixed inset-0 z-[76] flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-3xl rounded-[28px] bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">Ajuste separado</p>
                                <h3 className="mt-1 text-2xl font-bold text-slate-900">{sheetAdjustmentDraft.categoryLabel || 'Categoría'}</h3>
                                <p className="mt-1 text-sm text-slate-500">{sheetAdjustmentDraft.sheetTitle || 'Planilla histórica cerrada'}</p>
                            </div>
                            <button onClick={() => setShowSheetAdjustmentModal(false)} className="p-2 rounded-full hover:bg-slate-100">
                                <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="max-h-[78vh] overflow-y-auto px-6 py-5">
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                La planilla original queda cerrada. El ajuste vive aparte, con motivo, fecha y responsable, y se refleja en el próximo ciclo.
                            </div>
                            <div className="mt-4 grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Filas a ajustar</p>
                                    <div className="mt-3 space-y-2">
                                        {sheetAdjustmentDraft.lines.map((line) => {
                                            const checked = sheetAdjustmentDraft.selectedLineIds.includes(line.id);
                                            return (
                                                <label key={line.id} className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-3 ${checked ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => handleToggleAdjustmentLine(line.id)}
                                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-semibold text-slate-900">{line.productName}</p>
                                                        <p className="mt-1 text-xs text-slate-500">{line.codeLabel || 'Sin variante visible'}</p>
                                                        <p className="mt-1 text-xs font-semibold text-slate-700">
                                                            Sistema {line.expectedQuantity ?? 0} · Físico {line.countedQuantity ?? 0} · Dif. {Number(line.differenceValue || 0) > 0 ? `+${line.differenceValue}` : line.differenceValue}
                                                        </p>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                        <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Motivo del ajuste</label>
                                        <input
                                            type="text"
                                            value={sheetAdjustmentDraft.reason}
                                            onChange={(event) => setSheetAdjustmentDraft((prev) => ({ ...prev, reason: event.target.value }))}
                                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20"
                                            placeholder="Ej.: mal conteo inicial, mercadería mezclada, sobrante detectado"
                                        />
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                                        <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Desarrollo / contexto</label>
                                        <textarea
                                            rows={5}
                                            value={sheetAdjustmentDraft.note}
                                            onChange={(event) => setSheetAdjustmentDraft((prev) => ({ ...prev, note: event.target.value }))}
                                            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20"
                                            placeholder="Opcional: dejá asentado qué pasó, quién ajusta y cómo se resolvió para el próximo control."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
                            <button onClick={() => setShowSheetAdjustmentModal(false)} className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                                Cancelar
                            </button>
                            <button onClick={handleApplySheetAdjustment} className="rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700">
                                Registrar ajuste
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
            {showStockCountModal && activeControlProduct && <StockCountModal product={activeControlProduct} taskToUpdate={activeCalendarTask} show={showStockCountModal} onClose={handleCloseCountModal} onComplete={handleCountCompleted} showMessage={showMessage} companyId={resolvedCompanyId} />}
            {showHistoryListModal && activeHistoryProduct && <HistoryListModal product={activeHistoryProduct} show={showHistoryListModal} onClose={() => setShowHistoryListModal(false)} showMessage={showMessage} companyId={resolvedCompanyId} />}
            {showDayDetailsModal && selectedDayDate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4"><h3 className="text-2xl font-bold text-stone-900">Agenda del {selectedDayDate.toLocaleDateString('es-AR')}</h3><button onClick={() => setShowDayDetailsModal(false)} className="p-2 rounded-full hover:bg-stone-200"><Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6"/></button></div>
                        <div className="overflow-y-auto">{selectedDayTasks.length === 0 ? (<p className="text-center text-stone-500 py-8">No hay controles programados para este día.</p>) : (<ul className="space-y-3">{selectedDayTasks.map((entry) => { if (entry.entryType === 'sheet' || entry.entryType === 'sheet_program') { const isProgramPaused = entry.programStatus === 'paused'; const isSheetCompleted = entry.entryType === 'sheet' && entry.status === 'completed'; const isProjectedProgram = entry.entryType === 'sheet_program'; const controllerLabel = entry.assignedByName || entry.sheet?.assignedControllerDisplayNameSnapshot || 'Sin controlador asignado'; return (<li key={entry.id} className="p-4 rounded-md shadow-sm border flex items-center justify-between gap-3"><div><p className="text-lg font-semibold text-stone-800">{entry.title}</p><p className="text-sm text-stone-500">{entry.summary}</p><p className="mt-1 text-xs text-stone-500">{isProjectedProgram ? `Recurrencia #${entry.projectedOccurrence || 2}` : `${entry.linesCount || 0} filas · estado ${String(entry.rawStatus || 'waiting_assignment').replaceAll('_', ' ')}`}{isProgramPaused ? ' · programa pausado' : ''}</p><p className="mt-1 text-xs font-medium text-slate-600">Productos a controlar: {entry.sourceCategorySnapshot || 'Categoría sin nombre'} · {controllerLabel}</p></div><div className="flex flex-wrap items-center justify-end gap-2">{isSheetCompleted ? <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">REALIZADO</span> : isProgramPaused ? <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">PAUSADO</span> : isProjectedProgram ? <span className="text-xs font-bold text-cyan-700 bg-cyan-100 px-2 py-1 rounded">RECURRENTE</span> : <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">PROGRAMADO</span>}{isSheetCompleted ? <button onClick={() => { setShowDayDetailsModal(false); handleOpenSheetPreview(entry.sheet, { mode: 'readonly-modal' }); }} className="bg-cyan-600 text-white py-2 px-4 rounded-md text-sm">Ver planilla</button> : !isProjectedProgram ? <button onClick={() => { setShowDayDetailsModal(false); handleOpenSheetPreview(entry.sheet, { mode: 'panel' }); }} className="bg-cyan-600 text-white py-2 px-4 rounded-md text-sm">Abrir en control físico</button> : null}<button onClick={() => { setShowDayDetailsModal(false); handleOpenCategoryHistory(entry.sourceCategorySnapshot || entry.sheet?.sourceCategorySnapshot || ''); }} className="bg-white border border-slate-300 text-slate-700 py-2 px-4 rounded-md text-sm hover:bg-slate-50">Historial</button>{!isSheetCompleted ? <button onClick={() => { setShowDayDetailsModal(false); handleOpenProgramEdit(entry.sheet); }} className="bg-white border border-slate-300 text-slate-700 py-2 px-4 rounded-md text-sm hover:bg-slate-50">{isProjectedProgram ? 'Editar recurrencia' : 'Editar programa'}</button> : null}{!isSheetCompleted ? <button onClick={() => handleToggleProgramStatus(entry.sheet)} className={`py-2 px-4 rounded-md text-sm ${isProgramPaused ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>{isProgramPaused ? 'Reactivar' : 'Pausar'}</button> : null}</div></li>); } const product = products.find(p => p.id === entry.productId); const inControl = product?.controlStatus?.status === 'in_control'; return (<li key={entry.id} className="p-4 rounded-md shadow-sm border flex items-center justify-between gap-3"><div><p className="text-lg font-semibold text-stone-800">{entry.productName}</p><p className="text-sm text-stone-500">{entry.summary}{entry.assignedByName ? ` · ${entry.assignedByName}` : ''}</p><p className="mt-1 text-xs text-stone-500">{entry.status === 'completed' ? 'Ya realizado' : inControl ? 'En control desde ejecución física' : 'Pendiente de ejecución en control físico'}</p></div><div className="flex items-center gap-2">{inControl && entry.status === 'pending' ? <span className="text-xs font-bold text-yellow-800 bg-yellow-300 px-2 py-1 rounded">EN CONTROL</span> : entry.status === 'completed' ? <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">REALIZADO</span> : <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">PROGRAMADO</span>}{entry.status === 'completed' && product && <button onClick={() => handleShowHistory(product)} className="bg-indigo-500 text-white py-2 px-4 rounded-md text-sm">Ver Historial</button>}{entry.entryType === 'task' && <button onClick={() => handleRemoveTask(entry.sourceId)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-100 rounded-full"><Icon path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12.576 0c-.245.035-.488.072-.732.112m13.308 0l-1.88-1.88-1.88-1.88m-1.88 1.88l-1.88 1.88m-1.88-1.88l-1.88-1.88-1.88 1.88m6.62-1.88l-1.88-1.88" className="w-5 h-5"/></button>}</div></li>); })}</ul>)}</div>
                    </div>
                </div>
            )}
            {showEditProductModal && editingProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-bold text-stone-900 mb-4">Editar producto</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Nombre</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.name || ''} onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Producto principal</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.category || ''} onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">SKU</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.sku || ''} onChange={(e) => setEditingProduct({ ...editingProduct, sku: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Marca</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.brand || ''} onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Modelo</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.model || ''} onChange={(e) => setEditingProduct({ ...editingProduct, model: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Característica</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.featureLabel || ''} onChange={(e) => setEditingProduct({ ...editingProduct, featureLabel: e.target.value })} placeholder="24 pulgadas, caja x 100, 5 metros..." />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Pulgadas / subtipo</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.inches || ''} onChange={(e) => setEditingProduct({ ...editingProduct, inches: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Código de barras</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.barcode || ''} onChange={(e) => setEditingProduct({ ...editingProduct, barcode: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Proveedor base</label>
                                <input type="text" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.defaultSupplierName || ''} onChange={(e) => setEditingProduct({ ...editingProduct, defaultSupplierName: e.target.value })} />
                            </div>
                              <div>
                                  <label className="block text-sm font-semibold text-stone-700 mb-1">Costo compra</label>
                                  <input type="number" min="0" step="0.01" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.defaultPurchaseCost || ''} onChange={(e) => setEditingProduct({ ...editingProduct, defaultPurchaseCost: e.target.value })} />
                              </div>
                              <div>
                                  <label className="block text-sm font-semibold text-stone-700 mb-1">Precio base de salida</label>
                                  <input type="number" min="0" step="0.01" className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.defaultSalePrice || ''} onChange={(e) => setEditingProduct({ ...editingProduct, defaultSalePrice: e.target.value })} />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="block text-sm font-semibold text-stone-700 mb-1">Observación</label>
                                  <textarea className="w-full p-2 border border-stone-300 rounded-md min-h-[84px]" value={editingProduct.notes || ''} onChange={(e) => setEditingProduct({ ...editingProduct, notes: e.target.value })} />
                              </div>
                            <div>
                                <label className="block text-sm font-semibold text-stone-700 mb-1">Frecuencia de control</label>
                                <select className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.controlFrequency} onChange={(e) => setEditingProduct({ ...editingProduct, controlFrequency: e.target.value })}>
                                    <option value="manual">Manual</option><option value="weekly">Semanal</option><option value="bi-weekly">Quincenal</option><option value="monthly">Mensual</option>
                                </select>
                            </div>
                            {editingProduct.controlFrequency !== 'manual' ? (
                                <div>
                                    <label className="block text-sm font-semibold text-stone-700 mb-1">Día de control</label>
                                    <select className="w-full p-2 border border-stone-300 rounded-md" value={editingProduct.controlDay} onChange={(e) => setEditingProduct({ ...editingProduct, controlDay: e.target.value })}>
                                        <option value="1">Lunes</option><option value="2">Martes</option><option value="3">Miércoles</option><option value="4">Jueves</option><option value="5">Viernes</option><option value="6">Sábado</option><option value="7">Domingo</option>
                                    </select>
                                </div>
                            ) : <div />}
                        </div>
                        <div className="mt-5 flex justify-end gap-3"><button onClick={() => { setShowEditProductModal(false); setEditingProduct(null); }} className="bg-stone-300 text-stone-800 py-2 px-4 rounded-md hover:bg-stone-400">Cancelar</button><button onClick={handleSaveEditedProduct} className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700">Guardar</button></div>
                    </div>
                </div>
            )}
            {showConfirmDeleteModal && productToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm"><h3 className="text-xl font-bold text-stone-900 mb-4">Confirmar Eliminación</h3><p className="text-stone-700 mb-4">¿Seguro que quieres eliminar "{productToDelete.name}"? Se borrarán también sus tareas.</p><div className="flex justify-end gap-3"><button onClick={() => { setShowConfirmDeleteModal(false); setProductToDelete(null); }} className="bg-stone-300 text-stone-800 py-2 px-4 rounded-md hover:bg-stone-400">Cancelar</button><button onClick={confirmDeleteProduct} className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700">Eliminar</button></div></div>
                </div>
            )}
            {showProductHistoryModal && productForHistory && (
                <ProductHistoryModal 
                    product={productForHistory}
                    show={showProductHistoryModal}
                    onClose={() => setShowProductHistoryModal(false)}
                    showMessage={showMessage}
                    companyId={resolvedCompanyId}
                />
            )}
        </div>
        </>
    );
};

// --- Componente de Calendario Mensual Mejorado ---
const MonthlyCalendar = ({ currentDate, setCurrentDate, onDateSelect, weekDays, plannerEntries, pulsingDate, getDayStatus, selectedDayDate }) => {
    const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const startDate = new Date(startDay);
    startDate.setDate(startDate.getDate() - (startDate.getDay() === 0 ? 6 : startDate.getDay() - 1));

    const weeks = [];
    let day = new Date(startDate);
    while (weeks.length < 6) {
        let week = [];
        for (let i = 0; i < 7; i++) {
            week.push(new Date(day));
            day.setDate(day.getDate() + 1);
        }
        weeks.push(week);
    }

    const formatDate = (date) => date.toISOString().slice(0, 10);
    const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    const isDayInCurrentWeek = (day) => weekDays.some(weekDay => isSameDay(weekDay, day));
    const isSelectedDay = (day) => selectedDayDate ? isSameDay(day, selectedDayDate) : false;
    const hasTasks = (day) => plannerEntries.some((entry) => entry.date === formatDate(day));

    const changeMonth = (offset) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + offset);
        setCurrentDate(newDate);
    };

    return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200 w-full">
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-stone-100"><Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="w-5 h-5"/></button>
                <h4 className="font-bold text-lg text-slate-700">{currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</h4>
                <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-stone-100"><Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" className="w-5 h-5"/></button>
            </div>
            <table className="w-full border-collapse">
                <thead>
                    <tr className="text-center text-sm font-semibold text-stone-500">
                        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => <th key={d} className="p-2">{d}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((week, weekIndex) => (
                        <tr key={weekIndex} className={`transition-colors duration-300 ${week.some(day => isDayInCurrentWeek(day)) ? 'bg-blue-100/50' : ''}`}>
                            {week.map((day, dayIndex) => {
                                const isToday = isSameDay(day, new Date());
                                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                                const dayHasTasks = hasTasks(day);
                                const isPast = !isToday && new Date(day).setHours(23, 59, 59, 999) < new Date();
                                const isPulsing = pulsingDate === formatDate(day);
                                const isSelected = isSelectedDay(day);
                                const dayStatus = getDayStatus(day);

                                let dayClass = `transition-all duration-200 cursor-pointer bg-gradient-to-br relative hover:scale-110 active:scale-95 ${isPulsing ? 'animate-pulse-bright' : ''}`;
                                if (isToday) {
                                    dayClass += ' bg-blue-500 text-white font-bold animate-glow';
                                } else if (isCurrentMonth) {
                                    dayClass += ` ${isDayInCurrentWeek(day) ? 'bg-blue-200' : isPast ? 'from-stone-100 to-stone-200 text-stone-400' : 'from-white to-slate-100'} text-stone-700 hover:bg-blue-200`;
                                } else {
                                    dayClass += ' from-white to-slate-50 text-stone-300';
                                }
                                if (isSelected && !isToday) {
                                    dayClass += ' ring-2 ring-cyan-400 ring-offset-2 shadow-[0_10px_22px_rgba(6,182,212,0.18)] bg-cyan-50';
                                }

                                return (
                                    <td 
                                        key={dayIndex} 
                                        onClick={() => onDateSelect(day, { openDetails: true })} 
                                        className={`p-2 h-12 w-12 text-center border border-black/10 ${dayClass}`}
                                    >
                                        <span className={isToday ? 'relative [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]' : ''}>
                                            {day.getDate()}
                                        </span>
                                        {dayHasTasks && !isToday && isCurrentMonth && (
                                            <span className="absolute bottom-1 right-1">
                                                <Icon path="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0h18" className="w-3 h-3 text-blue-400" />
                                            </span>
                                        )}
                                        {dayStatus === 'perfect' && (
                                            <div className="absolute top-1 right-1 text-green-500">
                                                <Icon path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="w-4 h-4" />
                                            </div>
                                        )}
                                        {dayStatus === 'differences' && (
                                            <div className="absolute top-1 right-1 text-yellow-500">
                                                <Icon path="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" className="w-4 h-4" />
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


export default StockControlApp;
